/*#
# b3d

The root 3D scene container. All other components (`b3dSun`, `b3dSkybox`, `b3dLoader`, etc.)
must be children of a `b3d` element.

## Demo

```js
import {
  b3d, b3dSun, b3dSkybox, b3dSphere, b3dLoader,
  b3dBiped, ualAnimationStates, assetUrl, b3dButton, b3dLight, b3dWater, b3dReflections, b3dCollisions,
  b3dAmbient, gameController, inputFocus, toggle3d, slider3d,
} from 'tosijs-3d'
import { demoLoadout } from 'tosijs-3d/demo-utils'
import { tosi, elements } from 'tosijs'
const { div, span } = elements

const { demo } = tosi({
  demo: {
    showColliders: false,
    time: 19,
    // Drag this UP to flood the scene and walk your biped under. The fog closes in and the
    // bubbles ramp in with the depth — neither of them switches on at the surface.
    waterLevel: -0.2,
  },
})

const scene = '/test-3.glb'
// A Quaternius UAL rig — 1.83 m, feet at the origin, 27 curated clips off the
// CDN (the full library is 120 clips and 20 MB). A person is 1.8 m here, and
// the movement constants are tuned for one.
const person = assetUrl('quaternius/UAL1_core.glb', 2)

// Your loadout: a button on the system bar opens it under the bar, with your
// bag beside it (see inventory-box).
const loadout = demoLoadout()

const formatTime = (v) => {
  const h = Math.floor(v)
  const m = Math.round((v % 1) * 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

preview.append(
  b3d(
    // gamepad: on-screen glass gamepad wired into the input system. XR is on by
    // default — Enter VR drives the same player biped through the unified input
    // spine (XR controllers → bipedMapping), chase-followed by the XR rig.
    {
      glowLayerIntensity: 1,
      gamepad: true,
      scenePanel: () => [
        toggle3d({ label: 'show colliders', value: demo.showColliders }),
        slider3d({ label: 'time of day', value: demo.time, min: 0, max: 24, step: 0.1 }),
        slider3d({ label: 'water level', value: demo.waterLevel, min: -1, max: 4, step: 0.1 }),
      ],
    },
    b3dSun({ shadowTextureSize: 2048, activeDistance: 20 }),
    b3dSkybox({ timeOfDay: demo.time, realtimeScale: 100, latitude: 30, moonIntensity: 1.5 }),
    b3dSphere({ meshName: 'ref-sphere', diameter: 1, y: 1, x: -3, z: -3, color: '#aaaaaa' }),
    b3dLoader({ url: scene }),
    inputFocus(
      gameController(),
      b3dBiped({ url: person, animationStates: ualAnimationStates(), x: 5, ry: 135, player: true, cameraType: 'follow', initialState: 'idle' }),
    ),
    b3dBiped({ url: person, animationStates: ualAnimationStates(), x: -4, z: 3, ry: 45, initialState: 'idle' }),
    b3dBiped({ url: person, animationStates: ualAnimationStates(), x: 3, z: -2, initialState: 'dance' }),
    b3dLight({ y: 1, z: 0.5, intensity: 0.2, diffuse: '#8080ff' }),
    b3dWater({ y: demo.waterLevel, twoSided: true, waterSize: 1024 }),
    // ABOVE the surface: leaves tumbling on the breeze — two-sided quads that flip and blow,
    // not dust. Raise the water level (scene panel) and they fade as you submerge.
    b3dAmbient({ preset: 'leaves', where: 'above', radius: 10, windX: 1.5 }),
    // BELOW it: plankton hanging in the light (motes) and bubbles rising. Both arrive AS the
    // water does — emission ramps with depth rather than switching on at the plane. The motes
    // are tinted dark green so they read as plankton and DON'T blur together with the bright
    // silvery bubbles.
    b3dAmbient({ preset: 'motes', where: 'underwater', radius: 8, color: '#3c5238' }),
    b3dAmbient({ preset: 'bubbles', where: 'underwater', radius: 8 }),
    b3dReflections(),
    b3dCollisions({ debug: demo.showColliders }),
    ...loadout.panels()
  ),
  div(
    { class: 'debug-panel' },
    span({
      class: 'time-display',
      bind: {
        value: demo.time,
        binding: (el, v) => { el.textContent = formatTime(v) },
      },
    })
  )
)

setInterval(() => {
  const skybox = document.querySelector('tosi-b3d-skybox')
  if (skybox) demo.time.value = skybox.timeOfDay
}, 1000)
```
```css
.preview { display: flex; flex-direction: column; height: 100%; }
tosi-b3d { flex: 1; min-height: 0; width: 100%; }
.debug-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 20px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 6px;
  font-size: 14px;
}
.debug-panel label {
  display: flex;
  align-items: center;
  gap: 4px;
}
.time-display {
  font-family: ui-monospace, monospace;
}
```

## Demo — pause, start screens, and the VR entry gesture

```js
import { b3d, b3dBox, b3dSphere, label3d, button3d, select3d, sceneDelta } from 'tosijs-3d'
import { demoStage } from 'tosijs-3d/demo-utils'
import { tosi } from 'tosijs'

const demo = tosi({ pauseDemo: { state: 'paused since load', spin: 'medium' } })
// DEGREES per second — `ry` is degrees (see AbstractMesh). `medium` is one
// revolution every two seconds.
const RATE = { slow: 60, medium: 180, fast: 420 }

// `glow` is self-illumination as a fraction of `color`; `glowLayerIntensity` on
// the scene is what makes it bloom past the edges. Both, or it just looks pale.
const cube = b3dBox({ meshName: 'spinner', size: 1.4, y: 0.9, color: '#a8382c', glow: 0.26 })
const moon = b3dSphere({ meshName: 'moon', diameter: 0.5, y: 1.6, color: '#f2d98a', glow: 0.7, glowColor: '#ffd34d' })

const scene = b3d(
  {
    startPaused: true,
    // pauseWhenHidden is ON by default — switch to another tab and come back.
    frameRate: 60,
    glowLayerIntensity: 0.5,
    // Replace the built-in rows. `resume` is handed in: whatever you build has
    // to be able to let the player back in.
    pausePanel: (host, resume) => [
      label3d({ text: 'PAUSED', bold: true }),
      button3d({ label: 'Continue', handleClick: resume }),
    ],
    // The SAME control, in the settings panel — so you can change the speed while it
    // is RUNNING and watch it change, instead of only while it is frozen. Both
    // panels bind the one `demo.pauseDemo.spin` value, so they never disagree.
    scenePanel: () => [
      select3d({
        label: 'spin',
        value: demo.pauseDemo.spin,
        options: ['slow', 'medium', 'fast'],
      }),
    ],
    update: (host) => {
      // Never runs while paused — that is the point. The cube freezing IS the
      // demo, so leave the readout to the events below.
      const rate = RATE[demo.pauseDemo.spin.valueOf()] ?? RATE.medium
      // `ry`, not `mesh.rotation.y` — AbstractMesh writes a rotationQuaternion
      // from rx/ry/rz every frame, so a euler write is silently overwritten.
      cube.ry += rate * host.frameDelta
    },
    sceneCreated(el) {
      // THE HONEST TEST. The cube stops because `update` isn't called — easy,
      // and it would look identical if pause did nothing but skip that one
      // callback. The moon flies on the RENDER OBSERVABLE off `sceneDelta`,
      // which a pause also has to hold. If pause is real, BOTH freeze.
      let t = 0
      el.scene.registerBeforeRender(() => {
        t += sceneDelta(el.scene)
        moon.x = Math.cos(t) * 3.2
        moon.y = 1.6 + Math.sin(t * 2) * 0.5
        moon.z = Math.sin(t) * 3.2
      })
    },
  },
  ...demoStage({ texture: '/tosi-warhol-testgrid.svg', size: 40, timeOfDay: 11 }),
  cube,
  moon
)

// Both events carry what a game needs to react: `reason` distinguishes "the
// player asked" from "the tab went away".
scene.addEventListener('pause', (e) => {
  demo.pauseDemo.state = `paused (${e.detail.reason})`
})
scene.addEventListener('resume', () => {
  demo.pauseDemo.state = 'running'
})
// Orientation comes from the VIEWPORT, so it works where screen.orientation
// doesn't — rotate a phone, or make the window taller than it is wide.
scene.addEventListener('orientation', (e) => {
  demo.pauseDemo.state = `${e.detail.orientation} ${e.detail.width}x${e.detail.height}`
})

const readout = document.createElement('div')
readout.style.cssText = 'font: 13px ui-monospace, monospace; padding: 6px'
demo.pauseDemo.state.observe(() => {
  readout.textContent = `state: ${demo.pauseDemo.state.valueOf()}   ·   scene.paused: ${scene.paused}`
})
readout.textContent = 'state: paused since load'

const bar = document.createElement('div')
bar.style.cssText = 'display: flex; gap: 8px; padding: 6px'
for (const [label, fn] of [
  ['pause()', () => scene.pause('user')],
  ['resume()', () => scene.resume()],
  ['togglePause()', () => scene.togglePause()],
]) {
  const b = document.createElement('button')
  b.textContent = label
  b.onclick = fn
  bar.append(b)
}

preview.append(scene, readout, bar)
```

> **⚠️ EXPERIMENTAL.** The flat behaviour above is verified; the VR half — the
> Continue tap carrying a user gesture into `enterXRAsync`, and taking the
> headset off pausing — has not been through a headset yet. That's the half the
> feature is *for*, so treat these attributes as unsettled until it has.

A second, deliberately small scene. It starts paused with its own panel, so the
first thing you see is a Start screen; backgrounding the tab pauses it again.

The reason this shape matters is not tidiness. **`enterXRAsync` requires a user
gesture** — a scene cannot enter VR on load, the browser refuses. So "come up
paused, put the headset on, press Continue" is the only arrangement that
reliably enters VR. `enterXrOnResume` closes the loop: set it on the scene above
and the button changes to "Continue in VR" on a device that has it. Leaving VR
pauses every scene: entering unpauses, so leaving is its inverse.

## Usage

```javascript
import { b3d, b3dSun, b3dSkybox, b3dLoader, b3dWater } from 'tosijs-3d'

document.body.append(
  b3d(
    { glowLayerIntensity: 1 },
    b3dSun(),
    b3dSkybox({ timeOfDay: 12 }),
    b3dLoader({ url: '/scene.glb' }),
    b3dWater({ y: -0.2 })
  )
)
```

## Attributes

| Attribute | Default | Description |
|-----------|---------|-------------|
| `glowLayerIntensity` | `0` | Glow effect intensity (0 = off) |
| `clearColor` | `''` | The colour behind everything (`'#000'` for space); empty keeps Babylon's default |
| `frameRate` | `30` | Target frame rate |
| `timeScale` | `1` | How fast simulation time runs against wall time: `1` is real time, `0.25` slow motion, `0` stops the simulation while the scene keeps rendering. Everything integrating with `sceneDelta` scales, and so do Babylon's animation groups and particles (times any `animationTimeScale` or `updateSpeed` the app set); anything that must not slow (UI animation, a countdown in wall seconds) reads `realDt` from the frame package |
| `quality` | `'auto'` | Device tier. `'auto'` follows the measured or cached device profile (and probes in the background on a first visit); `'low'`, `'medium'` or `'high'` force one. Drives the `auto` defaults of shadows, reflections, terrain and render scaling. See [[b3d-quality]] |
| `pixelRatio` | `0` | Device pixels per CSS pixel, flat. `0` is auto: the display's own ratio, capped by the device tier (high 2, medium 1.5, low 1). A positive value is an explicit cap and wins: `1` renders at CSS resolution, `2` full Retina, `0.75` below CSS for speed. Ignored in a headset |
| `groundDetail` | `'auto'` | The ground shader's form: `full`, `lite` (three noise samples a pixel for eight, for a GPU that cannot afford the full one), or `auto` (from the device tier: lite on the low tier, and in a headset on the medium one) |
| `ssao` | `'off'` | Ambient occlusion: `'off'`, `'auto'` (follows the device tier, and stays off in a headset) or `'on'`. Off by default because it is a cost a scene should choose. See [[b3d-ssao]] |
| `ssaoMethod` | `'projected'` | `'projected'` computes occlusion once and every material looks it up by world position, so it works flat and in a headset. `'screen'` is Babylon's post-process, flat only |
| `ssaoStrength` | `1` | How dark a fully occluded crease gets |
| `ssaoRadius` | `2` | Metres a surface looks for something occluding it |
| `ssaoRate` | `30` | Projected only: redraws per second (`0` = every frame) |
| `windSpeed` | `0` | The scene's one wind, in m/s, read by clouds, water and ambient effects (see [[wind]]). `0` means no scene wind; a child's own wind attributes win where they are set |
| `windBearingDeg` | `0` | Where the wind is going, north-up and clockwise. `0` blows toward +Z |
| `windGust` | `0` | Gust size as a fraction of `windSpeed`. `0` is dead steady |
| `startPaused` | `false` | Come up paused, showing the pause panel (the "press Start" shape) |
| `pauseWhenHidden` | `'on'` | Pause when the tab or window goes to the background. ⚠️ Experimental: the VR path is unvalidated |
| `enterXrOnResume` | `'off'` | `'on'`: resuming enters immersive VR where the device supports it. The Continue tap is the user gesture `enterXRAsync` requires |
| `reseatFreeze` | `'on'` | Freeze the clock while the re-seat dialog is up. Set `'off'` for a networked world, which cannot stop the other players; the dialog still gates your own input |
| `no-xr` | `false` | Suppress the automatic Enter-VR button (WebXR is offered by default when an immersive-vr session is supported) |
| `xrMultiview` | `'off'` | ⚠️ Experimental, not working: `'on'` asks for both eyes in one pass (WebXR layers). On a Quest 3 it gave a blank view, and water and sky do not handle multiview |
| `xrRenderScale` | `0` | In a headset: the fraction of each eye's width and height drawn. `0` is auto (from the device tier: 0.67 on the low tier). Engine hardware scaling does not reach a headset; this does. Read on entering a session |
| `xrFoveation` | `-1` | In a headset: fixed foveation, 0-1. `-1` is auto (low tier 1, medium 0.5, high 0). Read on entering a session |
| `xrGrid` | `'auto'` | The reference grid on the floor in an immersive session. `'auto'` shows it for the built-in free-fly rig and hides it when a player entity drives the rig or you supply your own `setupXr`; `'on'` and `'off'` force it |
| `xrReticle` | `'off'` | `'on'` shows the head-locked face crosshair (a pin target, e.g. for a tracking weapon) |
| `flatUi` | `'scene'` | Where the flat view's own controls are drawn. `'scene'`: in the scene (the system bar, settings panel and pause), the same UI a headset gets. `'dom'`: HTML corner buttons, settings panel and pause (where `::part(...)` styling of them shows). Any other value warns once and means `'scene'` |
| `scenePanelOpen` | `false` | Start with the settings panel open instead of closed behind the owl |
| `panelTitle` | `''` | The settings panel's name, drawn in its title bar beside the move and close glyphs (so it costs the panel no row) |
| `panelSections` | `'fold'` | How collapsible sections in the settings panel present: `'fold'` (headers you open and close, one open at a time: opening one closes the others), `'folds'` (several may be open) or `'tabs'` (file tabs, one section at a time) |
| `stats` | `false` | Add a Perf Stats section to the settings panel, flat and in a headset. `#perf` or `#debug` (or `?perf`, `?debug`) in the page URL, or calling `showB3dStats()`, shows it on every scene |
| `gamepad` | absent | When present, mount the on-screen glass gamepad wired into the input system. Bare/`true` = the default layout (everything but the d-pad); a value like `"a,b,left_stick"` selects controls, and naming `view` or `menu` also puts them on the headset's system bar. A plain property, read once at setup |
| `gamepadScale` | `1` | Scale factor for the glass gamepad clusters |
| `gamepadFade` | `'on'` | `'off'` keeps the glass gamepad visible instead of fading it once a key is pressed or a hardware pad is used (the pointer never fades it) |
| `minElevation` / `maxElevation` | `5` / `70` | Default orbit-camera elevation limits (degrees above the horizon) |
| `minDistance` / `maxDistance` | `2` / `50` | Default orbit-camera zoom limits |

## Callbacks

Set these in the `b3d()` config.

| Property | Called |
|----------|--------|
| `sceneCreated(el)` | once the engine and scene exist: set up a camera here |
| `update(el)` | every frame the simulation runs (never while paused) |
| `scenePanel(el)` | returns the settings panel's widgets, drawn flat and in a headset from the same list; rebuilt each time it opens, and `refreshScenePanel()` updates an open one |
| `pausePanel(el, resume)` | returns the pause panel's rows in place of the default title and Continue button; `resume` lets the player back in |
| `setupXr(el)` | replaces the built-in WebXR setup and Enter-VR button with your own |

## Loading

The spinner shows until the scene is built: its files loaded, its shaders
ready, its terrain streamed in, and every child that holds it up
(`el.holdLoading('city')` returns the release; `<tosi-b3d-city>` and
`<tosi-b3d-library>` do) finished, for at most 20 seconds. A page that loads its own
content after that (models, a body, a terrain) holds it up with
`el.setLoading('loading the body…')`, which shows the spinner with the
message below it; `setLoading(true)` is the spinner alone, `setLoading(false)`
lets it go, and `{ spin: false }` shows a message without the spinner (a load
that failed).
*/
/*{ "parent": "core", "order": 10 }*/

import { Component, elements, updates } from 'tosijs'
import * as BABYLON from '@babylonjs/core'
import * as GUI from '@babylonjs/gui'
import { GridMaterial } from '@babylonjs/materials'
import '@babylonjs/loaders'
import { touchOrbit } from './touch-orbit.js'
import { xrControllers, type TosiXRControllerMap } from './gamepad.js'
import {
  fitPanel,
  panel3d,
  titled3d,
  button3d,
  iconBar3d,
  label3d,
  textBlock3d,
  foldSections,
  type Widget3d,
} from './widgets3d.js'
import { handlerOf } from './handler-of.js'
import { panelFitWidth } from './widgets3d-layout.js'
import { w3dTheme } from './w3d-theme.js'
import type { Medium } from './medium.js'
import { SvgTexture, svgTextureCost } from './svg-texture.js'
import { debugSink, sendDebug } from './debug-sink.js'
import { frameProfile } from './frame-profile.js'
import { b3dSvgPlane, type B3dSvgPlane } from './b3d-svg-plane.js'
import { createMakers, type Makers } from './make-mesh.js'
import {
  openPopup,
  type PopupSurface,
  type PopupSurfaceOptions,
} from './popup-surface.js'
import {
  cameraIsAttached,
  freshWorldMatrix,
  isNoCollide,
  insideCarrier,
  isOff,
  markUiMesh,
  replaceKeepingLayers,
  refuseOwnAncestor,
  refuseOwnAncestors,
} from './b3d-utils.js'
import { NO_WIND, gustAt, windFromPolar, type Wind } from './wind.js'
import {
  CALM,
  weatherAt as composeWeather,
  type WeatherCell,
  type WeatherSample,
} from './weather.js'
import { faceViewer } from './dialog-placement.js'
import { attachSceneLayer, type PopupOwner } from './panel-layer.js'
import {
  angularHeight,
  bandOrbit,
  clampOrbit,
  orbitCentre,
  orbitFromAim,
  orbitPosition,
  type Orbit,
} from './panel-orbit.js'
import { svgIcons } from './svg-icons.js'
import {
  systemBar,
  BAR_HEIGHT,
  ATTENTION_FILL,
  type SystemBar,
} from './system-bar.js'
import { CombatWorld } from './destroyable.js'
import { b3dGamepad } from './glass-gamepad.js'
import { XrGamepadSource } from './xr-gamepad.js'
import { XrFrames, EntityFrame } from './xr-frames.js'
import {
  attachFramePanel,
  pickPanelLayers,
  pointerClaimedByPanel,
  presentationSpec,
  screenAnchorDragged,
  placeholderPanelSvg,
  setPanelsHidden,
  opacityWeightedGlow,
  type FramePanelSpec,
  type ScreenAnchor,
} from './frame-panel.js'
import { runProbe, hydrateProfileFromCache } from './b3d-probe.js'
import {
  compositeFog,
  approachFog,
  type FogState,
  type FogLayer,
} from './atmosphere.js'
import {
  setQuality,
  qualityBudgets,
  onQualityChange,
  effectiveTier,
  renderScalingLevel,
  type QualitySetting,
} from './b3d-quality.js'
import { SsaoController, ssaoActive, type SsaoSetting } from './b3d-ssao.js'
import { ProjectedAoController } from './b3d-ssao-projected.js'
import { reticleSvg } from './dial-art.js'
import {
  costSweep,
  formatRuns,
  formatSweep,
  recordRun,
  type SweepRow,
  type SweepRun,
} from './cost-sweep.js'
import { nearFirst } from './draw-order.js'
import {
  setBiomeLite,
  biomeLite,
  setBiomeComputedNoise,
  biomeComputedNoise,
} from './biome-plugin.js'
const SWEEP_KEY = 'tosi-b3d-cost-sweep'
import {
  allocateAmbient,
  ratchetPool,
  recoverPool,
  type AmbientEffect,
} from './ambient-budget.js'
import { realNow, wallDate, wallMs } from './clock.js'

const { canvas, div, span, slot, button } = elements

// Site-wide opt-in for the 📊 perf overlay: a host (the doc site) calls
// `showB3dStats()` once so the toggle appears on EVERY scene without a per-scene
// attribute or a URL flag. Library consumers leave it off — their scenes stay
// uncluttered unless they set `stats` or add `#perf`.
let _statsGlobal = false
export const showB3dStats = (on = true): void => {
  _statsGlobal = on
}

// Whether the 📊 toggle should be revealed on a given scene. True when the host
// enabled it site-wide, OR `#perf` / `#debug` (or the `?perf` / `?debug` query
// form) is in the page URL — so it's reachable on a device with no console (the
// whole reason this exists). The HASH form is preferred: it survives the
// doc-browser's client-side navigation (a query string can be dropped when the
// SPA rewrites the URL between docs) and never hits the server. Guarded for
// non-browser contexts (SSR/tests).
const perfDebugEnabled = (): boolean => {
  if (_statsGlobal) return true
  if (typeof window === 'undefined' || !window.location) return false
  const { search, hash } = window.location
  return /(^|[?&])(perf|debug)\b/.test(search) || /\b(perf|debug)\b/.test(hash)
}

/**
 * A contributor to the Perf Stats panel (see `B3d.addDebugSource`). The panel is
 * dual-presence — flat overlay AND in-headset — which is the whole point: there's no
 * console in VR, and VR is where the frame budget is tightest.
 */
export type DebugPanelSource = {
  /** Short header, e.g. `'terrain'`. */
  name: string
  /** Called on every refresh — return LIVE values, not a snapshot. */
  lines: () => string[]
  /** Rendered as buttons. This is how you toggle a profiler on from inside a headset. */
  actions?: Array<{
    label: string | (() => string)
    handleClick?: () => void
  }>
  /** Icon for this source's toggle in the panel's debug icon-bar (an `iconGlyph`
   * name — see [[svg-icons]]). Defaults to `'bug'`. */
  icon?: string
  /**
   * Has this source nothing to report right now?
   *
   * Its icon is DIMMED when true, so the bar says "nothing here" without being
   * pressed. Tonio: "The errors popup should be dimmed unless it has errors to
   * report." A diagnostic you must open to discover is empty costs something
   * every time you check it — and in a headset, where that bar is the only
   * console there is, you check constantly.
   *
   * Absent means "always lit", which is right for a readout that is always
   * saying something: the crowd's figure count, the perf stats.
   */
  quiet?: () => boolean
}

/**
 * The per-frame package handed to update callbacks and published on the scene.
 *
 * One object rather than a growing argument list, so adding a field later is
 * additive for every consumer instead of a signature change.
 */
export interface FrameInfo {
  /** SIM seconds since the last frame — scaled by `timeScale`, 0 when paused.
   * This is the one to integrate motion with. */
  dt: number
  /** WALL-CLOCK seconds since the last frame. Ignores `timeScale` and pause —
   * for UI animation, spinners, anything that must not slow with the world. */
  realDt: number
  /** Sim seconds since the scene started. Does not advance while paused. */
  elapsed: number
  /** Wall-clock seconds since the scene started. Always advances. */
  realElapsed: number
  /** Current `timeScale`. */
  scale: number
  /** Is the world stopped (paused or frozen)? `dt` is 0 when true. */
  paused: boolean
  /** Frames rendered since the scene started. */
  frame: number
}

export type SceneAdditionHandler = (additions: SceneAdditions) => void

export type SceneAdditions = {
  meshes?: BABYLON.AbstractMesh[]
  lights?: BABYLON.Light[]
}

/** Radar alignment — matches the HUD's `TraceKind` so a blip's faction maps
 * straight to a radar-trace colour/template. Extend freely; these are the seed. */
export type RadarFaction = 'friendly' | 'neutral' | 'hostile' | 'waypoint'

/** Anything detectable on radar — a target, the player's own missile, a waypoint.
 * A radar platform (e.g. the aircraft HUD) enumerates `B3d.radarBlips`, gates each
 * by its `radarProfile` against the platform's range, and plots the survivors. */
export interface RadarBlip {
  /** Detectability multiplier: 1 = detectable at the platform's nominal range,
   * 2 = out to 2× range, 0.05 = very stealthy; NEGATIVE = always detectable
   * regardless of range (e.g. waypoints). */
  radarProfile: number
  faction: RadarFaction
  /** Current world position (floating-origin-corrected), or null if not yet placed
   * (mesh still loading, etc.) — the platform skips a null. */
  radarPosition(): { x: number; y: number; z: number } | null
  /** The mesh a homing weapon should chase when this blip is locked, or null (a
   * positional-only blip like a waypoint — a missile fired at it goes ballistic). */
  radarMesh(): BABYLON.AbstractMesh | null
}

// An NPC nameplate: an entity-pinned frame + a gaze-revealed panel on it.
type Nameplate = {
  ef: EntityFrame
  panel: {
    update: (ctx?: { firstPerson?: boolean }) => void
    dispose: () => void
    readonly debug: {
      reveal: number
      cosine: number
      distance: number
      updates: number
      camera: string
    }
  }
}

type B3dCallback =
  | ((element: B3d, BABYLON: typeof import('@babylonjs/core')) => void)
  | ((element: B3d, BABYLON: typeof import('@babylonjs/core')) => Promise<void>)

const noop = () => {}
const noopRefresh = () => {}
// The system bar in a headset: up ahead, where the settings panel hung. Its
// panels (settings, inventory) open under it.
const XR_BAR_ELEVATION = 66
/** Flat, where a panel opened from the system bar starts: under the bar. */
const UNDER_BAR = 8 + BAR_HEIGHT + 8
const XR_BAR_DISTANCE = 1.4

/*
A SYSTEM PANEL'S FIRST SEAT IN A HEADSET, from where it is pinned flat. A
panel pinned to the left edge goes left of centre and one pinned right goes
right (left of centre is negative azimuth, as `left-shoulder` is), its inner
edge a little off the middle; a pixel `top` below the bar puts it that much
lower, at the scale the panel is drawn (K metres per viewBox unit). Unpinned,
it is under the bar, centred. So an app's two columns stay two columns.
*/
function xrSeatFor(
  pin: ScreenAnchor | undefined,
  width: number,
  halfHeightDeg: number,
  K: number
): { azimuthDeg: number; elevationDeg: number; distance: number } {
  const deg = (m: number) => (Math.atan2(m, XR_BAR_DISTANCE) * 180) / Math.PI
  const side =
    pin?.left != null && pin.right == null
      ? -1
      : pin?.right != null && pin.left == null
      ? 1
      : 0
  const down =
    typeof pin?.top === 'number' ? Math.max(0, pin.top - UNDER_BAR) * K : 0
  return {
    azimuthDeg: side * (8 + deg(width / 2)),
    elevationDeg: XR_BAR_ELEVATION - 5 - halfHeightDeg - deg(down),
    distance: XR_BAR_DISTANCE,
  }
}

/** A registered fog contributor: underwater, cloud, space… (see atmosphere.ts). */
type FogContributor = () => FogLayer | null

/** One debug source's live <text> nodes, so the panel can update without rebuilding. */
/** What `panel3d` hangs on its root for one-call popups — see widgets3d. */
type PanelPopup = (
  config: { title?: string; width?: number; handleClose?: () => void },
  ...items: Widget3d[]
) => { close: () => void }

type LiveDebugRow = {
  /** Push the source's current lines into its text block (re-wraps at the last width). */
  update: (lines: string[]) => void
  lines: () => string[]
}

// Read-only local axes reused by the per-frame XR loops (getDirectionToRef
// reads but never mutates them), so we never allocate a Vector3 per frame.
const XR_FORWARD = new BABYLON.Vector3(0, 0, 1)
const XR_RIGHT = new BABYLON.Vector3(1, 0, 0)

/*
WHICHEVER HAND YOU PRESS WITH, THE PRESS COUNTS.

Babylon points with ONE controller at a time. A press on the other one only
switches the pointer to it, and the press itself is dropped. A Quest decides for
itself which controller is active, so in practice a press now and then did
nothing, and a player just learns that the pointer is flaky (Tonio: "I think I
and most users just internalize flaky pointer behavior"). Nobody should have
to know which hand is "active".

So: a press on a controller that is not pointing switches to it AND is
delivered, once its ray has picked something (on the next frame or two).
Babylon's own release pairs with it, because by then that controller is the one
pointing. This reads Babylon's per-controller state (`_controllers`, `pick`,
`pointerDownTriggered`), which is private. `xr-press.test.ts` pins those names
and the drop itself, so a Babylon upgrade that renames them (or fixes the drop)
fails the suite and says which; in a live session it warns once instead.
*/
function pressWithEitherHand(
  xr: BABYLON.WebXRDefaultExperience,
  scene: BABYLON.Scene
): void {
  const sel = xr.pointerSelection as any
  const frames = xr.baseExperience?.sessionManager.onXRFrameObservable
  // Pinned by xr-press.test.ts; this is the same check, live, so a Babylon
  // that changed under us says so instead of quietly dropping presses again.
  if (
    sel?._controllers == null ||
    typeof sel.setAttachedController !== 'function'
  ) {
    console.warn(
      'tosi-b3d: Babylon XR pointer selection has changed (no _controllers / setAttachedController), so a press on the hand that is not pointing is dropped again. See pressWithEitherHand.'
    )
    return
  }
  xr.input.onControllerAddedObservable.add((controller) => {
    const watch = (mc: BABYLON.WebXRAbstractMotionController) => {
      const main = mc.getMainComponent()
      if (main == null || frames == null) return
      main.onButtonStateChangedObservable.add(
        (c) => {
          if (c.changes.pressed == null) return
          const id = controller.uniqueId
          /*
          A PRESS IS AIMED NOW, not a frame ago. Babylon reads the buttons
          before it picks, so a press or release carries the previous frame's
          ray, cut to that frame's hit distance. On a moving vehicle the bar
          has moved on with it since, and the stale ray stops short of it:
          hover (picked fresh) lit the button and the click never landed.
          */
          const ray = sel._controllers?.[id]?.pick?.ray
          if (ray != null) {
            freshWorldMatrix(controller.pointer)
            controller.getWorldPointerRayToRef(ray)
          }
          if (!c.changes.pressed.current) return
          if (
            sel._attachedController === id ||
            !sel.setAttachedController?.(id)
          )
            return
          let tries = 0
          const obs = frames.add(() => {
            const data = sel._controllers?.[id]
            if (data?.pick || ++tries > 5) frames.remove(obs)
            if (!data?.pick) return
            const init = { pointerId: data.id, pointerType: 'xr' }
            scene.simulatePointerDown(data.pick, init)
            data.pointerDownTriggered = true
            // Let go already? Then it was a tap.
            if (!main.pressed) scene.simulatePointerUp(data.pick, init)
          })
        },
        undefined,
        true // ahead of Babylon's own observer, which drops it
      )
    }
    if (controller.motionController) watch(controller.motionController)
    else controller.onMotionControllerInitObservable.addOnce(watch)
  })
}

/*
A CONTROLLER RAY PICKS THE UI LAYERS FIRST.

Babylon's XR pointer selection picks the world and its own default utility
layer, and a nearer world hit wins: so a `ui`/`modal` panel, drawn over the
world, was out of reach in a headset (a wall in between took the ray; and our
layers were never asked at all). While Babylon's rays are being picked (from
the start of an XR frame to the scene's render), a ray that hits one of our
layers returns THAT pick, so it beats the world and the laser stops at the
panel. The pick's distance is lengthened a centimetre because Babylon cuts its
ray to the hit distance and the press re-casts it (an exact-length ray can
fall just short of the plane). Other picks (a biped's ground probe, in
onBeforeRender) are outside the window and untouched.
*/
function pickLayersFirst(
  xr: BABYLON.WebXRDefaultExperience,
  scene: BABYLON.Scene
): () => void {
  const frames = xr.baseExperience?.sessionManager.onXRFrameObservable
  if (frames == null) return () => {}
  const own = Object.prototype.hasOwnProperty.call(scene, 'pickWithRay')
  const original = scene.pickWithRay
  let picking = false
  const start = frames.add(() => (picking = true), undefined, true)
  const end = scene.onBeforeRenderObservable.add(() => (picking = false))
  scene.pickWithRay = function (ray, predicate, fastCheck, trianglePredicate) {
    if (picking) {
      const hit = pickPanelLayers(scene, ray)
      if (hit) {
        hit.distance += 0.01
        return hit
      }
    }
    return original.call(scene, ray, predicate, fastCheck, trianglePredicate)
  }
  return () => {
    frames.remove(start)
    scene.onBeforeRenderObservable.remove(end)
    if (own) scene.pickWithRay = original
    else delete (scene as any).pickWithRay
  }
}

/*
A REBUILT PANEL KEEPS ITS PLACE. The settings panel is rebuilt on every icon
press and refresh (a new element: its rows' closures cannot be reused), and
each rebuild started back at the top, so pressing a toggle at the bottom of a
long panel threw you to the top of it. The new panel is scrolled to where the
old one was (clamped, if it is shorter).
*/
function carryScroll(from: Element | null, to: SVGSVGElement): void {
  const top = (from as { scrollTop?: number } | null)?.scrollTop ?? 0
  if (top > 0 && 'scrollTop' in to)
    (to as { scrollTop: number }).scrollTop = top
}

export class B3d extends Component {
  static preferredTagName = 'tosi-b3d'

  static initAttributes = {
    glowLayerIntensity: 0,
    /*
    THE COLOUR BEHIND EVERYTHING. Empty keeps Babylon's default.

    It is here because three shipped demos already pass it — `b3d-star`,
    `b3d-star-system` and `b3d-black-hole` all set `clearColor` in their `b3d()`
    config — and NOTHING READ IT. The element creator turned each into a
    `clear-color` attribute that no code consulted, so every one of them has
    been quietly rendering against the default blue-grey while its source says
    black. Found while baking a skybox, where a purple-grey void is rather hard
    to miss.
    */
    clearColor: '',
    /*
    WHERE THE FLAT VIEW'S OWN CONTROLS ARE DRAWN. 'scene' (default): in the
    scene, in its `ui` layer, the same UI a headset gets (the corner toolbar
    first; #3307). 'dom': the HTML overlay, kept working and tested.
    */
    flatUi: 'scene' as 'scene' | 'dom',
    frameRate: 30,
    // Default orbit-camera limits (only used when no camera is supplied). They
    // stop the two constant annoyances: zooming out into orbit / in through the
    // scene, and dropping the camera under the ground. Override by providing your
    // own camera in `sceneCreated`, or tune these attributes.
    minElevation: 5, // degrees above the horizon (keeps the camera above ground)
    maxElevation: 70, // degrees (keeps it from going straight overhead)
    minDistance: 2, // closest zoom
    maxDistance: 50, // farthest zoom
    // WebXR is offered by default whenever the device/browser supports an
    // immersive-vr session: a floating "Enter VR" button appears over the
    // scene. Set the `no-xr` attribute to suppress it (e.g. demos that drive
    // XR themselves through a controllable's `cameraType: 'xr'`).
    noXr: false,
    // The subtle reference grid on the floor during an immersive session (a
    // locomotion/motion cue). `"auto"` (default) shows it for the built-in
    // free-fly XR rig, but hides it when a player entity drives the rig (a
    // focused biped/car/aircraft — its own "non-default rig") or when you supply
    // your own `setupXr` (this grid only exists in the default experience).
    // `"on"` always shows it; `"off"` always hides it.
    xrGrid: 'auto' as 'on' | 'off' | 'auto',
    // Show the head-locked face crosshair (a pin target for aim-tracking UX). Opt-in:
    // 'off' (default) keeps it out of the way; 'on' shows it (e.g. a tracking weapon).
    xrReticle: 'off' as 'on' | 'off',
    // Start with the settings panel open (instead of closed behind the owl).
    scenePanelOpen: false,
    // The settings panel's name, drawn in its title bar beside move and close.
    panelTitle: '',
    /** How collapsible sections present: 'fold' (headers you open and close,
     * one open at a time), 'folds' (several open) or 'tabs' (file tabs). */
    panelSections: 'fold' as 'fold' | 'folds' | 'tabs',
    // Scale factor for the glass gamepad clusters. Touch-target pixel sizes vary
    // wildly across devices, so this is exposed for tuning per scene/device.
    gamepadScale: 1,
    /** `'off'` stops the glass gamepad fading when a mouse/keyboard/pad is
     * used (see b3d-gamepad's `fade`). */
    gamepadFade: 'on' as 'on' | 'off',
    // Device quality: 'auto' follows the measured/cached device profile (and, if
    // none exists, runs the probe in the background for next time); 'low' |
    // 'medium' | 'high' force a tier. Drives the `auto` defaults of shadows,
    // reflections, terrain, and the engine render scaling. See b3d-quality.
    quality: 'auto' as QualitySetting,
    /*
    Ambient occlusion (see b3d-ssao). 'off' | 'auto' | 'on'. `auto` follows
    the device tier, and stays off in a headset; `on` runs there too with the
    default `projected` method (the `screen` method is flat only: a post-process
    on a WebXR camera draws one image across both eyes). Off by default: it redraws the opaque
    scene and samples it per pixel, which is a cost a scene should choose.
    */
    ssao: 'off' as SsaoSetting,
    // How dark a fully occluded crease gets.
    ssaoStrength: 1,
    // Metres a surface looks for something occluding it.
    ssaoRadius: 2,
    /*
    'projected' (default) computes occlusion once from between the eyes and
    has every material look it up by world position: flat and in a headset,
    see b3d-ssao-projected. 'screen' is Babylon's post-process, flat only.
    */
    ssaoMethod: 'projected' as 'screen' | 'projected',
    // Projected only: redraws per second (0 = every frame). The lookup is by
    // world position, so a stale drawing stays put while the view moves.
    ssaoRate: 30,
    /*
    IN A HEADSET, resolution is the XR layer's, not the engine's: hardware
    scaling does not reach it. These two do.
    `xrRenderScale` is the fraction of each eye's width and height drawn
    (`0` is AUTO: 1 / the tier's hardware scaling, so 0.67 on the low tier).
    `xrFoveation` is fixed foveation, 0-1 (`-1` is AUTO: the tier's).
    Both apply on entering a session and can be changed during one.
    */
    xrRenderScale: 0,
    xrFoveation: -1,
    /*
    MULTIVIEW draws both eyes in ONE pass (WebXR layers + OVR_multiview2).
    EXPERIMENTAL and not working: on a Quest 3's browser the layers feature
    attached but multiview was not offered, and the layer Babylon fell back to
    drew a blank grey-blue view. Babylon's WaterMaterial and SkyMaterial (and
    our sky fork, cloud deck and light shafts) do not handle multiview either.
    Read on entering a session.
    */
    xrMultiview: 'off' as 'on' | 'off',
    // The ground (biome) shader's form. `auto` is the device tier's: the
    // cheap one on the low tier, and in a headset on the medium tier.
    groundDetail: 'auto' as 'auto' | 'full' | 'lite',
    /*
    Device pixels per CSS pixel to render at, flat. `0` is AUTO: the display's
    own ratio, capped by the device tier (high 2, medium 1.5, low 1 — see
    `PerfBudgets.pixelRatioCap`). A positive value is an explicit cap and wins:
    `1` renders at CSS resolution, `2` full Retina, `0.75` below CSS for speed.
    Rendering at CSS resolution on a 1.25× display upscaled the whole scene and
    softened every star (seen 2026-09-25). XR ignores it — a headset has its
    own framebuffer resolution.
    */
    pixelRatio: 0,
    // Add a "Perf stats" section to the scene panel (flat AND the
    // in-VR panel — so it's reachable in a headset). Opt-in per scene; a global
    // `#perf` / `#debug` (or `?perf` / `?debug`) in the page URL, or a host calling
    // `showB3dStats()`, reveals it on every scene (handy on mobile, no console
    // needed). Shows `debugState` plus a one-tap hardware-scaling probe. Default off.
    stats: false,

    /*
    PAUSE.

    `pauseWhenHidden` is a string enum, not a boolean, because an HTML boolean
    attribute cannot default to true — an absent attribute is false, which is
    correct HTML and would silently disable the default (tosijs now throws on a
    true-default boolean). Same reason as `gamepadFade` and friends.
    */
    /** Pause automatically when the tab/window goes to the background.
     * ⚠️ EXPERIMENTAL — see the pause demo; the VR path is unvalidated. */
    pauseWhenHidden: 'on' as 'on' | 'off',
    /** Come up paused, showing the pause panel — the "press Start" shape. */
    startPaused: false,
    /**
     * Freeze the clock while the **re-seat** dialog is up (`'on'` default).
     *
     * Re-seating is a comfort action, and being shot while you do it is unfair.
     * But freezing is a decision only a LOCAL world can make — a networked one
     * cannot stop the other players — so set `'off'` for multiplayer and the
     * dialog still gates YOUR input, which is the half that always works.
     *
     * A string enum rather than a boolean because an HTML boolean attribute
     * cannot default to true (absent ⇒ false), and this one wants to.
     */
    reseatFreeze: 'on' as 'on' | 'off',
    /**
     * How fast SIM time runs against wall time. `1` is real time, `0.25` is
     * slow motion, `0` stops the simulation while the scene keeps rendering.
     *
     * The gap this closes: a consumer could scale its own effect timing but not
     * craft motion, because velocity comes from integrating the engine delta —
     * so a "slow motion" control was a lie by omission, the explosions slowing
     * and the aircraft not (tosijs-3d-ensemble, #41).
     *
     * Everything already integrating with `sceneDelta` scales for free, since
     * that is now the SIM delta. Anything that must not slow — UI animation, a
     * spinner, a countdown in wall seconds — reads `realDt` from the frame
     * package instead.
     */
    timeScale: 1,
    /*
    ONE WIND FOR THE SCENE — see [[wind]].

    Clouds, water and ambient each carried their own, in three spellings, so an
    author set it three times and they could silently disagree: clouds streaming
    north-east over water whose waves ran south.

    `0` means "no scene wind", and a child's own wind attributes still win where
    they are set — absence is what starts meaning "take the scene's". So this is
    additive: nothing that worked stops working.
    */
    windSpeed: 0,
    /** Where the wind is GOING, north-up and clockwise. `0` blows toward +Z. */
    windBearingDeg: 0,
    /** Gust size as a fraction of `windSpeed`. `0` is dead steady. */
    windGust: 0,
    /**
     * On resume, enter immersive VR if the device supports it. This is why
     * starting paused matters: `enterXRAsync` REQUIRES a user gesture, and the
     * Continue tap is one. A scene that tried to enter XR on load would be
     * refused by the browser.
     *
     * The other direction — leaving VR pauses — is no longer gated on this; it
     * happens for every scene, because the pair is the point.
     */
    enterXrOnResume: 'off' as 'on' | 'off',
  }

  static shadowStyleSpec = {
    ':host': {
      display: 'block',
      position: 'relative',
      overflow: 'hidden',
      background: '#000',
      height: '100%',
      maxHeight: '100vh',
      // No page zoom from inside the scene: a double-tap on the system bar
      // zoomed an iPhone's page, and the scene (which takes every touch)
      // left no way to pinch it back.
      touchAction: 'none',
    },
    // Full screen where the browser has no Fullscreen API (iPhone Safari):
    // the scene fills what is visible. dvh follows Safari's toolbars.
    // In the top layer as a popover where it can be (iOS 17+), so no
    // ancestor's containment or overflow traps it; these undo the popover's
    // default margin, border and padding.
    ':host([data-maximized])': {
      position: 'fixed',
      inset: '0',
      width: '100%',
      height: '100dvh',
      maxWidth: 'none',
      maxHeight: 'none',
      margin: '0',
      border: '0',
      padding: '0',
      zIndex: '2147483000',
    },
    ':host .spinner': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '48px',
      height: '48px',
      marginTop: '-24px',
      marginLeft: '-24px',
      border: '4px solid rgba(255,255,255,0.15)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'tosi-spin 0.8s linear infinite',
      transition: 'opacity 0.3s ease-out',
      // Above the canvas, so `setLoading` can show it over a running scene.
      zIndex: '3',
      pointerEvents: 'none',
    },
    ':host .spinner.hidden': {
      opacity: '0',
      pointerEvents: 'none',
    },
    // What is loading, below the spinner (`setLoading('…')`).
    ':host .loading-caption': {
      position: 'absolute',
      top: 'calc(50% + 36px)',
      left: '0',
      right: '0',
      textAlign: 'center',
      color: '#fff',
      font: '13px system-ui, sans-serif',
      textShadow: '0 1px 3px rgba(0,0,0,0.8)',
      zIndex: '3',
      pointerEvents: 'none',
      transition: 'opacity 0.3s ease-out',
    },
    ':host .loading-caption.hidden': {
      opacity: '0',
    },
    '@keyframes tosi-spin': {
      to: { transform: 'rotate(360deg)' },
    },
    ':host canvas': {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      opacity: '0',
      transition: 'opacity 0.5s ease-in',
    },
    ':host canvas.ready': {
      opacity: '1',
    },
    ':host .babylonVRicon': {
      height: 50,
      width: 80,
      backgroundColor: 'transparent',
      filter: 'drop-shadow(0 0 4px #000c)',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      border: 'none',
      borderRadius: 5,
      borderStyle: 'none',
      outline: 'none',
      transition: 'transform 0.125s ease-out',
    },
    ':host .babylonVRicon:hover': {
      transform: 'scale(1.1)',
    },
    // Top-LEFT lozenge: one rounded pill holding the two icon buttons (scene
    // settings + Enter VR) side by side. Demos pin text overlays top-right, so the
    // left keeps this clear of them. Hidden until at least one button is relevant
    // (`:has`) so it never flashes as an empty pill while the scene loads. Icon
    // size is themed via the shared --tosi-icon-size var.
    ':host .scene-lozenge': {
      position: 'absolute',
      top: '12px',
      left: '12px',
      zIndex: '20',
      // Visible by default so the toolbar works everywhere (a browser without
      // :has() — e.g. older Firefox — must NOT lose it).
      display: 'inline-flex',
      alignItems: 'stretch',
      background: 'rgba(0,0,0,0.55)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '20px',
      overflow: 'hidden',
      '--tosi-icon-size': '20px',
    },
    // Progressive enhancement: where :has() is supported, hide the pill entirely
    // while it holds no visible button (no empty-pill flash on load). Where it
    // isn't, this selector is invalid and dropped — the lozenge stays visible.
    ':host .scene-lozenge:not(:has(.lozenge-button:not([hidden])))': {
      display: 'none',
    },
    // Height-uniform, width sizes to content (with a square floor + side padding)
    // so a non-square icon like the 40×24 xrColor mark gets horizontal room
    // instead of being cramped in a fixed square.
    ':host .lozenge-button': {
      minWidth: '40px',
      height: '40px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 10px',
      background: 'transparent',
      border: 'none',
      color: '#fff',
      cursor: 'pointer',
      transition: 'background 0.15s',
    },
    ':host .lozenge-button:hover': {
      background: 'rgba(255,255,255,0.15)',
    },
    ':host .lozenge-button[hidden]': {
      display: 'none',
    },
    // A system panel asking to be looked at (`panel.attention()`), blinking.
    ':host .lozenge-button[data-attention]': {
      background: ATTENTION_FILL,
    },
    // A hairline divider only between two VISIBLE buttons (so a lone button reads
    // as a clean single-icon pill).
    ':host .lozenge-button:not([hidden]) + .lozenge-button:not([hidden])': {
      borderLeft: '1px solid rgba(255,255,255,0.2)',
    },
    // Buttons are disabled (dimmed, inert) until the scene has loaded.
    ':host .lozenge-button:disabled': {
      opacity: '0.4',
      cursor: 'default',
      pointerEvents: 'none',
    },
    /*
    TOP-LEFT, inset equally — not hanging below the gear.

    It sat at `top: 60px` to clear the button that opens it, which meant the
    panel started a lozenge-height down the viewport and pushed its own content
    off the bottom on a short scene. Tonio: "since the scene panel has its own
    close box we might as well just place the panel top-left ... it just pushes
    stuff out of view."

    Covering the gear is fine precisely because the panel closes itself: the
    control that dismisses it is IN it, so the one underneath is not needed
    while it is open. Equal inset top and left so it reads as anchored to the
    corner rather than parked under something.
    */
    ':host .scene-panel-overlay': {
      position: 'absolute',
      top: '12px',
      left: '12px',
      zIndex: '20',
      filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.5))',
    },
    ':host .scene-panel-overlay[hidden]': {
      display: 'none',
    },
    // Centred, above everything, and it dims the world behind it — a modal
    // should read as one. `pointer-events` on the backdrop so a stray click
    // lands on the scrim rather than steering the camera underneath.
    ':host .pause-overlay': {
      position: 'absolute',
      inset: '0',
      zIndex: '30',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.35)',
    },
    ':host .pause-overlay[hidden]': {
      display: 'none',
    },
    ':host .pause-overlay > svg': {
      maxWidth: 'min(90%, 420px)',
      height: 'auto',
      filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.6))',
    },
    /*
    The panel's HEADER: a right-aligned row of equal, close-sized buttons —
    the icon-bar toggles, then close. Everything that is CHROME lives here, so
    the panel body is entirely the author's controls.
    */
    ':host .scene-panel-head': {
      position: 'absolute',
      top: '4px',
      right: '4px',
      // Across the top, so `panelTitle` sits at the left and the buttons stay
      // right-aligned (the title takes the slack).
      left: '12px',
      zIndex: '1',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    ':host .scene-panel-title': {
      // Centred, as the scene's title bar centres it (one UI).
      flex: '1',
      textAlign: 'center',
      minWidth: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#e8eef8',
      fontWeight: '600',
      fontSize: '14px',
    },
    ':host .scene-panel-btn': {
      width: '26px',
      height: '26px',
      border: 'none',
      borderRadius: '50%',
      background: 'rgba(0,0,0,0.55)',
      color: '#fff',
      cursor: 'pointer',
      fontSize: '17px',
      lineHeight: '1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      flex: '0 0 auto',
    },
    ':host .scene-panel-btn svg': {
      width: '15px',
      height: '15px',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    ':host .scene-panel-btn:hover': {
      background: 'rgba(0,0,0,0.85)',
    },
    // A toggle that is ON reads as filled rather than as merely hovered —
    // hover and active must not compete for the same intensity.
    ':host .scene-panel-btn.active': {
      background: 'var(--xr-color, #4a9eff)',
      color: '#fff',
    },
  }

  content = [
    div({ class: 'spinner', part: 'spinner' }),
    div({ class: 'loading-caption hidden', part: 'loadingCaption' }),
    canvas({ part: 'canvas' }),
    // Top-left lozenge holding two icon buttons side by side: scene settings
    // (opens the panel) and Enter VR (the purpose-built xrColor mark) — so VR
    // availability is obvious and Enter VR is never a panel row that could
    // scroll/clip. Each button reveals itself when relevant; the lozenge stays
    // hidden until then. (Both live in the template — appending to the shadow
    // root later doesn't persist.)
    div(
      { class: 'scene-lozenge', part: 'sceneToolbar' },
      button(
        {
          class: 'lozenge-button',
          part: 'scenePanelGear',
          type: 'button',
          title: 'Scene settings',
          hidden: true,
          // Disabled until the scene finishes loading (reveal() enables it).
          disabled: true,
        },
        // The tosijs-3d owl (goggles, no cube) rather than a gear: this is the
        // button that opens OUR panel, so it should look like us. The cube-less
        // mark reads at 20px where the full logo does not.
        //
        // The `scenePanelGear` part name stays — a consumer may be styling
        // `::part(scenePanelGear)`, and renaming it to match the artwork would
        // break that for nothing.
        svgIcons.tosiXr()
      ),
      button(
        {
          class: 'lozenge-button',
          part: 'enterVrButton',
          type: 'button',
          hidden: true,
          // Disabled until the scene finishes loading (reveal() enables it).
          disabled: true,
          title: 'Enter VR',
        },
        svgIcons.xrColor()
      ),
      // Full screen, where the browser has it (not iPhone Safari): the scene
      // element, not the page. Toggles; the icon says which way.
      button(
        {
          class: 'lozenge-button',
          part: 'fullscreenButton',
          type: 'button',
          hidden: true,
          title: 'Full screen',
        },
        svgIcons.maximize()
      )
    ),
    div({ class: 'scene-panel-overlay', part: 'scenePanelHost', hidden: true }),
    /*
    THE PAUSE PANEL, ON A FLAT SCREEN, IS DOM.

    It used to be an in-scene plane in both presentations. That is right in a
    headset — there is no DOM to put it on — and wrong on a monitor, where it
    inherits every problem of being a thing in the world: it has to find a spot
    with clear line of sight, it can be occluded, it has to be raycast to be
    clicked, and it lands wherever the geometry allows rather than where you are
    looking. Tonio: "in flat 3d the continue should be presented in the dom like
    the scene panel."

    Same rule the scene panel already follows — ONE widget list, two
    presentations — and the same reason: the flat one is a DOM overlay because
    flat HAS one.
    */
    div({ class: 'pause-overlay', part: 'pauseHost', hidden: true }),
    slot(),
  ]

  engine!: BABYLON.Engine
  scene!: BABYLON.Scene
  camera?: BABYLON.Camera
  gui?: GUI.GUI3DManager
  glowLayer?: BABYLON.GlowLayer
  private static _warnedSsaoAlways = false
  private _ssao?: SsaoController
  /** Whether occlusion is actually being drawn (the setting can say `on` where it cannot run). */
  private _ssaoRunning = false
  private _projectedAo?: ProjectedAoController
  private _ssaoCamObs?: BABYLON.Observer<BABYLON.Scene> | null
  xrHelper?: BABYLON.WebXRDefaultExperience
  private _unpickLayers: (() => void) | null = null
  xrActive = false
  /** True from ENTERING_XR to the end of EXITING_XR: wider than `xrActive`. */
  private _xrPresenting = false

  /** Whether a page is holding the spinner up (`setLoading`). */
  private _loadingHeld = false

  /** What is still building before the scene is first shown (`holdLoading`). */
  private _loadingHolds = new Set<string>()
  private _holdCount = 0

  /**
   * Keep the scene hidden behind its spinner while something builds itself
   * after its files have loaded (a city laying out its blocks, a body being
   * rigged), so the scene is never shown half made. Returns the release; call
   * it when built, or if the build fails. Only the first showing waits, and
   * for at most 20 s.
   */
  holdLoading(label = 'loading'): () => void {
    const key = `${label}#${++this._holdCount}`
    this._loadingHolds.add(key)
    return () => {
      this._loadingHolds.delete(key)
    }
  }

  private _terrainBusy(): boolean {
    for (const t of this.querySelectorAll('tosi-b3d-terrain')) {
      if ((t as unknown as { busy?: boolean }).busy) return true
    }
    return false
  }

  /**
   * The loading spinner, for a page that loads its own content after the
   * scene is up: `true` shows it, a string shows it with that message below,
   * `false` hides it. Until it is called the spinner is the scene's own and
   * goes when the scene first renders; once a page holds it, that page lets
   * it go. `{ spin: false }` shows the message alone (a load that failed).
   */
  setLoading(
    message: string | boolean,
    options: { spin?: boolean } = {}
  ): void {
    const spinner = this.parts.spinner as HTMLElement | undefined
    const caption = this.parts.loadingCaption as HTMLElement | undefined
    this._loadingHeld = message !== false
    spinner?.classList.toggle(
      'hidden',
      message === false || options.spin === false
    )
    if (caption) {
      caption.textContent = typeof message === 'string' ? message : ''
      caption.classList.toggle('hidden', typeof message !== 'string')
    }
  }

  // The scene the pointer last entered / pressed — so that when a page hosts several
  // live demos, global keyboard/gamepad input only drives the one you're interacting
  // with (see `hasInputFocus`). Null until the first interaction (then everything is
  // "focused", i.e. a lone demo just works).
  private static _active: B3d | null = null
  /** True when this scene should consume shared keyboard/gamepad input — it's the
   * active (last hovered/clicked) scene, or none has been touched yet. Controllables
   * gate their input on this so one gamepad doesn't drive every demo on a page. */
  get hasInputFocus(): boolean {
    return B3d._active === null || B3d._active === this
  }
  /** Make this the input-focused scene (also happens on pointerenter/pointerdown). */
  takeInputFocus(): void {
    B3d._active = this
  }
  /*
  A TOUCH THAT ENDS OFF THE CANVAS GIVES ITS SLOT BACK.

  Babylon's canvas input keeps a slot per touch (`navigator.maxTouchPoints`,
  else 2), and a touch pointer it sees MOVE without a press takes one
  retroactively. If that pointer's release lands elsewhere (the glass pad
  captures its own), the canvas never hears it, the slot is never freed, and
  once they are all held Babylon ignores every new press ("Max number of
  touches exceeded"): the system bar went dead after two pad drags in the
  headset's flat browser, until a DOM click blurred the canvas (Tonio). So a
  touch or pen released anywhere else is cancelled on the canvas too; one
  Babylon is not tracking is ignored there.
  */
  private _freeTouchSlot = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') return
    const canvas = this.engine?.getRenderingCanvas()
    if (canvas == null || e.target === canvas) return
    canvas.dispatchEvent(
      new PointerEvent('pointercancel', {
        pointerId: e.pointerId,
        pointerType: e.pointerType,
      })
    )
  }
  /** Reference frames (world/rig/body/neck/face) for spatial UI, live only while
   * an XR session is running. Parent in-scene UI to `xrFrames.body` etc. */
  xrFrames: XrFrames | null = null
  /*
  THE ENGINE, REACHABLE FROM THE FRAMEWORK.

  Babylon is a PEER dependency: the consumer supplies it, and a second copy in
  the tree is a real bug (two `Vector3` classes that fail `instanceof` against
  each other, two engine registries). So the safest way to get a `MeshBuilder`
  is to ask the library that is already holding one, rather than to import a
  second specifier and hope the bundler dedupes it.

  Static because it is a property of the FRAMEWORK, not of any one scene —
  `B3d.BABYLON` works before a scene exists. The instance field stays as a
  convenience for the common case, where you have an `el` in hand.

  It is also re-exported from the barrel (`import { BABYLON } from 'tosijs-3d'`),
  which is the form to reach for in a doc example. Before this, examples used a
  bare `BABYLON` global that DOES NOT EXIST — `typeof BABYLON` is `undefined` on
  the page — so every one of them was one line from a ReferenceError.
  */
  static BABYLON = BABYLON
  BABYLON = BABYLON

  /*
  XR SESSION ACCOUNTING — so a headset can answer "is it us or the browser?"

  The Quest does not reliably release WebXR GPU resources between sessions, so
  after a dozen enter/exits everything degrades — including back in the flat
  view, and only a reload clears it. That is browser-level and not ours to fix,
  but it is indistinguishable IN THE HEADSET from a leak of our own, and there is
  no console in VR to check with.

  So: snapshot the scene's resource counts at the FIRST XR entry and report the
  delta in the Perf panel. Flat deltas across many sessions means our teardown is
  clean and the degradation is the browser's; growing deltas means it is ours.
  Tonio hit exactly this after ~20 sessions in one pass and had no way to tell.
  */
  private _xrSessions = 0
  private _xrBaseline: { mesh: number; mat: number; tex: number } | null = null

  private _makers?: Makers
  /**
   * Babylon primitives with the easy-to-forget parts done: material from
   * `color`/`glow`, `register()` so the sun and reflections see it, and
   * `computeWorldMatrix` so a ray this frame doesn't find it at the origin.
   *
   * `el.make.box({ y: 1, color: '#c33' })`. Same shape as a library's
   * `lib.make.scout({ y: 1 })` — one vocabulary whether you're making a
   * primitive or a model. See `make-mesh`.
   */
  get make(): Makers {
    return (this._makers ??= createMakers(this))
  }

  /**
   * Open a popup as its own SURFACE — another plane, floating above the opener,
   * rather than more rows crammed into the panel you already have.
   *
   * See `popup-surface`: it can be owned (travels and dies with its opener) or
   * torn off (promoted to world space, preserving pose, and draggable).
   */
  openPopup(opts: PopupSurfaceOptions): PopupSurface {
    return openPopup(this, opts)
  }

  declare minElevation: number
  declare maxElevation: number
  declare minDistance: number
  declare maxDistance: number
  declare noXr: boolean
  declare xrMultiview: 'on' | 'off'
  declare xrGrid: 'on' | 'off' | 'auto'
  declare xrReticle: 'on' | 'off'
  declare scenePanelOpen: boolean
  declare panelTitle: string
  declare panelSections: 'fold' | 'folds' | 'tabs'
  /**
   * The on-screen "glass" gamepad. Absent/`false` = none; `true` or an empty
   * string = the full default layout; any other string selects and positions
   * controls (`'a,b,right_stick(40,0),menu'`). Markup: `gamepad` or
   * `gamepad="…"`.
   *
   * A PLAIN property, deliberately not in `initAttributes`: it is read once,
   * at setup (property or attribute), and it is genuinely `boolean | string`.
   * As an attribute tosijs typed it by its `false` default and warned on
   * every string layout: one console error per demo, of the kind that means
   * a call site was left behind by a type change. That trains people to
   * ignore that warning.
   */
  gamepad: boolean | string = false
  declare stats: boolean
  declare pauseWhenHidden: 'on' | 'off'
  declare startPaused: boolean
  declare reseatFreeze: 'on' | 'off'
  declare enterXrOnResume: 'on' | 'off'

  // ─── Pause ────────────────────────────────────────────────────────────────
  private _paused = false
  // Flat, in-scene pause (flatUi 'scene'): a modal frame panel, its own frames,
  // and the observer that poses them.
  private _pauseModal: (() => void) | null = null
  // Every modal open now (pause, a death's respawn panel…), with what it
  // shows, so it can move between the flat and headset presentations.
  private _modals = new Set<{
    svg: SVGSVGElement
    height: number
    fallback?: () => (() => void) | null
    close: (() => void) | null
  }>()
  private _pauseWatch: (() => void) | null = null
  private _cameraWasAttached = false
  // Snapshot of the flat ORBIT camera taken on XR entry, restored on exit. An
  // ArcRotateCamera orbits a target, so Babylon's default "carry the XR pose
  // back" copies your walked-to headset position into it and recomputes a low
  // orbit angle — you exit VR looking at the scene from the floor (Tonio, VR
  // pass 2). Free/walkable cameras CAN adopt an arbitrary pose, so they keep the
  // carry-back (which is the symmetric behaviour that was liked); only orbit
  // cameras, for which it's meaningless, are restored.
  private _flatOrbitState: {
    alpha: number
    beta: number
    radius: number
    target: BABYLON.Vector3
  } | null = null

  /** Is the simulation held? Rendering continues — the panel has to be drawn. */
  get paused(): boolean {
    return this._paused
  }

  /**
   * HOLD THE SIMULATION. Rendering keeps going (a frozen frame plus a panel is
   * the point; stopping the render loop would leave the panel invisible and the
   * canvas stale), but time does not advance: no combat tick, no fog, no
   * `update` hook, and `B3dControllable` sees an empty input so nothing drifts
   * while you are away.
   *
   * `reason` is carried on the `pause` event so a game can tell "the player
   * asked" from "the tab went away" — a settings menu and an interruption
   * deserve different handling.
   */
  /**
   * Modal input gate — the controls go dead, the world does NOT stop.
   *
   * For a dialog that borrows a control you also play with: the re-seat prompt
   * asks for a trigger pull, and without this that same pull fires the gun.
   * Deliberately not `pause()`: a pause raises the pause panel, can enter XR on
   * resume, and clobbers an existing pause when it lifts. Read by
   * `B3dControllable._update`.
   */
  private _inputSuppressed = false
  get inputSuppressed(): boolean {
    return this._inputSuppressed
  }
  suppressInput(on: boolean): void {
    this._inputSuppressed = on
  }

  /**
   * Stop the CLOCK for a transient modal — no pause panel, no resume semantics.
   *
   * `pause()` is a user-facing state: it raises the panel, can enter XR on
   * resume, and lifting it would clobber a pause the user set themselves. A
   * dialog that lasts two seconds wants none of that; it just wants the world
   * to hold still. This publishes `b3dFrameDelta = 0` exactly as a pause does,
   * so everything on `sceneDelta` stops, and lifts without touching pause state
   * (a scene paused underneath STAYS paused).
   *
   * **Local only, by definition.** Freezing is a decision your machine can make
   * and a networked world cannot honour — you cannot stop other players'
   * clocks. So `suppressInput` is the floor (your controls go dead, the world
   * carries on) and this is policy on top, which is why the re-seat dialog
   * always gates input and only optionally freezes (`reseatFreeze`).
   */
  private _frozen = false
  get frozen(): boolean {
    return this._frozen
  }
  freeze(on: boolean): void {
    if (on === this._frozen) return
    this._frozen = on
    // Don't hand the held time back as one giant step on the first live frame.
    if (!on) this.lastRender = realNow()
  }

  /**
   * Stop BABYLON'S OWN clocks too, not just ours.
   *
   * Publishing `b3dFrameDelta = 0` pauses everything that simulates on
   * `sceneDelta` — which is every component in this library. It does nothing
   * whatever to the engine's own time: `AnimationGroup`s keep playing and the
   * physics engine keeps stepping. So a paused scene held its projectiles and
   * its water still while every character carried on walking, every door kept
   * opening and anything with a rigid body kept falling. Tonio: "There are a
   * LOT of examples where pause doesn't seem to pause anything."
   *
   * That is most of what a demo actually shows moving, which is why the pause
   * read as doing nothing rather than as doing half.
   *
   * The previous values are REMEMBERED rather than assumed: a scene may have
   * turned either of these off for its own reasons, and resuming must not hand
   * it back something it never had. Only transitions write, so an app that
   * disables animations mid-pause keeps its own choice on resume.
   */
  private _engineTimeStopped = false
  private _animationsWere = true
  private _physicsWere = true
  private _stopEngineTime(stopped: boolean): void {
    if (stopped === this._engineTimeStopped) return
    const scene = this.scene
    if (scene == null) return
    this._engineTimeStopped = stopped
    if (stopped) {
      this._animationsWere = scene.animationsEnabled
      this._physicsWere = scene.physicsEnabled
      scene.animationsEnabled = false
      scene.physicsEnabled = false
    } else {
      scene.animationsEnabled = this._animationsWere
      scene.physicsEnabled = this._physicsWere
    }
  }

  /**
   * SLOW MOTION REACHES BABYLON'S CLOCKS TOO: animation groups
   * (`scene.animationTimeScale`) and particle ageing (each system's
   * `updateSpeed`). Physics already steps on `sceneDelta`.
   *
   * Each is the app's own value TIMES `timeScale`. What was written last is
   * remembered with the value it came from, so a value that differs from it
   * is the app's new choice, and becomes the base: an app's own speed-up
   * still works, and resuming hands back what it set.
   */
  private _animationScale: { base: number; written: number } | null = null
  private _particleScale = new WeakMap<
    BABYLON.IParticleSystem,
    { base: number; written: number }
  >()
  private _scaleEngineTime(scale: number): void {
    const scene = this.scene
    if (scene == null) return
    const scaled = (
      was: { base: number; written: number } | undefined | null,
      now: number
    ) => {
      const base = was == null || now !== was.written ? now : was.base
      return { base, written: base * scale }
    }
    const a = scaled(this._animationScale, scene.animationTimeScale)
    scene.animationTimeScale = a.written
    this._animationScale = a
    for (const ps of scene.particleSystems) {
      const p = scaled(this._particleScale.get(ps), ps.updateSpeed)
      ps.updateSpeed = p.written
      this._particleScale.set(ps, p)
    }
  }

  pause(reason: 'user' | 'hidden' | 'xr' | 'start' | string = 'user'): void {
    if (this._paused) return
    this._paused = true
    this._suppressGamepad('pause', true)
    this._showPausePanel()
    // The transport row reads `paused` to choose its label and icon, so the
    // panel has to be told the state moved — see `resume` for the report.
    this._repaintPanels()
    this.dispatchEvent(
      new CustomEvent('pause', { detail: { reason }, bubbles: true })
    )
  }

  /** Let time run again, and (if `enterXrOnResume`) take the user into VR —
   * this call is expected to be inside a user gesture, which is what makes
   * entering XR legal at all. */
  resume(): void {
    if (!this._paused) return
    this._paused = false
    this._suppressGamepad('pause', false)
    this._hidePausePanel()
    /*
    REPAINT THE PANEL — the state it displays has changed underneath it.

    The `__pause` row picks its label and icon from `this.paused`, but nothing
    repainted the panel when that flipped, so resuming from the pause DIALOG
    left the panel still offering Play. Tonio: "If you click continue, the PLAY
    button remains visible on the panel (it doesn't update)."

    It only looked right before because the common path was pressing the panel's
    own button, which reopens the panel afterwards. A second route to the same
    state — the dialog — had no reason to.
    */
    this._repaintPanels()
    // A resume must not deliver the whole absence as one frame. `frameDelta` is
    // already clamped to 0.1s, but resetting the baseline keeps even that away.
    this.lastRender = realNow()
    this.dispatchEvent(new CustomEvent('resume', { bubbles: true }))
    if (this.enterXrOnResume === 'on' && !this.xrActive) {
      const base = this.xrHelper?.baseExperience
      if (base != null) {
        // Flush rAF-batched work BEFORE handing the frame clock to the
        // compositor — a render queued at the moment of entry is stranded for
        // the whole session (see _installXrRafPump).
        void updates()
          .then(() => base.enterXRAsync('immersive-vr', 'local-floor'))
          .catch(() => {
            /* no headset, or the user declined — staying flat is fine */
          })
      }
    }
  }

  /**
   * The default pause panel: a title and a Continue button, centred in front of
   * the camera. Camera-relative so it works flat AND in a headset without a
   * second implementation — the same choice `b3d-death` makes.
   */
  /** Ask the glass gamepad to step aside for a menu, or come back. Named
   * reasons, so the pause panel and a death panel do not undo each other. */
  /** Every glass pad that serves this scene: inside it (`gamepad: true` mounts
   * it there) or mounted beside it, which is how the gamepad page's own demo
   * does it. */
  private _gamepads(): Array<{
    suppress?: (reason: string) => void
    release?: (reason: string) => void
    requested?: (part: string) => boolean
    pulse?: (part: 'view' | 'menu') => void
  }> {
    return [
      ...this.querySelectorAll('tosi-b3d-gamepad'),
      ...(this.parentElement?.querySelectorAll(':scope > tosi-b3d-gamepad') ??
        []),
    ] as unknown as ReturnType<B3d['_gamepads']>
  }
  _suppressGamepad(reason: string, on: boolean): void {
    for (const pad of this._gamepads()) {
      if (on) pad.suppress?.(reason)
      else pad.release?.(reason)
    }
  }

  private _showPausePanel(): void {
    if (this._pauseModal != null || this.scene == null) return
    /*
    TAKE THE CAMERA'S HANDS OFF THE POINTER FIRST.

    The panel is IN THE SCENE, so a tap on it is also a tap on the canvas — and
    the camera's own input gets it too. Reported from a phone: the first attempt
    to press Continue was read as a pinch-zoom, which moved the camera through
    the panel and hid it, so the button had to be un-zoomed back into reach
    before it could be pressed. A pause panel you have to fight the camera to
    reach is worse than no pause panel.

    Freezing the camera is also just what "paused" means. Restored on resume,
    and only if we were the ones who detached it.
    */
    /*
    Record whether the camera was REALLY attached, not merely that one exists.

    This flag used to be set to `true` whenever `this.camera != null`, which
    made the comment above it vacuous — it claimed "only if we were the ones who
    detached it" and then recorded nothing of the sort. Every gameplay camera in
    this library is installed unattached (`setActiveCamera(cam, {attach: false})`
    in b3d-biped and b3d-input-focus, and the death rig's orbit camera), so
    resuming HANDED THE CANVAS to a follow camera that was deliberately never
    given it: Babylon's FollowCamera wires arrows/wheel/drag by default, and the
    arrows are also KeyboardGamepad's right stick, so the player's own look keys
    would drive Babylon's rig at the same time. Reachable with no opt-in —
    `pauseWhenHidden` defaults on, so tab away, tab back, Continue, drag.

    Asking Babylon is better than remembering what we did, because several
    components attach or detach the camera directly without going through
    `setActiveCamera` (b3d-galaxy, b3d-svg-plane, the XR restore) and a
    remembered flag goes stale behind them.
    */
    this._cameraWasAttached = cameraIsAttached(this.camera)
    if (this._cameraWasAttached) this.camera?.detachControl()
    const rows = this.pausePanel(this, () => this.resume()) ?? [
      label3d({ text: 'Paused', bold: true }),
      button3d({
        // Only where there is a headset to enter: the helper is built only
        // when immersive-vr is supported (#3431, an iPhone said "in VR").
        label:
          this.enterXrOnResume === 'on' && this.xrHelper != null
            ? 'Continue in VR'
            : 'Continue',
        // The tap IS the user gesture that makes entering XR legal.
        handleClick: () => this.resume(),
      }),
    ]
    // Sized to its CONTENT — `rows` comes from the consumer's `pausePanel`
    // hook, so a guessed height is a guess about someone else's widgets.
    const { svg, height: svgH } = fitPanel(rows, { width: 320, maxHeight: 560 })

    /*
    (FLAT with `flat-ui="dom"`: the DOM. The default flat presentation is now
    the modal in the scene, above; what follows is why the DOM was chosen
    before the modal layer and screen pins existed.)

    FLAT: the DOM. IMMERSIVE: a plane in the scene.

    ONE widget list, two presentations — the scene panel's rule, applied to the
    modal that most needed it. On a monitor a DOM overlay is simply better on
    every axis that has bitten this panel: it cannot be occluded, it cannot be
    placed somewhere odd by a line-of-sight cast, it needs no raycast to be
    clicked, and it is exactly where you are already looking. All of that
    machinery exists because a HEADSET has no DOM, which is the only place it
    earns its cost.

    `panel3d` hangs `handlePointer` off the SVG for the in-scene path, but as a
    DOM node the widgets' own listeners work natively — the same element,
    unmodified, in both.
    */
    /*
    FLAT, IN THE SCENE (flatUi 'scene', the default; #3307): the same SVG as a
    MODAL panel, over the world and over the ui layer, drawn and picked first,
    pinned to the middle of the screen square to it. The reasons it once
    moved to the DOM (occlusion, a line-of-sight placement, a raycast to
    press it, landing somewhere odd) are each answered by the modal layer
    and the screen pins. Its own head-locked frame, because a scene with no
    other flat panels has none.
    */
    this._pauseModal = this.showModal(svg, svgH, () =>
      this._pauseOwn(svg, svgH)
    )
  }

  /** The pause where the scene's modal is not available: the DOM flat
   * (`flat-ui="dom"`), else a plane in the world. Returns its dispose. */
  private _pauseOwn(svg: SVGSVGElement, svgH: number): () => void {
    if (!this.xrActive) {
      const host = this.parts.pauseHost as HTMLElement | undefined
      if (host != null) {
        host.replaceChildren(svg)
        host.removeAttribute('hidden')
        return () => {
          host.replaceChildren()
          host.setAttribute('hidden', '')
        }
      }
    }

    /*
    FIT THE VIEWPORT, don't assume a desktop one.

    A fixed 1.1-wide panel at z=2.2 is comfortable on a 16:9 monitor and TOO
    WIDE on a phone held upright: at the default ~0.8 rad vertical FOV the
    visible width there is about 0.86 world units, so the panel's edges — and
    with them the button — sit off-screen. Reported from a phone: the button had
    to be un-zoomed back into reach.

    So derive the size from the camera's actual FOV and aspect, and take the
    smaller of "as wide as it wants" and "80% of what's visible".
    */
    const z = 2.2
    const cam = this.scene!.activeCamera
    const fov = (cam as BABYLON.FreeCamera)?.fov ?? 0.8
    const aspect = this.engine.getAspectRatio(cam as BABYLON.Camera) || 1.6
    const width = panelFitWidth(fov, aspect, z, 1.1)
    const plane = b3dSvgPlane({
      cameraRelative: true,
      // World-placed, like the respawn panel: a thing you read and then press
      // should not follow every glance, but must still be findable — so it
      // comes to you if you look away for ~2s. See dialog-placement.
      placement: 'world',
      width,
      height: width * (svgH / 320),
      z,
      y: 0,
      resolution: 512,
      pointerEvents: 'on',
    }) as B3dSvgPlane
    plane.svgElement = svg
    this.appendChild(plane)
    return () => plane.remove()
  }

  /**
   * Show `svg` (a `fitPanel`, `height` its viewBox height) as the scene's
   * MODAL: over the world and over the ui layer, drawn and picked first.
   * Flat it is centred on the screen and square to it; in a headset it hangs
   * in front of you at a comfortable distance, a little below eye level, and
   * follows your body round. Where the scene has no modal to give
   * (`flat-ui="dom"` on a monitor, a custom `setupXr` with no frames) it calls
   * `fallback`, the caller's own presentation, which returns its dispose; and
   * again whenever entering or leaving a headset leaves no modal to give.
   * Returns its close, or `null` if there was neither.
   *
   * The pause and a death's respawn panel both come through here, so they are
   * one thing in one place. One that is open when you enter or leave a
   * headset moves across with you.
   */
  showModal(
    svg: SVGSVGElement,
    height: number,
    fallback?: () => (() => void) | null
  ): (() => void) | null {
    const m = { svg, height, fallback, close: null as (() => void) | null }
    m.close =
      this._presentModal(svg, height, this._modals.size) ?? fallback?.() ?? null
    if (m.close == null) return null
    this._modals.add(m)
    this._syncGamepadCover()
    return () => {
      if (!this._modals.delete(m)) return
      m.close?.()
      this._syncGamepadCover()
    }
  }

  /** Put each open modal in the presentation now running (flat or headset). */
  private _representModals(): void {
    let i = 0
    for (const m of this._modals) {
      m.close?.()
      // Where the scene has no modal to give now (flat-ui="dom" back on the
      // monitor), the owner's own presentation, never nothing: a respawn
      // panel that vanished would leave you dead with no way back.
      m.close =
        this._presentModal(m.svg, m.height, i++) ?? m.fallback?.() ?? null
    }
  }

  /** One modal, in the presentation now running; returns its dispose. */
  private _presentModal(
    svg: SVGSVGElement,
    height: number,
    stack: number
  ): (() => void) | null {
    // A later modal sits a little nearer than the one before, so two open
    // at once (dead, then the tab hidden) do not share a plane: the newer is
    // drawn over the older and is what a press reaches.
    const nearer = Math.max(0.5, 1 - 0.06 * stack)
    const scene = this.scene
    if (scene == null) return null
    const xrFrames = this.xrFrames
    const xrCam = this.xrHelper?.baseExperience?.camera
    if (this.xrActive) {
      if (xrFrames == null || xrCam == null) return null
      // A panel you read and then press: in front, at arm's length and a
      // touch low, riding the body frame so a glance away does not take it
      // with you but turning round brings it along.
      // Measured from the eye: the body frame's origin is the rig's floor, and
      // the eye is the head's height above it, which is yours, not a standing
      // 1.6 m. Seated, a fixed 1.6 put the panel over your head, tilted to
      // face a point above it.
      const eyeY = xrCam.position.y > 0.3 ? xrCam.position.y : 1.6
      const panel = attachFramePanel(scene, xrCam, xrFrames.get('body'), {
        frame: 'body',
        anchor: {
          azimuthDeg: 0,
          elevationDeg: -10,
          distance: 1.1 * nearer,
          focus: [0, eyeY, 0],
        },
        presence: 'both',
        reveal: 'always',
        layer: 'modal',
        svg,
        width: 0.6,
        resolution: 1024,
      })
      const obs = scene.onBeforeRenderObservable.add(() => panel.update())
      return () => {
        scene.onBeforeRenderObservable.remove(obs)
        panel.dispose()
      }
    }
    const cam = scene.activeCamera
    if (cam == null || this._flatDom()) return null
    // Its own head-locked frame, because a scene with no other flat panels
    // has none.
    const frames = XrFrames.flat(scene, cam)
    const cnv = this.parts.canvas as HTMLCanvasElement
    const tall = Math.min(height, (cnv.clientHeight || 600) * 0.8)
    const panel = attachFramePanel(
      scene,
      cam as BABYLON.TargetCamera,
      frames.get('face'),
      {
        frame: 'face',
        anchor: { position: [0, 0, 1] },
        presence: 'both',
        reveal: 'always',
        layer: 'modal',
        svg,
        width: 0.3,
        resolution: 1024,
        screenAnchor: {
          left: '50%',
          top: '50%',
          anchor: 'center',
          height: tall,
          distance: nearer,
        },
      }
    )
    const obs = scene.onBeforeCameraRenderObservable.add((rendering) => {
      if (rendering !== cam) return
      frames.update(0)
      panel.update()
    })
    return () => {
      scene.onBeforeCameraRenderObservable.remove(obs)
      panel.dispose()
      frames.dispose()
    }
  }

  private _hidePausePanel(): void {
    this._pauseModal?.()
    this._pauseModal = null
    if (this._cameraWasAttached && this.camera != null) {
      this.camera.attachControl(this.parts.canvas as HTMLCanvasElement, false)
    }
    this._cameraWasAttached = false
  }

  /**
   * Watch the things that should pause a scene without being asked.
   *
   * BACKGROUNDING: a hidden tab's rAF is throttled to nothing anyway, so the
   * value here isn't saving work — it's that the player comes back to a held
   * frame and a panel rather than to a world that carried on without them.
   *
   * ORIENTATION: reported, not acted on. A phone rotating is sometimes a pause
   * ("I put it down") and sometimes nothing at all, and only the game knows
   * which — so this dispatches `orientation` with the new value and lets the
   * game decide. Note that TILT (deviceorientation) is a different, permissioned
   * API on iOS and deliberately not touched here.
   */
  private _watchPause(): void {
    if (this._pauseWatch != null) return
    const onVisibility = () => {
      if (isOff(this.pauseWhenHidden)) return
      // NOT while immersive: some browsers report the page hidden during an XR
      // session, and pausing there would fight the headset for no reason.
      if (document.hidden && !this.xrActive) this.pause('hidden')
    }
    /*
    ORIENTATION FROM THE VIEWPORT, not from `screen.orientation`.

    The Orientation API is the obvious source and the wrong one to DEPEND on —
    it is patchy on iOS, and a scene that only listens to it hears nothing on
    the devices that rotate most. But the viewport always tells the truth: when
    the device turns, `innerWidth`/`innerHeight` swap, and that is observable
    everywhere. `screen.orientation` is still read when present, for the finer
    `type`/`angle` a game may want — as extra detail, never as the trigger.

    Deliberately the VIEWPORT and not this element's own box: an embedded scene
    in a doc page is a small rectangle whose aspect says nothing about which way
    the phone is being held. Both are reported so a game can use either.
    */
    const orientationOf = () =>
      window.innerHeight >= window.innerWidth ? 'portrait' : 'landscape'
    let last = orientationOf()
    const onResize = () => {
      const now = orientationOf()
      if (now === last) return // a resize is not a rotation
      last = now
      this.dispatchEvent(
        new CustomEvent('orientation', {
          detail: {
            orientation: now,
            width: window.innerWidth,
            height: window.innerHeight,
            // Present on most platforms, absent on some iOS versions — which is
            // exactly why it isn't the trigger.
            type: screen.orientation?.type,
            angle: screen.orientation?.angle,
          },
          bubbles: true,
        })
      )
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('resize', onResize)
    // orientationchange fires before the viewport settles on some browsers, so
    // it's a second nudge into the same debounced check, not a separate path.
    window.addEventListener('orientationchange', onResize)
    this._pauseWatch = () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }

  /** Flip it. What the default panel's button and a pause key both want. */
  togglePause(): void {
    if (this._paused) this.resume()
    else this.pause('user')
  }

  sceneCreated: B3dCallback = noop
  update: B3dCallback = noop
  // Override the default WebXR setup entirely. When set (not noop) it runs
  // instead of the built-in Enter-VR button — call it to wire your own XR
  // experience (e.g. custom features, teleportation, controller models).
  setupXr: B3dCallback = noop
  /**
   * A dual-presence settings panel. Return the widgets to show; the SAME
   * definitions are drawn flat (in the scene, opened by the owl on the system
   * bar; `flat-ui="dom"` makes it an HTML overlay) AND in a headset (under the
   * system bar, which carries Exit VR). Both bind to the same reactive values,
   * so they stay in sync. Rebuilt each time it opens, so a hook that reads
   * async state is current; `refreshScenePanel()` updates an open one.
   */
  // Defaults to a function (not undefined) so the element creator recognises
  // it as a settable callback prop, like sceneCreated/update.
  scenePanel: (host: B3d) => Widget3d[] = () => []

  /**
   * The centred in-scene panel shown while paused. Return your own rows to
   * replace the default (a title and a Continue button) — a title screen, a
   * settings menu, a "you were away" summary.
   *
   * `resume` is passed in rather than left to be found: whatever you build must
   * be able to let the player back in, and a pause panel with no way out is the
   * failure mode this whole feature exists to avoid.
   *
   * Defaults to a function (not undefined) so the element creator treats it as
   * a settable prop, like `scenePanel`.
   */
  pausePanel: (host: B3d, resume: () => void) => Widget3d[] | null = () => null

  /*
  Body-anchored XR panels: pinned to a reference frame (default `body`) and
  revealed by looking toward them. The named PINS (`waist`, `left-shoulder`,
  `right-shoulder`, `overhead`, `wrist`; see b3d-panel) are places for a
  game's own panels; none ship filled. The placeholder Inventory, Journal,
  Quick Access and Menu that used to sit on them went when the inventory
  moved to the system bar (Tonio: "They are pin locations"). Declared
  `<tosi-b3d-panel>` children are the set; ones with an `icon` go on
  the system bar instead. Like scenePanel, a function so the element creator
  treats it as a prop.
  */
  bodyPanels: (host: B3d) => FramePanelSpec[] = (host) => {
    const panels: FramePanelSpec[] = Array.from(
      host.querySelectorAll('tosi-b3d-panel')
    )
      .map((el) =>
        (el as unknown as { toSpec?: () => FramePanelSpec }).toSpec?.()
      )
      .filter((s): s is FramePanelSpec => s != null)
    // The face crosshair is a PIN TARGET for aim-tracking UX — not something that
    // belongs on screen everywhere (no more than the quick-access bar does). Opt in
    // with `xr-reticle="on"` (e.g. when a weapon tracks it); default hides it.
    if (this.xrReticle === 'on') {
      panels.push({
        frame: 'face',
        anchor: { position: [0, 0, 2], focus: [0, 0, 0] },
        reveal: 'always',
        blend: 'add',
        view: 'first', // crosshair only when looking through your own eyes
        // Built in: a `/reticle.svg` url 404'd on any site but ours.
        svg: new DOMParser().parseFromString(reticleSvg, 'image/svg+xml')
          .documentElement as unknown as SVGSVGElement,
        width: 0.24,
      })
    }
    return panels
  }

  private lastRender = 0

  /** Seconds of wall clock since the previous rendered frame (clamped to

   * 100ms so a backgrounded tab can't teleport the sim). Use this — NOT

   * `engine.getDeltaTime()` — for anything advancing state inside a scene

   * observer; see the note in `_update`. */

  /** SIM seconds since the last rendered frame — scaled, and 0 while paused. */
  frameDelta = 1 / 60
  private _realDelta = 1 / 60
  private _simElapsed = 0
  private _realElapsed = 0
  private _frameCount = 0

  /**
   * Everything a per-frame callback should need, in one object.
   *
   * Tonio's framing: *"make it easy to do the right thing."* A bare `dt` makes
   * you go and find the rest — which clock am I on, is the world paused, how
   * long has this actually been running — and each caller answers it slightly
   * differently. Handing over the package means the correct choice is the one
   * already in your hand.
   *
   * Reach for `dt` unless you know why not; `realDt` is for the things that
   * must not slow down when the world does.
   */
  /**
   * The scene's wind right now, gusts included. See [[wind]].
   *
   * Zero speed means "no scene wind", and children fall back to their own
   * attributes — which is what makes this additive rather than a migration.
   */
  get wind(): Wind {
    const speed = Number((this as any).windSpeed ?? 0) || 0
    if (speed === 0) return { ...NO_WIND }
    const base = windFromPolar(
      speed,
      Number((this as any).windBearingDeg ?? 0) || 0
    )
    const gust = Number((this as any).windGust ?? 0) || 0
    return gust > 0
      ? gustAt(base, this.frameInfo().elapsed, { amount: gust })
      : base
  }

  private _weatherCells: WeatherCell[] = []
  private _weatherDebugOff: (() => void) | null = null

  /**
   * **The weather at (x, z), now** — the scene's base (its wind, gusts
   * included) plus every weather cell that reaches the point. The one
   * question every weather consumer asks; see [[weather]] and
   * WEATHER-DESIGN.md. Consumers read it at THEIR position, which is what
   * makes a lee calm and a storm local.
   */
  weatherAt(x: number, z: number): WeatherSample {
    return composeWeather(
      { ...CALM, wind: this.wind },
      this._weatherCells,
      x,
      z
    )
  }

  /** The weather cells in force (read-only; add with `addWeatherCell`). */
  get weatherCells(): readonly WeatherCell[] {
    return this._weatherCells
  }

  /** The weather where the viewer is (the active camera). */
  weatherHere(): WeatherSample {
    const c = this.scene?.activeCamera?.globalPosition
    return c ? this.weatherAt(c.x, c.z) : { ...CALM, wind: this.wind }
  }

  /** A region whose weather differs (`<tosi-b3d-weather-cell>` uses this).
   * The cell is read live, so moving it moves its weather. Returns a remover. */
  addWeatherCell(cell: WeatherCell): () => void {
    this._weatherCells.push(cell)
    if (this._weatherDebugOff == null) {
      this._weatherDebugOff = this.addDebugSource({
        name: 'Weather',
        lines: () => {
          const w = this.weatherHere()
          return [
            `wind ${Math.hypot(w.wind.x, w.wind.z).toFixed(1)}m/s cover=${
              w.coverage == null ? '-' : w.coverage.toFixed(2)
            } rain=${w.precipitation.toFixed(2)} storm=${w.storminess.toFixed(
              2
            )} cells=${this._weatherCells.length}`,
          ]
        },
      })
    }
    return () => {
      const i = this._weatherCells.indexOf(cell)
      if (i >= 0) this._weatherCells.splice(i, 1)
    }
  }

  frameInfo(): FrameInfo {
    return {
      dt: this.frameDelta,
      realDt: this._realDelta,
      elapsed: this._simElapsed,
      realElapsed: this._realElapsed,
      scale: Math.max(0, Number((this as any).timeScale ?? 1) || 0),
      paused: this._paused || this._frozen,
      frame: this._frameCount,
    }
  }
  private sceneListeners: SceneAdditionHandler[] = []
  private pastAdditions: SceneAdditions[] = []
  private _sceneReady = false
  // Pull-model readiness: B3dChild components call whenReady() from their own
  // connectedCallback to insert themselves once the scene is up. Runs the callback
  // immediately if the scene is already ready, else queues it for the flush below.
  private _readyQueue: Array<() => void> = []
  // Durable — see whenDisposed. NOT cleared by teardown, so a handler
  // registered once still fires for the scene after next.
  private _disposeHandlers: Array<() => void> = []
  private _libraries = new Map<string, Set<any>>()

  /**
   * Run `cb` when the scene is ready — now if it already is, else on scene-ready.
   *
   * **It is a promise, not a hope.** A queued callback survives a teardown and
   * fires against the NEXT scene, because the alternative is the failure this
   * whole area specialises in: register, have the scene torn down before it was
   * ready, and your callback is dropped with no error and nothing to observe.
   * If the element is never re-added the callback is simply garbage along with
   * it, which costs nothing.
   */
  whenReady(cb: () => void): void {
    if (this._sceneReady) cb()
    else this._readyQueue.push(cb)
  }

  /**
   * Run `cb` when the scene is torn down — the other half of `whenReady`, and
   * the half that did not exist. Returns an unsubscribe.
   *
   * Anything holding a reference INTO the scene (a mesh, a material, an
   * observer, a timer closing over one) needs this: after teardown those
   * references are to a disposed scene, and Babylon's failure mode there is a
   * black material that still reports `isReady()` — silent, not loud.
   *
   * Subscriptions are durable across a rebuild; scene STATE is not. So a
   * handler registered once keeps working for every subsequent scene, and does
   * not need re-registering.
   */
  whenDisposed(cb: () => void): () => void {
    this._disposeHandlers.push(cb)
    return () => {
      const i = this._disposeHandlers.indexOf(cb)
      if (i > -1) this._disposeHandlers.splice(i, 1)
    }
  }

  addSceneListener(callback: SceneAdditionHandler): void {
    this.sceneListeners.push(callback)
    for (const additions of this.pastAdditions) {
      callback(additions)
    }
  }

  removeSceneListener(callback: SceneAdditionHandler): void {
    const idx = this.sceneListeners.indexOf(callback)
    if (idx > -1) {
      this.sceneListeners.splice(idx, 1)
    }
  }

  register(additions: SceneAdditions): void {
    this.pastAdditions.push(additions)
    for (const callback of this.sceneListeners) {
      callback(additions)
    }
  }

  // --- Floating origin: keeping the whole world shiftable, not just the player ---
  //
  // When the terrain rebases (see B3dTerrain.resetOrigin) the world is moved so the
  // viewpoint returns near the origin. EVERYTHING that carries a world position must
  // move by the same amount or it drifts relative to the terrain. Two ways in:
  //
  //  - registerWorldRoot(node): the entity's world position lives ENTIRELY on the
  //    node (inert targets, props, other vehicles). We move the node.
  //  - addOriginListener(cb): the entity also holds world coordinates in JS (a
  //    projectile integrating its own position, remembered target positions, AI
  //    memory). It gets (dx, dz) and fixes ITSELF — node AND JS state. Such an
  //    entity must NOT also registerWorldRoot (that would shift its node twice).
  private _worldRoots = new Set<BABYLON.TransformNode>()
  private _originShiftListeners: Array<(dx: number, dz: number) => void> = []

  // Everything detectable on radar this scene (targets, the player's own missiles,
  // waypoints). A radar platform — the aircraft HUD — enumerates these each frame.
  // Blips self-register (b3d-radar-blip on connect; spawnProjectile for a missile)
  // and unregister on dispose. Position is pulled live, so movers just work.
  private _radarBlips = new Set<RadarBlip>()

  // The scene's combat state (pure, deterministic; see destroyable.ts). Combat
  // components (b3d-destroyable/warhead/launcher) find it via findB3dOwner and
  // share it; the render loop advances it (regen + chain reactions) each frame.
  readonly combat = new CombatWorld()

  // Whether the flat gear panel's click handler is wired (idempotent setup).
  private _scenePanelWired = false

  // NPC nameplates, live in flat AND XR. Keyed by biped element; a cached list is
  // iterated per frame (no per-frame allocation), and a throttled scan adds/removes
  // as bipeds' GLBs load or leave.
  private _nameplates = new Map<Element, Nameplate>()
  private _nameplateList: Nameplate[] = []
  private _nameplateScan = 0

  /**
   * CAVITIES — open air that lives INSIDE the ground (a bore, a cavern).
   *
   * Anything that navigates by "terrain is a heightfield below me" needs to
   * know when that stopped being true: a flat ground-plane floor, a downward
   * ray, a landing gate. A cavity predicate is how a volumetric patch tells
   * the rest of the engine "here, the rule is suspended" without either side
   * knowing about the other — the same shape as the origin listeners above,
   * and for the same reason.
   */
  addCavity(predicate: (x: number, y: number, z: number) => boolean): void {
    this._cavities.push(predicate)
  }

  removeCavity(predicate: (x: number, y: number, z: number) => boolean): void {
    const i = this._cavities.indexOf(predicate)
    if (i >= 0) this._cavities.splice(i, 1)
  }

  /** Is this world point inside open air within the ground? False when no
   * patch has claimed it — so a scene with no cavities pays one array-length
   * check and behaves exactly as it always did. */
  insideCavity(x: number, y: number, z: number): boolean {
    for (let i = 0; i < this._cavities.length; i++) {
      if (this._cavities[i](x, y, z)) return true
    }
    return false
  }

  private _cavities: ((x: number, y: number, z: number) => boolean)[] = []

  registerWorldRoot(node: BABYLON.TransformNode): void {
    this._worldRoots.add(node)
  }

  unregisterWorldRoot(node: BABYLON.TransformNode): void {
    this._worldRoots.delete(node)
  }

  addOriginListener(callback: (dx: number, dz: number) => void): void {
    this._originShiftListeners.push(callback)
  }

  removeOriginListener(callback: (dx: number, dz: number) => void): void {
    const idx = this._originShiftListeners.indexOf(callback)
    if (idx > -1) this._originShiftListeners.splice(idx, 1)
  }

  registerRadarBlip(blip: RadarBlip): void {
    this._radarBlips.add(blip)
  }

  unregisterRadarBlip(blip: RadarBlip): void {
    this._radarBlips.delete(blip)
  }

  /** Every radar-detectable blip in the scene (targets, own missiles, waypoints). */
  get radarBlips(): ReadonlySet<RadarBlip> {
    return this._radarBlips
  }

  /**
   * Move every world-space thing by (-dx, -dz) so the viewpoint returns near the
   * origin with no visible motion. Called by the terrain AFTER it has rebased its
   * own tiles by (dx, dz). Shifts: the camera CARRIER (the piloted entity if one is
   * driven — the chase rig re-derives from it each frame, so shifting the rig would
   * be overwritten; else the camera's parent; else the camera), every registered
   * world root, and every addOriginListener listener (which fixes its own node + JS).
   * Skybox/water are viewer/origin-centred and intentionally NOT shifted.
   */
  shiftOrigin(dx: number, dz: number): void {
    if (dx === 0 && dz === 0) return
    const shifted = new Set<any>()
    const move = (node: any) => {
      if (node == null || shifted.has(node)) return
      node.position.x -= dx
      node.position.z -= dz
      shifted.add(node)
    }
    const camera = this.scene?.activeCamera
    const focused = (this.querySelector('tosi-b3d-input-focus') as any)?.focused
    const piloted =
      (focused?.getCameraTarget?.() as BABYLON.TransformNode | null) ?? null
    const carrier = piloted ?? (camera?.parent as any) ?? camera
    move(carrier)
    for (const root of this._worldRoots) move(root)
    for (const cb of this._originShiftListeners) cb(dx, dz)
    // Instrument the floating-origin desync hypothesis (VR pass 2, 'origin'
    // tag): the phantom "collide with terrain nowhere near you" fits the piloted
    // aircraft NOT being what shiftOrigin moves — in chase view the carrier can
    // be the chase rig, leaving the plane at pre-shift coords while the tiles
    // jump. Record exactly what was moved so the next pass reads the truth
    // instead of us guessing. `carrierIsPiloted:false` while flying = confirmed.
    if (this._debugCapture.has('origin')) {
      this.logDebug('origin', {
        kind: 'shiftOrigin',
        dx,
        dz,
        carrier: (carrier as any)?.name ?? null,
        piloted: (piloted as any)?.name ?? null,
        carrierIsPiloted: carrier === piloted && piloted != null,
        carrierY: (carrier as any)?.position?.y ?? null,
        worldRoots: this._worldRoots.size,
        view: focused?.cameraView ?? null,
      })
    }
  }

  registerLibrary(type: string, library: any): void {
    if (!this._libraries.has(type)) {
      this._libraries.set(type, new Set())
    }
    this._libraries.get(type)!.add(library)
    this.dispatchEvent(
      new CustomEvent('library-changed', { detail: { type, library } })
    )
  }

  unregisterLibrary(type: string, library: any): void {
    const set = this._libraries.get(type)
    if (set) {
      set.delete(library)
      if (set.size === 0) this._libraries.delete(type)
    }
    this.dispatchEvent(
      new CustomEvent('library-changed', { detail: { type, library } })
    )
  }

  getLibrary(type: string): any | null {
    const set = this._libraries.get(type)
    if (!set || set.size === 0) return null
    return set.values().next().value
  }

  getLibraries(type: string): any[] {
    const set = this._libraries.get(type)
    return set ? [...set] : []
  }

  setActiveCamera(
    camera: BABYLON.Camera,
    options: { attach?: boolean; preventDefault?: boolean } = {}
  ): void {
    const { attach = true, preventDefault = false } = options
    const cnv = this.parts.canvas as HTMLCanvasElement
    if (this.camera != null) {
      this.camera.detachControl()
    }
    this.camera = camera
    this.scene.activeCamera = camera
    if (attach) {
      camera.attachControl(cnv, preventDefault)
    }
    // Every orbit camera gets the house view controls (shift-drag and a
    // trackpad's two-finger drag pan): a demo that built its own camera was
    // left with Babylon's, and could not pan on a laptop.
    if (camera instanceof BABYLON.ArcRotateCamera) touchOrbit(camera)
  }

  /**
   * Switch the **gameplay viewpoint** — chase↔cockpit, a death spectator orbit, climbing into a
   * vehicle. This is the ONE sanctioned way to change what the player looks through, and the ONE
   * place that knows the XR rule:
   *
   * > **In an XR session the WebXR camera OWNS the view. Never swap `scene.activeCamera` — that
   * > steals it from the headset and blanks the display.** Instead the piloted entity moves the
   * > XR *rig* (it parents the rig to itself), so the head rides along.
   *
   * So: flat → swap to `camera` (returns `true`); XR → **no-op on the camera** (returns `false`,
   * so the caller can skip building a flat-only camera and let the rig handle the view). Every
   * gameplay camera change must route through here rather than `setActiveCamera` /
   * `scene.activeCamera` directly — that's what stops "it breaks in VR" from recurring
   * (it bit the chase camera, then the death orbit; centralising the rule is the fix).
   */
  setGameplayCamera(
    camera: BABYLON.Camera,
    options: { attach?: boolean; preventDefault?: boolean } = {}
  ): boolean {
    if (this.xrActive) return false
    this.setActiveCamera(camera, options)
    return true
  }

  /*
  VIEWER-RELATIVE PANELS IN THE FLAT VIEW (tosijs-3d#81).

  `<tosi-b3d-panel presence="both">` gets the same frame vocabulary flat as in a
  headset — the frames just follow the flat camera (`XrFrames.flat`). ONE panel
  in both, rather than a flat overlay and a VR panel to keep in step.

  Rebuilt when the declared panels or the active camera change (checked every
  30 ticks — a panel is not something that changes per frame), and dropped the
  moment XR builds its own frames, so the two sets never coexist.
  */
  private _flatFrames: XrFrames | null = null
  private _flatPanels: Array<{
    update: (ctx?: { firstPerson?: boolean }) => void
    dispose: () => void
    setSvg: (next: SVGSVGElement) => void
    /** What it was built from, less its SVG; equal means keep it. */
    key: string
    svg: SVGSVGElement | undefined
  }> = []
  /** The camera the flat panels' frames follow (a new one rebuilds them all). */
  private _flatCam = -1
  private _flatSig = ''
  private _flatCheckIn = 0
  private _flatHandWarned = new Set<string>()
  private _flatPoseObs: BABYLON.Nullable<BABYLON.Observer<BABYLON.Camera>> =
    null

  /*
  THE CORNER TOOLBAR, IN THE SCENE (#3307). It mirrors the HTML pair (now
  three: settings, Enter VR, full screen) rather than redoing them: what shows,
  what is enabled, and a press is that button's own click, so VR entry and full
  screen keep one definition and still run inside the user's press. The HTML
  lozenge is hidden while this is up; `flat-ui="dom"` keeps it instead.
  */
  private _sceneBar: SystemBar | null = null
  private _sceneBarButtons: HTMLButtonElement[] = []
  private _sceneBarVersion = 0

  /*
  SYSTEM PANELS: a `<tosi-b3d-panel>` with an `icon` (or `preset="system-bar"`) is not pinned to
  the rig. It gets a button on the system bar (its `icon`) and opens UNDER
  the bar, flat and in a headset: the inventory is one. One is open at a
  time, the settings panel included, unless it says `exclusive="off"`: an
  app's main chrome (a tool palette, a properties panel) is several panels
  used together, which stay open beside each other and beside whichever
  exclusive panel is up. `open` opens one when the scene starts, and one
  that is open when a headset session starts or ends opens again on the
  other side. Tonio: "it should be able to extend the
  system bar in flat 3d and VR so it's just one UI with slightly different
  arrangement."

  The content is asked with a cancelable `panelopen` before it is shown and
  when it is put away: content that opens and closes itself (the
  inventory's dock) does, and a `panelfocus` with `focus: false` from it
  (its own ×) puts the panel away.
  */
  /*
  KEYED BY THE ELEMENT, not by its content (tosijs-3d#111). A host whose rows
  change rebuilds its `panel3d` and gets a new svg; keyed by the svg, that
  was a new button, a lost spot and a stale open panel. The element is what
  the host declared, so its button, spot and open state stay its own while
  `el.svg = next` swaps what it shows.
  */
  private _systemEntries(): { el: Element; spec: FramePanelSpec }[] {
    const out: { el: Element; spec: FramePanelSpec }[] = []
    for (const el of Array.from(this.querySelectorAll('tosi-b3d-panel'))) {
      const s = (el as unknown as { toSpec?: () => FramePanelSpec }).toSpec?.()
      if (s == null || s.anchor !== 'system-bar') continue
      // Markup alone (no JS `svg`) gets the title card a pinned panel gets,
      // one per element so its button stays the same button.
      let svg = s.svg ?? this._systemCards.get(el)
      if (svg == null) {
        svg = placeholderPanelSvg(s.title || s.icon || 'Panel')
        this._systemCards.set(el, svg)
      }
      out.push({ el, spec: { ...s, svg } })
    }
    return out
  }
  private _systemCards = new WeakMap<Element, SVGSVGElement>()
  private _systemButtonOf = new WeakMap<Element, HTMLButtonElement>()
  /** One HTML button per system panel, mirrored by both system bars. */
  private _systemButtons(): HTMLButtonElement[] {
    const icons = svgIcons as unknown as Record<string, () => Element>
    return this._systemEntries().map(({ el: svg, spec }) => {
      let b = this._systemButtonOf.get(svg)
      if (b == null) {
        b = button(
          { type: 'button', title: spec.title || spec.icon || 'Panel' },
          icons[spec.icon ?? '']?.() ?? svgIcons.square()
        ) as HTMLButtonElement
        b.addEventListener('click', () => this._toggleSystemPanel(svg, true))
        // Held on the system bar: back where it started, and open.
        b.addEventListener('systemhold', () => {
          this._systemSpots.delete(svg)
          this._systemXrSpots.delete(svg)
          this._closeSystemPanel(svg)
          this._openSystemPanel(svg, true)
        })
        this._systemButtonOf.set(svg, b)
      }
      return b
    })
  }
  /** The open system panels, by their `<tosi-b3d-panel>`. */
  private _sysPanels = new Map<
    Element,
    {
      content: SVGSVGElement
      panel: {
        update: () => void
        dispose: () => void
        toClient?: (x: number, y: number) => { x: number; y: number } | null
        setSvg: (next: SVGSVGElement) => void
      }
      frames: XrFrames | null
      /** Its anchor changed (flat): whether the new one fixes the height. */
      reanchored?: (fixedHeight: boolean) => void
      /** New content under the same title bar, in place (framed only). */
      swap: (next: SVGSVGElement) => void
      obs: BABYLON.Nullable<BABYLON.Observer<BABYLON.Camera>>
      off: () => void
      exclusive: boolean
      /** Opened by its button, not by code. */
      byUser: boolean
    }
  >()
  /** System panels already opened by their `open` attribute (once each). */
  private _sysOpened = new WeakSet<Element>()
  /** The headset's settings panel closer, while a session has one. */
  private _closeXrSettings: () => void = noop
  private _toggleSystemPanel(el: Element, byUser = false): void {
    if (this._sysPanels.has(el)) this._closeSystemPanel(el)
    else this._openSystemPanel(el, byUser)
  }
  /*
  FROM CODE (#3436, Manta's objectives list): `panel.show()`, `hide()`,
  `toggle()` and `attention()` on the `<tosi-b3d-panel>` come here. Before
  the scene is up, `show()` is the `open` attribute (it opens at start).
  */
  /** Is this system panel open? (`panel.isOpen`) */
  _systemPanelOpen(el: Element): boolean {
    return this._sysPanels.has(el)
  }
  /** Open or close a system panel (`panel.show()` / `hide()`). */
  _setSystemPanel(el: Element, open: boolean): void {
    if (this.scene == null || !this._sysOpened.has(el)) {
      // Not started yet: what it does at start.
      ;(el as unknown as { open: boolean }).open = open
      if (this.scene == null) return
      this._sysOpened.add(el)
    }
    if (open) this._openSystemPanel(el)
    else this._closeSystemPanel(el)
  }
  /**
   * Blink a system panel's button on both bars, for `ms` (`panel.attention()`):
   * something in it changed (an objective met) while it was closed.
   */
  _systemAttention(el: Element, ms = 1600): void {
    const b = this._systemButtonOf.get(el)
    if (b == null) return
    const prev = this._attention.get(b)
    if (prev != null) clearInterval(prev)
    const BLINK = 200
    let left = Math.max(1, Math.round(ms / BLINK))
    b.setAttribute('data-attention', '')
    const t = setInterval(() => {
      if (--left <= 0) {
        clearInterval(t)
        this._attention.delete(b)
        b.removeAttribute('data-attention')
      } else b.toggleAttribute('data-attention')
    }, BLINK)
    this._attention.set(b, t)
  }
  private _attention = new Map<
    HTMLButtonElement,
    ReturnType<typeof setInterval>
  >()
  /*
  EVERY OPEN AND CLOSE IS REPORTED (#3436): `paneltoggle` on the panel, and
  `systempanels` on the scene with the whole picture, so the game decides
  what it means (pause while one the person opened is up:
  `detail.open.some((p) => p.byUser)`). A headset session starting or ending
  closes and reopens the shared ones; that is reported once, when it is over.
  */
  private _panelToggled(
    el: Element | null,
    open: boolean,
    byUser = false
  ): void {
    if (this._holdPanelEvents) return
    if (el != null)
      el.dispatchEvent(
        new CustomEvent('paneltoggle', { detail: { open, byUser } })
      )
    this.dispatchEvent(
      new CustomEvent('systempanels', {
        detail: {
          panel: el,
          shown: open,
          byUser,
          open: [...this._sysPanels].map(([panel, p]) => ({
            panel,
            byUser: p.byUser,
          })),
        },
        bubbles: true,
        composed: true,
      })
    )
  }
  /**
   * New content on a system panel's element: if it is open, show the new
   * content in the same place, IN PLACE: under its title bar, or (with
   * `chrome="none"`) on the same plane. Never closed and reopened, so a press
   * mid-swap is not lost and nothing blinks.
   */
  private _swapSystemContent = (e: Event): void => {
    const el = e.target as Element
    const open = this._sysPanels.get(el)
    if (open == null) return
    const next = this._systemEntries().find((x) => x.el === el)?.spec.svg
    if (next == null || next === open.content) return
    open.swap(next)
  }
  /**
   * A new `screenAnchor` on a system panel's element: an open one that no one
   * has dragged moves there now (a host re-stacking its panels as they grow);
   * a dragged one stays where it was put, until its button is held.
   */
  private _reanchorSystemPanel = (e: Event): void => {
    const el = e.target as Element
    const open = this._sysPanels.get(el)
    const spot = this._systemSpots.get(el)
    if (open == null || spot == null || screenAnchorDragged(spot)) return
    const next = this._systemEntries().find((x) => x.el === el)?.spec
      .screenAnchor
    for (const k of Object.keys(spot)) delete (spot as any)[k]
    Object.assign(
      spot,
      next
        ? { distance: 0.9, ...next }
        : { left: 8, top: UNDER_BAR, distance: 0.9 }
    )
    open.reanchored?.(next?.height != null)
  }
  /**
   * Where a point of a system panel's content (its own viewBox units, what
   * its `handlePointer` takes) is on the page, flat; null when closed or in a
   * headset (#113).
   */
  _systemContentToClient(
    el: Element,
    x: number,
    y: number
  ): { x: number; y: number } | null {
    const p = this._sysPanels.get(el)
    if (p?.panel.toClient == null) return null
    // Inside the title-bar frame the content sits below the bar (titled3d
    // places it with x/y); framed or not, this is where it is.
    const dx = Number(p.content.getAttribute('x')) || 0
    const dy = Number(p.content.getAttribute('y')) || 0
    return p.panel.toClient(x + dx, y + dy)
  }
  /**
   * `byUser`: opened by its button (the person asked to look at it), not by
   * code. That decides whether the glass pad stands aside and whether
   * `systempanels` counts it (#3436).
   */
  private _openSystemPanel(key: Element, byUser = false): void {
    const scene = this.scene
    if (scene == null) return
    if (this._sysPanels.has(key)) return
    const spec = this._systemEntries().find((x) => x.el === key)?.spec
    if (spec?.svg == null) return
    const content = spec.svg
    const exclusive = spec?.exclusive !== false
    // One at a time: the settings panel and the other exclusive ones go.
    if (exclusive) {
      this._closeScenePanel()
      this._closeXrSettings()
      this._closeSystemPanels(true)
    }
    /*
    THE SETTINGS PANEL'S TITLE BAR, by default: move, its title, ×. Tonio:
    "I think the way the scene panel works should just be the default unless
    overridden" (`chrome="none"` on the panel). `framed` tells content that
    draws its own close and outline (the dock) to leave them to the frame.
    */
    const framed = spec?.chrome !== 'none'
    // Open the content FIRST, so it is placed at the size it will be.
    content.dispatchEvent(
      new CustomEvent('panelopen', {
        detail: { open: true, framed },
        cancelable: true,
      })
    )
    const titled = framed
      ? titled3d(content, {
          title: spec?.title,
          handleClose: () => this._closeSystemPanel(key),
        })
      : null
    const svg = titled ?? content
    const vb = svg.viewBox.baseVal
    let panel: {
      update: () => void
      dispose: () => void
      toClient?: (x: number, y: number) => { x: number; y: number } | null
      setSvg: (next: SVGSVGElement) => void
    }
    let reanchored: ((fixed: boolean) => void) | undefined
    let frames: XrFrames | null = null
    let obs: BABYLON.Nullable<BABYLON.Observer<BABYLON.Camera>> = null
    const xrCam = this.xrHelper?.baseExperience?.camera
    if (this.xrActive && this.xrFrames != null && xrCam != null) {
      // Under the headset's system bar, about the settings panel's scale.
      const K = 0.0018 // metres per viewBox unit
      const h = (vb.height || 400) * K
      const half = (Math.atan2(h / 2, XR_BAR_DISTANCE) * 180) / Math.PI
      // Its own anchor, kept: a drag by its title bar writes the angles, so
      // it reopens where you left it (holding its button resets it).
      let seat = this._systemXrSpots.get(key)
      if (seat == null) {
        seat = xrSeatFor(spec?.screenAnchor, (vb.width || 400) * K, half, K)
        this._systemXrSpots.set(key, seat)
      }
      panel = attachFramePanel(scene, xrCam, this.xrFrames.get('eye'), {
        frame: 'eye',
        anchor: seat,
        reveal: 'always',
        layer: 'ui',
        svg,
        width: (vb.width || 400) * K,
        resolution: 1024,
        focusable: false,
      })
    } else {
      const cam = scene.activeCamera
      if (cam == null) return
      const f = XrFrames.flat(scene, cam)
      frames = f
      // Under the corner bar, one viewBox unit per CSS pixel, and no taller
      // than the screen (the content grows and shrinks, so every frame).
      // Where you dragged it last time, as the settings panel does (holding
      // its button on the bar puts it back).
      // The first time, its own `screenAnchor` if it declares one.
      let spot = this._systemSpots.get(key)
      // Until a person drags it, its spot is its CURRENT anchor (a host that
      // re-stacks its panels moves this one too); once dragged, theirs.
      if (spot != null && !screenAnchorDragged(spot)) spot = undefined
      if (spot == null) {
        spot = spec?.screenAnchor
          ? { distance: 0.9, ...spec.screenAnchor }
          : { left: 8, top: UNDER_BAR, distance: 0.9 }
        this._systemSpots.set(key, spot)
      }
      let fixedHeight = spec?.screenAnchor?.height != null
      reanchored = (fixed) => {
        fixedHeight = fixed
      }
      const fitHeight = () => {
        if (fixedHeight) return
        const top = typeof spot!.top === 'number' ? spot!.top : UNDER_BAR
        const room = (this.parts.canvas?.clientHeight ?? 600) - top - 8
        spot!.height = Math.max(
          80,
          Math.min(svg.viewBox.baseVal.height || 400, room)
        )
      }
      fitHeight()
      const p = attachFramePanel(
        scene,
        cam as BABYLON.TargetCamera,
        f.get('face'),
        {
          frame: 'face',
          anchor: { position: [0, 0, 1] },
          presence: 'both',
          reveal: 'always',
          layer: 'ui',
          svg,
          width: 0.3,
          resolution: 1024,
          focusable: false,
          screenAnchor: spot,
        }
      )
      panel = p
      obs = scene.onBeforeCameraRenderObservable.add((rendering) => {
        if (rendering !== cam) return
        fitHeight()
        f.update(0)
        p.update()
      })
    }
    const onFocus = (e: Event) => {
      if ((e as CustomEvent<{ focus?: boolean }>).detail?.focus === false)
        this._closeSystemPanel(key)
    }
    content.addEventListener('panelfocus', onFocus)
    const entry = {
      content,
      panel,
      reanchored,
      frames,
      obs,
      exclusive,
      byUser,
      swap: (next: SVGSVGElement) => {
        // The same open/close a reopen would send, around the swap.
        const was = entry.content
        was.removeEventListener('panelfocus', onFocus)
        was.dispatchEvent(
          new CustomEvent('panelopen', {
            detail: { open: false },
            cancelable: true,
          })
        )
        next.dispatchEvent(
          new CustomEvent('panelopen', {
            detail: { open: true, framed },
            cancelable: true,
          })
        )
        if (titled != null) titled.setContent(next)
        else panel.setSvg(next)
        next.addEventListener('panelfocus', onFocus)
        entry.content = next
      },
      off: () => {
        entry.content.removeEventListener('panelfocus', onFocus)
        titled?.dispose()
      },
    }
    this._sysPanels.set(key, entry)
    this._syncGamepadCover()
    this._panelToggled(key, true, byUser)
  }
  /** Where each system panel was dragged to, flat. */
  private _systemSpots = new WeakMap<Element, ScreenAnchor>()
  /** …and in a headset. */
  private _systemXrSpots = new WeakMap<
    Element,
    { azimuthDeg: number; elevationDeg: number; distance: number }
  >()
  /** `flat-ui="dom"`; anything but 'scene' or 'dom' warns once and is 'scene'. */
  private _flatDom(): boolean {
    const v = (this as any).flatUi
    if (v !== 'scene' && v !== 'dom' && !this._flatUiWarned) {
      this._flatUiWarned = true
      console.warn(
        `tosi-b3d: flat-ui="${v}" is not 'scene' or 'dom'; drawing it in the scene`
      )
    }
    return v === 'dom'
  }
  private _flatUiWarned = false
  /** Close every system panel, or only the exclusive ones. */
  private _closeSystemPanels(exclusiveOnly = false): void {
    for (const [svg, p] of [...this._sysPanels])
      if (!exclusiveOnly || p.exclusive) this._closeSystemPanel(svg)
  }
  /**
   * Close them all for a change of presentation (a headset session starting
   * or ending), and open the non-exclusive ones again once the other side is
   * up: an app's chrome follows you into the headset and back.
   */
  private _carrySystemPanels(toXr: boolean): void {
    const shared = [...this._sysPanels]
      .filter(([, p]) => !p.exclusive)
      .map(([svg, p]) => [svg, p.byUser] as const)
    // Not a close and an open to the game: one change, once it is over.
    this._holdPanelEvents = true
    this._closeSystemPanels()
    const scene = this.scene
    const settle = () => {
      this._holdPanelEvents = false
      this._syncGamepadCover()
      this._panelToggled(null, false)
    }
    if (scene == null || shared.length === 0) return settle()
    const obs = scene.onBeforeRenderObservable.add(() => {
      // Wait for the other side: the headset's frames, or the flat camera.
      const xr = this.xrActive && this.xrFrames != null
      const flat = !this.xrActive && this.xrFrames == null
      if ((toXr ? !xr : !flat) || scene.activeCamera == null) return
      scene.onBeforeRenderObservable.remove(obs)
      for (const [svg, byUser] of shared) this._openSystemPanel(svg, byUser)
      settle()
    })
  }
  private _closeSystemPanel(key: Element): void {
    const p = this._sysPanels.get(key)
    if (p == null) return
    this._sysPanels.delete(key)
    p.content.dispatchEvent(
      new CustomEvent('panelopen', {
        detail: { open: false },
        cancelable: true,
      })
    )
    if (p.obs != null) this.scene?.onBeforeCameraRenderObservable.remove(p.obs)
    p.panel.dispose()
    p.frames?.dispose()
    // Last: the frame gives the content back once its plane is gone.
    p.off()
    this._syncGamepadCover()
    this._panelToggled(key, false, p.byUser)
  }
  private _sceneToolbarSpec(): FramePanelSpec | null {
    const lozenge = this.parts.sceneToolbar as HTMLElement | undefined
    /*
    The system panels' buttons sit in the HTML lozenge too, after the owl, so
    `flat-ui="dom"` reaches them as well (they open the same in-scene panel;
    an inventory has no HTML presentation). Re-seated here because a button
    added to the shadow root later does not survive a re-render.
    */
    const gearEl = this.parts.scenePanelGear as HTMLElement | undefined
    let after: Element | undefined = gearEl
    for (const b of this._systemButtons()) {
      if (
        lozenge != null &&
        after != null &&
        b.previousElementSibling !== after
      )
        after.after(b)
      // Guarded: even a no-op class write is a mutation, and the bar
      // watches its buttons for those (a rebuild twice a second).
      if (!b.classList.contains('lozenge-button'))
        b.classList.add('lozenge-button')
      after = b
    }
    if (this._flatDom() || lozenge == null) {
      if (lozenge) lozenge.style.visibility = ''
      return null
    }
    const part = (p: string) => this.parts[p] as HTMLButtonElement | undefined
    const buttons = [
      part('scenePanelGear'),
      ...this._systemButtons(),
      part('enterVrButton'),
      part('fullscreenButton'),
    ].filter((b): b is HTMLButtonElement => b != null)
    // Rebuilt when the system panels change (one arriving late, say).
    const same =
      buttons.length === this._sceneBarButtons.length &&
      buttons.every((b, i) => b === this._sceneBarButtons[i])
    if (!same || this._sceneBar == null) {
      this._sceneBar?.dispose()
      this._sceneBar = systemBar(buttons)
      this._sceneBarButtons = buttons
      this._sceneBarVersion++
    }
    const bar = this._sceneBar!
    if (bar.count === 0) return null
    lozenge.style.visibility = 'hidden'
    return {
      frame: 'face',
      anchor: { position: [0, 0, 1] },
      presence: 'both',
      reveal: 'always',
      layer: 'ui',
      svg: bar.svg,
      width: 0.1,
      resolution: 512,
      screenAnchor: { left: 8, top: 8, height: BAR_HEIGHT },
    }
  }

  private _disposeFlatPanels(): void {
    if (this._flatPoseObs != null) {
      this.scene?.onBeforeCameraRenderObservable.remove(this._flatPoseObs)
      this._flatPoseObs = null
    }
    for (const p of this._flatPanels) p.dispose()
    this._flatPanels = []
    this._flatFrames?.dispose()
    this._flatFrames = null
    this._flatCam = -1
    this._flatSig = ''
    this._flatCheckIn = 0
  }

  private _updateFlatPanels(): void {
    const scene = this.scene
    const cam = scene?.activeCamera
    if (scene == null || cam == null || this.xrFrames != null) {
      if (this._flatFrames != null) this._disposeFlatPanels()
      return
    }
    if (--this._flatCheckIn <= 0) {
      this._flatCheckIn = 30
      const specs = Array.from(this.querySelectorAll('tosi-b3d-panel'))
        .map((el) =>
          (el as unknown as { toSpec?: () => FramePanelSpec }).toSpec?.()
        )
        .filter(
          (s): s is FramePanelSpec =>
            s != null && s.presence === 'both' && s.anchor !== 'system-bar'
        )
      const bar = this._sceneToolbarSpec()
      if (bar) specs.unshift(bar)
      // System panels that say `open`, once each, when the scene starts.
      for (const { el, spec } of this._systemEntries())
        if (spec.open && !this._sysOpened.has(el)) {
          this._sysOpened.add(el)
          this._openSystemPanel(el)
        }
      const sig = specs.length
        ? `${cam.uniqueId}|${
            bar ? `bar${this._sceneBarVersion}` : ''
          }|${JSON.stringify(specs)}`
        : ''
      if (sig !== this._flatSig) {
        /*
        RECONCILED, not rebuilt. Any change to the set (a system button
        arriving, one panel's attribute) used to dispose EVERY flat panel,
        the system bar included, and build them again: each new plane is
        blank until its texture is drawn, so the bar and every pinned panel
        blinked. A panel whose spec is unchanged keeps its plane; the bar,
        whose spec is the same but whose SVG is new (another button), takes
        the new SVG on the same plane. Only a new camera rebuilds them all,
        because the frames follow it.
        */
        if (specs.length === 0 || cam.uniqueId !== this._flatCam)
          this._disposeFlatPanels()
        this._flatSig = sig
        this._flatCheckIn = 30
        if (specs.length > 0 && this._flatFrames == null) {
          const frames = XrFrames.flat(scene, cam)
          this._flatFrames = frames
          this._flatCam = cam.uniqueId
          /*
          POSE THE FRAMES WHEN THE CAMERA IS FINAL, not when this loop runs.
          This used to happen here, before `scene.render()`, and anything that
          moves the camera during the render (b3d-aircraft's follow camera
          writes its position in its own before-render update) had not run
          yet. So every flat eye-anchored panel trailed the view by exactly
          one frame, measured by manta-recon at 2.151 m at speed (board #767).
          `computeWorldMatrix()` in `updateFlat` refreshed a stale MATRIX; it
          could not see a position nobody had written yet.
          `onBeforeCameraRenderObservable` fires after every before-render
          observer, once per camera, so the pose is whatever the frame
          actually renders.
          */
          this._flatPoseObs = scene.onBeforeCameraRenderObservable.add(
            (rendering) => {
              if (rendering !== cam || this._flatFrames == null) return
              this._flatFrames.update(0)
              for (const p of this._flatPanels) p.update()
            }
          )
        }
        const frames = this._flatFrames
        const old = [...this._flatPanels]
        const next: typeof this._flatPanels = []
        for (const spec of specs) {
          // `flatFrame` wins whenever it is given (the hands need it; a
          // pitched flat camera wants 'face'), and is required for a hand.
          let frame = spec.frame ?? 'body'
          // A screen-pinned panel lives in the head-locked frame.
          if (spec.screenAnchor != null) frame = 'face'
          else if (spec.flatFrame != null) frame = spec.flatFrame
          else if (frame === 'left-hand' || frame === 'right-hand') {
            const key = spec.title ?? spec.url ?? frame
            if (!this._flatHandWarned.has(key)) {
              this._flatHandWarned.add(key)
              console.warn(
                `b3d-panel "${key}": frame "${frame}" has no flat analogue ` +
                  '(a monitor has no hands), so it is VR-only. Give it a ' +
                  '`flatFrame` to say where it goes flat.'
              )
            }
            continue
          }
          if (frames == null) continue
          const svg = spec.svg ?? undefined
          const key = JSON.stringify({ ...spec, svg: undefined, frame })
          // The same panel: same spec and same SVG, or (the bar) a new SVG.
          const i = old.findIndex(
            (p) => p.key === key && (p.svg === svg || spec === bar)
          )
          if (i >= 0) {
            const kept = old.splice(i, 1)[0]
            if (svg != null && kept.svg !== svg) {
              kept.setSvg(svg)
              kept.svg = svg
            }
            next.push(kept)
            continue
          }
          const panel = attachFramePanel(
            scene,
            cam as BABYLON.TargetCamera,
            frames.get(frame),
            presentationSpec(spec, true)
          )
          // Assigned, not spread: a spread would freeze its `el` getter.
          next.push(Object.assign(panel, { key, svg }))
        }
        for (const p of old) p.dispose()
        this._flatPanels = next
      }
    }
    // Posed in onBeforeCameraRenderObservable (above), when the view is final.
  }

  private _update = () => {
    this._debugFrame++
    // `_frozen` stops the clock exactly like a pause, but WITHOUT the pause
    // panel or any of pause()'s resume semantics — see `freeze()`.
    if (this._paused || this._frozen) {
      /*
      Keep RENDERING — the panel has to be visible and pickable — but stop the
      CLOCK. Publishing a frame delta of zero is what actually pauses the world:
      everything that simulates does so on the render observable via
      `sceneDelta`, so gating only this method left projectiles flying, water
      moving and aircraft coasting at cruise speed with the stick disconnected.
      Measured at 66 m of travel over a 3-second pause (#30).
      */
      // The WALL clock keeps running, which is the one state `realDt` exists
      // for (a spinner on the pause panel). It used to publish 0 here and hold
      // `realElapsed`/`frame` still, contradicting FrameInfo's own doc.
      const nowPaused = realNow()
      const realPaused =
        this.lastRender > 0
          ? Math.min((nowPaused - this.lastRender) / 1000, 0.1)
          : 0
      this.lastRender = nowPaused
      // Babylon's own clocks as well — see `_stopEngineTime`. Ours is the
      // smaller half of a pause.
      this._stopEngineTime(true)
      this._scaleEngineTime(0)
      if (this.scene != null) {
        if (this.scene.metadata == null) this.scene.metadata = {}
        this.scene.metadata.b3dFrameDelta = 0
        this.frameDelta = 0
        // A paused frame still RENDERS, so the package must be present and
        // honest rather than stale: sim stopped, wall clock still running.
        this._realDelta = realPaused
        this._realElapsed += realPaused
        this._frameCount++
        this.scene.metadata.b3dFrame = this.frameInfo()
        /*
        FOG STILL HAS TO BE RIGHT WHILE STOPPED (adopter issue #31).

        A paused frame RENDERS — that is the whole point, the panel must be
        visible — so everything the render reads must be correct, and fog is
        read by the render. `_updateFog` lives past this early return, and
        `startPaused` pauses inside the same synchronous setup block, so a scene
        that BOOTS paused had never run it once: you saw Babylon's default fog,
        not the scene's.

        `dt = 0` because the clock is stopped: this APPLIES the current fog
        state without advancing any transition. Stopping time must not mean
        rendering something the scene never asked for.
        */
        this._updateFog(0)
        /*
        THE SCENE'S CHROME TOO (board #3342). The system bar and the
        flat panels are built here, and a stopped clock is no reason to
        withhold them: Manta freezes the scene on every menu, so its bar
        never appeared and a system panel got no button. Building chrome
        advances nothing.
        */
        this._updateFlatPanels()
        if (this.scene.activeCamera != null) this.scene.render()
      }
      return
    }
    if (this.scene != null && !this.hidden) {
      // Advance combat with real elapsed time (regen + scheduled chain reactions),
      // frame-rate independent and separate from the render throttle below.
      const dt = this.engine.getDeltaTime() / 1000
      if (dt > 0) this.combat.tick(dt)
      if (dt > 0) this._updateFog(dt)
      this._ambientWatchdog()
      this._updateFlatPanels()
      if (this.update !== noop) {
        this.update(this, BABYLON)
      }
      const now = realNow()
      if (
        this.xrActive ||
        now - this.lastRender >= 1000 / (this as any).frameRate
      ) {
        // THE authoritative frame delta — wall clock between actual renders.
        // Babylon's getDeltaTime() measures the engine's rAF TICK, not the
        // gap between scene.render() calls, so anything ticking in a scene
        // observer (which only fires on render) that trusts it advances too
        // little time whenever the render throttle bites: at frameRate 60 on
        // a 120Hz display everything runs at HALF speed, at the default 30 a
        // QUARTER (measured 2026-08-11 — dropped bombs inherited half the
        // aircraft's velocity and sagged behind). jolt-plugin hit this and
        // worked around it locally; this is the shared fix.
        const realDt =
          this.lastRender > 0
            ? Math.min((now - this.lastRender) / 1000, 0.1)
            : 1 / ((this as any).frameRate || 60)
        /*
        TWO CLOCKS, and the DEFAULT one is the scalable one.

        `frameDelta` / `sceneDelta` are SIM seconds, so everything already
        integrating with them honours `timeScale` and pause without changing a
        line. That direction is deliberate: the common case is motion that
        should slow down when the world slows down, so the obvious name gives
        the right clock and the exceptions have to ask.

        `realDt` is the wall-clock escape hatch, for the things that must NOT
        slow — a spinner, a UI tween, a countdown in real seconds.
        */
        const scale = Math.max(0, Number((this as any).timeScale ?? 1) || 0)
        this._stopEngineTime(false)
        this._scaleEngineTime(scale)
        this.frameDelta = realDt * scale
        this._realDelta = realDt
        this._simElapsed += this.frameDelta
        this._realElapsed += realDt
        this._frameCount++
        // Also published on the scene so owner-less helpers (explosionFx,
        // spawnProjectile, the exploder) can read it via `sceneDelta`.
        if (this.scene.metadata == null) this.scene.metadata = {}
        this.scene.metadata.b3dFrameDelta = this.frameDelta
        this.scene.metadata.b3dFrame = this.frameInfo()
        this.lastRender = now
        if (this.scene.activeCamera !== undefined) {
          this.scene.render()
        }
      }
    }
  }

  private _resizing = false
  // How many times handleResize has driven engine.resize(). A steady climb every
  // frame (rather than a couple of firings that settle) is the fingerprint of a
  // resize→reflow→resize feedback loop — surfaced in `debugState` / the 📊 overlay.
  _resizeCount = 0
  // `handleResize`, not `onResize`: tosijs reserves the `on<Event>` prefix for the
  // elements factory's event sugar (`creator({ onResize })` would attach a 'resize'
  // LISTENER), so a component callback named that way collides with it. tosijs warns
  // and points here — the same footgun as tosijs#22, now with a migration path.
  handleResize() {
    // An open flat panel re-fits to the new height, once the resize settles.
    const host = this.parts?.scenePanelHost as HTMLElement | undefined
    if (
      host != null &&
      !host.hasAttribute('hidden') &&
      this.clientHeight !== this._panelHeightKey
    ) {
      if (this._panelResizeTimer != null) clearTimeout(this._panelResizeTimer)
      this._panelResizeTimer = setTimeout(() => {
        this._panelResizeTimer = null
        this.refreshScenePanel()
      }, 150)
    }
    if (this.engine && !this._resizing) {
      this._resizing = true
      this.engine.resize()
      this._resizeCount++
      this._resizing = false
    }
  }

  // A cheap, allocation-light snapshot of the render pipeline's live state —
  // handy from the console, haltija, or the 📊 stats overlay (no devtools needed
  // on mobile). Reports the actual backbuffer vs CSS size (is DPR inflating it?),
  // the current hardware-scaling level and quality tier, live FPS, and the resize
  // count (loop detector). Returns nulls before the engine exists.
  get debugState() {
    const e = this.engine
    return {
      renderWidth: e ? e.getRenderWidth() : null,
      renderHeight: e ? e.getRenderHeight() : null,
      cssWidth: this.clientWidth,
      cssHeight: this.clientHeight,
      devicePixelRatio:
        typeof window !== 'undefined' ? window.devicePixelRatio : null,
      hardwareScaling: e ? e.getHardwareScalingLevel() : null,
      tier: effectiveTier({ xr: this.xrActive }),
      fps: e ? Math.round(e.getFps()) : null,
      resizeCount: this._resizeCount,
      xrActive: this.xrActive,
    }
  }

  loadScene = async (
    path: string,
    file: string,
    processCallback?: (scene: BABYLON.Scene) => void
  ): Promise<void> => {
    BABYLON.SceneLoader.Append(path, file, this.scene, processCallback)
  }

  // (Component insert/dispose is pull-model: each B3dChild self-registers via
  // whenReady() on connect and self-disposes on disconnect — b3d no longer pushes
  // sceneReady/sceneDispose or watches the subtree.)

  private _qualityOff: (() => void) | null = null
  private static _probeStarted = false

  // Seed the device quality profile and apply render scaling. Explicit `quality`
  // wins; otherwise hydrate a cached profile synchronously so children build with
  // the right budgets, and — if there's no cache and no probe on the page — run one
  // in the background (this scene uses the safe default until it caches for next
  // time). Render scaling (hardware scaling) is the one lever cheap to re-apply
  // live, so it tracks quality/XR changes.
  private _setupQuality(): void {
    const q = (this as any).quality as QualitySetting
    if (q && q !== 'auto') setQuality(q)

    // Seed synchronously from cache so children build with the right budgets now.
    const hydrated = q !== 'auto' || hydrateProfileFromCache()

    // If there's nothing cached, measure — DOM-free (runProbe mounts no element,
    // so it can't trip live-reload/doc observers) and DEFERRED (its throwaway
    // engine shouldn't share this scene's setup frame). This scene uses the safe
    // default until the probe caches for next time.
    if (q === 'auto' && !hydrated && !B3d._probeStarted) {
      B3d._probeStarted = true
      this._probeWhenIdle()
    }

    this._applyHardwareScaling(this.xrActive)
    this._applyGroundDetail(this.xrActive)
    this._qualityOff = onQualityChange(() => {
      this._applyHardwareScaling(this.xrActive)
      this._applyGroundDetail(this.xrActive)
      this._reallocAmbient() // a new tier is a new pool
      this._applySsao()
    })
  }

  /**
   * Run the device probe once the scene has actually settled.
   *
   * It used to go out on `setTimeout(…, 0)`, described as "deferred" — but a 0 ms
   * timeout defers by ONE TASK, so the benchmark ran inside the host scene's
   * heaviest moment: terrain building, shaders compiling, GLBs parsing. The probe
   * times GPU and CPU work against a fixed reference, so contention inflates every
   * measurement, and `classify()` puts anything scoring below 0.6 — under ~1.67×
   * the medium baseline — in the LOW tier. The result is then cached for 30 days.
   *
   * So the machine most able to show the engine off got the most conservative
   * budgets, because it loads the biggest scene and therefore contends the most,
   * and it stayed that way for a month. (tosijs-3d#11, reported on an M5 Max
   * holding 120fps.) Measuring an idle machine is the entire point of measuring.
   *
   * A spin-up sequence would let us measure a KNOWN workload during load instead
   * of waiting for quiet — see DECISIONS.md. This is the fix that doesn't need one.
   */
  private _probeWhenIdle(): void {
    const start = realNow()
    let quietFrames = 0
    const tick = () => {
      if (this.engine == null || this.scene == null) return // scene went away
      // Give up waiting after 30s and measure anyway: a scene that streams
      // forever (an endless world) would otherwise never probe at all, and a
      // stale default is worse than a slightly noisy measurement.
      const timedOut = realNow() - start > 30000
      quietFrames = this.sceneBusy ? 0 : quietFrames + 1
      // ~1s of consecutive settled frames — one quiet frame mid-load proves nothing.
      if (quietFrames < 60 && !timedOut) {
        requestAnimationFrame(tick)
        return
      }
      runProbe({ measuredWhileBusy: timedOut }).catch(() => {
        /* probing is best-effort — never let it break the host scene */
      })
    }
    requestAnimationFrame(tick)
  }

  private _xrViewportOff: (() => void) | null = null
  /** What a session is drawn at right now: `{ scale, foveation }`. */
  xrResolution = { scale: 1, foveation: 0 }

  /*
  THE HEADSET'S RESOLUTION LEVERS. The XR layer owns the framebuffer, so the
  engine's hardware scaling (above) changes nothing in a session: the tier's
  "XR-biased scaling" was a number nobody read, and every tier drew both eyes
  at full size. Measured on a Quest in Land and Sky (90 ms a frame): half the
  pixels saved 19 ms, full foveation 12 ms.

  The viewport is asked for per frame because that is the only form WebXR
  offers after a session starts (`XRView.requestViewportScale`); a browser
  without it draws at full size and nothing is lost.
  */
  private _applyXrResolution(): void {
    const session = this.xrHelper?.baseExperience?.sessionManager
    if (session == null || !this.xrActive) return
    const b = qualityBudgets({ xr: true })
    const askedScale = Number((this as any).xrRenderScale) || 0
    const scale = Math.min(
      1,
      Math.max(0.3, askedScale > 0 ? askedScale : 1 / b.hardwareScaling)
    )
    const askedFov = Number((this as any).xrFoveation)
    const foveation = Math.min(
      1,
      Math.max(0, askedFov >= 0 ? askedFov : b.xrFoveation)
    )
    this.xrResolution = { scale, foveation }
    try {
      session.fixedFoveation = foveation
    } catch {
      /* not offered by this browser */
    }
    this._xrViewportOff?.()
    this._xrViewportOff = null
    const ask = (frame: XRFrame) => {
      const pose = frame.getViewerPose(session.referenceSpace)
      for (const v of pose?.views ?? [])
        (
          v as unknown as { requestViewportScale?: (s: number) => void }
        ).requestViewportScale?.(scale)
    }
    if (scale >= 1) {
      // Once: a smaller viewport asked for earlier stays until it is undone.
      session.onXRFrameObservable.addOnce(ask)
      return
    }
    const obs = session.onXRFrameObservable.add(ask)
    this._xrViewportOff = () => session.onXRFrameObservable.remove(obs)
  }

  // What each headset lever is saving: put it back to full and measure.
  private _xrLeverExperiments(): Array<{
    name: string
    apply: () => () => void
  }> {
    if (!this.xrActive) return []
    const lever = (name: string, attr: string, full: number) => ({
      name,
      apply: () => {
        const was = (this as any)[attr]
        ;(this as any)[attr] = full
        this._applyXrResolution()
        return () => {
          ;(this as any)[attr] = was
          this._applyXrResolution()
        }
      },
    })
    const out = []
    if (this.xrResolution.scale < 1)
      out.push(lever('+ full resolution', 'xrRenderScale', 1))
    if (this.xrResolution.foveation > 0)
      out.push(lever('+ no foveation', 'xrFoveation', 0))
    return out
  }

  private _applyGroundDetail(xr: boolean): void {
    if (this.scene == null) return
    const asked = (this as any).groundDetail as string
    const lite =
      asked === 'lite' ||
      (asked !== 'full' && qualityBudgets({ xr }).groundDetail < 0.5)
    setBiomeLite(this.scene, lite)
  }

  private _applyHardwareScaling(xr: boolean): void {
    if (this.engine == null) return
    const b = qualityBudgets({ xr })
    this.engine.setHardwareScalingLevel(
      renderScalingLevel(
        b.hardwareScaling,
        xr,
        (this as any).pixelRatio,
        b.pixelRatioCap,
        globalThis.window?.devicePixelRatio ?? 1
      )
    )
    this._watchPixelRatio(xr)
  }

  /*
  THE DISPLAY'S RATIO CAN CHANGE UNDER US — dragging a window from a Retina
  screen to an external monitor, or browser zoom. A resolution media query
  that matches the CURRENT ratio fires once when it stops matching; re-arm it
  at the new one each time.
  */
  private _appliedPixelRatio: unknown = undefined
  private _pixelRatioQuery: MediaQueryList | null = null
  private _pixelRatioListener: (() => void) | null = null
  private _watchPixelRatio(xr: boolean): void {
    this._unwatchPixelRatio()
    const w = globalThis.window
    if (xr || w?.matchMedia == null) return
    const q = w.matchMedia(`(resolution: ${w.devicePixelRatio ?? 1}dppx)`)
    const listener = () => this._applyHardwareScaling(this.xrActive)
    q.addEventListener?.('change', listener)
    this._pixelRatioQuery = q
    this._pixelRatioListener = listener
  }
  private _unwatchPixelRatio(): void {
    if (this._pixelRatioQuery != null && this._pixelRatioListener != null)
      this._pixelRatioQuery.removeEventListener?.(
        'change',
        this._pixelRatioListener
      )
    this._pixelRatioQuery = null
    this._pixelRatioListener = null
  }

  // ─── Ambient budget ───────────────────────────────────────────────────────
  // Ambient effects (rain, motes, bubbles — and one day footprints and bullet holes) are
  // GARNISH: they compete for one shared pool, and an effect that can't be given its honest
  // minimum switches OFF rather than thinning into a lie. The maths is pure and lives in
  // `ambient-budget.ts`; B3d just owns the registry, the pool, and the watchdog.

  private _ambient: AmbientEffect[] = []
  /** Rationed by the watchdog: shed fast (`ratchetPool`), recovered slowly
   * (`recoverPool`) once the machine has held a good frame rate for 20 settled
   * seconds. The asymmetry is the damping — a transient must not cost the
   * session its weather, and a rebound must not cost it its frame rate. */
  private _ambientPoolScale = 1
  private _ambientSampleMs = 0
  private _ambientBadSamples = 0
  /** Sustained GOOD seconds, for the recovery path — see `_ambientWatchdog`. */
  private _ambientGoodSamples = 0
  private _ambientCooldownMs = 0
  /** Don't judge the frame rate until the scene has settled — see `_ambientWatchdog`.
   * A FLOOR, not the whole rule: `sceneBusy` holds the countdown while assets are
   * still landing, so this is the quiet time required AFTER loading finishes. */
  private _ambientWarmupMs = 4000

  /**
   * Is the scene still building itself? Any frame-rate judgement taken while this
   * is true says nothing about the hardware.
   *
   * A fixed timer can't answer this: a streaming world loads for longer than any
   * number you'd pick, and a trivial scene settles sooner. `getWaitingItemsCount`
   * is what `reveal()` already trusts for exactly this question, and terrain
   * publishes its own settle state, so this asks THEM rather than the clock.
   * (tosijs-3d#11, manta-recon: ambient was shed during the loading screen and
   * ratcheted to zero for the session, on hardware that then ran fine.)
   */
  get sceneBusy(): boolean {
    if (this.scene == null) return true
    if (this.scene.getWaitingItemsCount() > 0) return true
    // Terrain streams in AFTER the asset loader has gone quiet, so
    // getWaitingItemsCount alone would call a half-built world "settled".
    for (const t of this.querySelectorAll('tosi-b3d-terrain')) {
      if ((t as unknown as { busy?: boolean }).busy) return true
    }
    return false
  }

  /**
   * The ambient pool multiplier the watchdog has ratcheted down (1 = untouched,
   * 0 = garnish shed for the session).
   *
   * Settable because the watchdog's verdict is one-way by design, and a game may
   * legitimately know better — "that was the loading screen, try again". Manta
   * was reaching into two privates to say exactly that (#11).
   */
  get ambientPoolScale(): number {
    return this._ambientPoolScale
  }

  set ambientPoolScale(scale: number) {
    const next = Math.max(0, Math.min(1, scale))
    if (next === this._ambientPoolScale) return
    this._ambientPoolScale = next
    this._ambientBadSamples = 0
    this._reallocAmbient()
  }

  /** An ambient effect joins the scene's pool. Returns its unregister. */
  registerAmbient(effect: AmbientEffect): () => void {
    if (this._ambient.length === 0) {
      // Readable IN the headset — the only place the watchdog's damage is visible. `pool` < 1
      // means the ratchet has fired and garnish has been permanently shed this session.
      this.addDebugSource({
        name: 'Ambient',
        lines: () => [
          `pool=${this._ambientPoolScale.toFixed(2)} warmup=${Math.max(
            0,
            Math.round(this._ambientWarmupMs / 1000)
          )}s bad=${this._ambientBadSamples}`,
          ...this._ambient.map((a) => {
            const r = a.budgetRequest()
            const s = a as unknown as {
              preset: string
              granted: number
              active: number
              intensity: number
            }
            // got but live=0 ⇒ built, not rendering. live>0 but you see nothing ⇒ a LOOK
            // problem (too small/sparse/faint), not a plumbing one.
            return `${s.preset} got=${s.granted}/${r.desired} live=${
              s.active
            } i=${s.intensity.toFixed(2)}`
          }),
        ],
      })
    }
    this._ambient.push(effect)
    this._reallocAmbient()
    return () => {
      const i = this._ambient.indexOf(effect)
      if (i < 0) return
      this._ambient.splice(i, 1)
      this._reallocAmbient() // its budget goes back to the survivors
    }
  }

  /** Divide the pool and tell everyone what they got (0 = switch off). */
  private _reallocAmbient(): void {
    if (this._ambient.length === 0) return
    const xr = this.xrActive
    const pool =
      qualityBudgets({ xr }).ambientParticles * this._ambientPoolScale
    const alloc = allocateAmbient(
      this._ambient.map((a) => a.budgetRequest()),
      { pool, tier: effectiveTier({ xr }) }
    )
    for (const a of this._ambient) {
      a.applyAllocation(alloc[a.budgetRequest().id] ?? 0)
    }
  }

  /**
   * Garnish is the first thing to go. If the frame stays under target we shrink the ambient
   * pool — effects that fall below their honest minimum switch themselves off.
   *
   * This needs NO cost attribution, which is the point: we can't measure what the rain costs
   * (Babylon has no per-system counter, and the real cost is GPU fill), but we don't have to.
   * We only need to know that ambient is the cheapest thing in the scene to give up.
   */
  private _ambientWatchdog(): void {
    /*
    A pool at ZERO still has to be watched — that is the whole recovery case.

    This used to bail on `_ambientPoolScale <= 0`, which made the recovery path
    below unreachable from exactly the state it was written for:
    `recoverPool(0)` returns 0.25 by design ("Zero is not a special case… a pool
    ratcheted to nothing recovers from a floor rather than staying dead"), and
    `ambient-budget.test.ts` pins it with "a pool shed to ZERO comes back — the
    reported bug". The model was right, the test was green, and the caller
    returned one line before it could ever run — so a scene that shed to nothing
    stayed dead for the session, which is precisely the reported symptom (no
    leaves, no bubbles, no motes, on hardware that had been drawing them a
    second earlier).

    Only the "no ambient effects at all" case is a real early-out.
    */
    if (this._ambient.length === 0) return
    if (this.engine == null) return
    const dt = this.engine.getDeltaTime()

    // WARM UP before judging. The frame rate right after load — and right after XR entry — is
    // garbage for reasons that have nothing to do with ambient: shaders compiling, textures
    // uploading, GLBs landing. Judged during that, a ONE-WAY ratchet would shed the garnish
    // for the whole session over a hitch that was already over. Grace period first.
    // Still building? Then the frame rate is about the LOADING, not the hardware,
    // and a one-way ratchet must not act on it. The clock only runs once the
    // scene is quiet — so `_ambientWarmupMs` means "settled for this long".
    if (this.sceneBusy) {
      this._ambientBadSamples = 0
      return
    }
    if (this._ambientWarmupMs > 0) {
      this._ambientWarmupMs -= dt
      this._ambientBadSamples = 0
      return
    }
    if (this._ambientCooldownMs > 0) {
      this._ambientCooldownMs -= dt
      return
    }
    this._ambientSampleMs += dt
    if (this._ambientSampleMs < 1000) return
    this._ambientSampleMs = 0

    // 0.75, not 0.85: shedding is IRREVERSIBLE, so the bar to do it has to be a frame rate
    // that's actually bad, not one that's merely short of ideal. A headset running 68 of a
    // nominal 72 is fine; it is not a reason to delete the weather for the rest of the session.
    const target = this.xrActive ? 72 : 60
    const fps = this.engine.getFps()
    if (Number.isFinite(fps) && fps > 0 && fps < target * 0.75) {
      this._ambientBadSamples++
      this._ambientGoodSamples = 0
    } else {
      this._ambientBadSamples = 0 // it must be SUSTAINED — one bad second is a hitch, not a trend
      // Comfortably above the shed bar, not merely above it — recovering at the
      // same threshold that sheds is how you build an oscillator.
      if (Number.isFinite(fps) && fps > target * 0.9) this._ambientGoodSamples++
      else this._ambientGoodSamples = 0
    }

    /*
    GIVE IT BACK, GRUDGINGLY.

    The ratchet is one-way by design and that is right on weak hardware. Its
    failure mode shows up in ordinary play instead: **a transient costs you the
    weather for the whole session.** Reported from a headset — falling into the
    water shed the pool to zero, and there were no leaves, bubbles or motes for
    the rest of the run on a device that had been rendering them a second
    earlier. Entering water fires fog, bubbles and a surface transition at once;
    the hitch ends long before you surface, and the punishment did not.

    Recovery is deliberately harder to earn than shedding: 20 good seconds
    against 6 bad ones, a higher bar than the shed threshold, and a smaller step
    (×1.35 up vs ×0.6 down). A wrong shed heals in a few intervals; a wrong
    recovery is re-shed cheaply. The asymmetry IS the damping the old TODO asked
    for — "a damped, deliberate thing, not a rebound".
    */
    if (this._ambientGoodSamples >= 20 && this._ambientPoolScale < 1) {
      this._ambientGoodSamples = 0
      this._ambientCooldownMs = 5000
      this._ambientPoolScale = recoverPool(this._ambientPoolScale)
      this._reallocAmbient()
      return
    }

    if (this._ambientBadSamples < 6) return // ~6s of genuinely bad frames, not 3

    this._ambientBadSamples = 0
    this._ambientGoodSamples = 0
    this._ambientCooldownMs = 5000 // let the frame settle before judging again
    this._ambientPoolScale = ratchetPool(this._ambientPoolScale)
    this._reallocAmbient()
  }

  private _statsBaseScale: number | null = null
  // The last few sweeps, kept across page loads: one that hung the device
  // left its journal, naming the experiment it stopped at.
  private _sweepRuns: SweepRun[] = (() => {
    try {
      const j = JSON.parse(localStorage.getItem(SWEEP_KEY) ?? 'null')
      // Only what has the shape of a run: this is storage, and a bad entry
      // would stop Perf Stats building until it was cleared by hand.
      if (Array.isArray(j?.runs))
        return j.runs.filter(
          (r: unknown) =>
            r != null &&
            typeof (r as SweepRun).n === 'number' &&
            Array.isArray((r as SweepRun).lines)
        )
      // The journal before runs were numbered.
      if (Array.isArray(j?.lines))
        return [{ n: 0, began: 0, lines: j.lines, starting: j.starting }]
    } catch {
      /* unreadable: start clean */
    }
    return []
  })()
  private _sweepRun: number | null = null
  private get _sweepLines(): string[] {
    return formatRuns(this._sweepRuns, this._sweepRun)
  }
  private _sweepNow: string | null = null
  private _sweepStep = 'sweep: not running'
  /** The rows of the last {@link costSweep}, for reading over a debug bridge. */
  lastCostSweep: SweepRow[] = []

  /**
   * Where this scene's frame goes, measured here: see [[cost-sweep]]. Also the
   * **Cost sweep** button in Perf Stats, so it runs inside a headset session.
   */
  async costSweep(
    seconds = 2,
    what: 'scene' | 'shaders' = 'scene'
  ): Promise<SweepRow[]> {
    if (this.scene == null) return []
    const rate = (this as any).frameRate
    const run: SweepRun = {
      n: this._sweepRuns.reduce((m, r) => Math.max(m, r.n), 0) + 1,
      began: wallMs(),
      lines: [],
      starting: 'baseline',
      kind: what,
    }
    this._sweepRun = run.n
    const keep = () => {
      this._sweepRuns = recordRun(this._sweepRuns, { ...run })
      try {
        localStorage.setItem(
          SWEEP_KEY,
          JSON.stringify({ runs: this._sweepRuns })
        )
      } catch {
        /* private mode: the sweep still runs, it just leaves no trace */
      }
    }
    // Before the first repaint, so the panel shows this run and not the last.
    keep()
    let rows: SweepRow[]
    try {
      rows = await costSweep(this.scene, {
        seconds,
        xr: this.xrHelper,
        // The render throttle would be what is measured, not the scene.
        unthrottle: (on) => {
          ;(this as any).frameRate = on ? 1000 : rate
        },
        // The panel the sweep is run from, the hands and their rays stay.
        skip: (m) =>
          m.renderingGroupId > 0 ||
          /^xr-|panel|controller|laser|pointer|teleport|hand/i.test(m.name) ||
          /^controller/i.test(m.material?.name ?? ''),
        /*
      TWO repaints, not one per experiment. Repainting rebuilds the panel and
      redraws its texture, and doing that eighteen times inside a session on a
      device already at its limit is its own load (a panel rebuild has hung a
      Quest before, #3135).
      */
        progress: (name, done, total) => {
          // A LIVE line (rewritten in place, no rebuild): the step it is on.
          this._sweepStep =
            name == null
              ? `sweep: run ${run.n} finished`
              : `sweep: run ${run.n}, ${done + 1} of ${total}: ${name}`
          const was = this._sweepNow
          this._sweepNow = name == null ? null : 'Sweeping, about a minute'
          if (name == null || (was == null && done === 0)) this._repaintPanels()
        },
        /*
        The ground shader's other form, whichever is not in use: its cost (or
        its saving) on this device. Asked for separately (**Shader sweep**):
        each one recompiles every ground and decoration shader twice, and the
        sweeps that included them hung a Quest.
        */
        only: what === 'shaders' ? 'extra' : undefined,
        extra:
          what !== 'shaders'
            ? this._xrLeverExperiments()
            : [
                {
                  name: biomeLite(this.scene)
                    ? '+ full ground shader'
                    : '- ground detail',
                  apply: () => {
                    const scene = this.scene!
                    const was = biomeLite(scene)
                    setBiomeLite(scene, !was)
                    return () => setBiomeLite(scene, was)
                  },
                },
                {
                  name: biomeComputedNoise(this.scene)
                    ? '- computed noise (texture)'
                    : '+ computed ground noise',
                  apply: () => {
                    const scene = this.scene!
                    const was = biomeComputedNoise(scene)
                    setBiomeComputedNoise(scene, !was)
                    return () => setBiomeComputedNoise(scene, was)
                  },
                },
              ],
        journal: (soFar, starting, phase) => {
          run.lines = formatSweep(soFar)
          run.starting = starting
          run.phase = phase
          keep()
          sendDebug('sweep', {
            run: run.n,
            kind: what,
            phase,
            starting,
            last: run.lines[run.lines.length - 1] ?? null,
          })
        },
      })
    } finally {
      this._sweepRun = null
    }
    this.lastCostSweep = rows
    sendDebug('sweep', { run: run.n, kind: what, finished: true, rows })
    console.log('cost sweep\n' + this._sweepLines.join('\n'))
    this._repaintPanels()
    return rows
  }
  private _perfMeter: BABYLON.SceneInstrumentation | null = null
  // SVG texture repaints (the HUD, panels) over the last second or so, on the
  // main thread: the counters are page-wide, so this is every scene's.
  private _svgCostAt = { t: 0, checks: 0, repaints: 0, ms: 0 }
  private _svgCostText = 'svg textures: measuring'
  private _svgCostLine(): string {
    const now = realNow()
    const was = this._svgCostAt
    const dt = (now - was.t) / 1000
    if (dt >= 1) {
      if (was.t > 0) {
        const repaints = (svgTextureCost.repaints - was.repaints) / dt
        const ms = (svgTextureCost.ms - was.ms) / dt
        // One decimal below 10: a popup redrawn every 2 s read as "0".
        this._svgCostText = `svg textures: ${repaints.toFixed(
          repaints < 10 ? 1 : 0
        )} repaints/s  ${ms.toFixed(0)}ms/s on this thread`
      }
      this._svgCostAt = { t: now, ...svgTextureCost }
    }
    return this._svgCostText
  }
  /**
   * JS heap now and against the first reading, with the redraw count: a number
   * that climbs while the scene sits still is a leak on this side. GPU memory
   * is not in it (no browser reports that to a page).
   */
  private _heapWas: number | null = null
  private _heapLine(): string {
    const used = (performance as any).memory?.usedJSHeapSize as
      | number
      | undefined
    if (used == null) return `heap ?  redraws ${svgTextureCost.repaints}`
    const mb = used / 1048576
    this._heapWas ??= mb
    const d = mb - this._heapWas
    return `heap ${mb.toFixed(0)}MB (${d >= 0 ? '+' : ''}${d.toFixed(
      0
    )})  redraws ${svgTextureCost.repaints}`
  }
  // Which debug tools (Perf Stats + registered sources) are expanded, by id. Empty
  // by default — the panel opens with the debug data collapsed to its icon bar, so
  // a demo's own controls aren't buried under diagnostics you didn't ask to see.
  private _debugOpen = new Set<string>()
  private _debugSources: DebugPanelSource[] = []
  private _liveDebug: { flat: LiveDebugRow[]; xr: LiveDebugRow[] } = {
    flat: [],
    xr: [],
  }
  private _liveDebugTimer: ReturnType<typeof setInterval> | null = null
  /** Set while an XR panel exists; rewrites its contents in place so debug numbers stay
   * live in the headset. No-op flat (the flat panel rebuilds on open). */
  /**
   * Where the in-XR panel was last dragged to — kept on the ELEMENT so it
   * survives the dispose-and-re-attach a structural refresh does.
   */
  private _xrPanelSeat: Orbit | null = null

  private _refreshXrPanel: () => void = noopRefresh

  /**
   * Write into the **Perf Stats panel** — the only debug readout that exists BOTH as a
   * flat overlay and as a floating panel inside a headset. There is no console in VR, so
   * without this a profiler's numbers are unreadable on the one device whose budget is
   * tightest.
   *
   * Any code can contribute: a scene child, a demo, an ad-hoc investigation. `lines()` is
   * re-called on every panel refresh, so return live values, not a snapshot. `actions`
   * become buttons — which is how you turn a profiler ON from inside a headset.
   *
   * Returns an unregister function.
   *
   * ```js
   * const off = b3d.addDebugSource({
   *   name: 'terrain',
   *   lines: () => [`worst ${t.debugState.worstFrameMs.toFixed(1)}ms`],
   *   actions: [{ label: () => (t.profiling ? 'Profiling ON' : 'Profile'), handleClick: () => t.setProfiling(!t.profiling) }],
   * })
   * ```
   */
  // --- Atmosphere: fog is ALWAYS ON, and systems lean on it (see atmosphere.ts) ---
  //
  // Nothing may switch `fogMode` at runtime: it's a shader DEFINE, so toggling it recompiles
  // every material — that hitch is most of the "thunk" you feel crossing the water's surface.
  // The mode is set once; contributors modulate only the UNIFORMS (colour, density, start,
  // end), their weights ramp over a band rather than flipping at a boundary, and the result
  // is temporally smoothed.
  private _fogLayers: FogContributor[] = []
  private _fogVeil = 0
  private _fogBase: FogState | null = null
  private _fogNow: FogState | null = null
  /**
   * **Paint the fog a colour nothing else is** (`'#ff00ff'`), to see what is
   * fog and what is surface. Composited fog in a murky scene is the same hue
   * as half the things in it, so "I can't see X" cannot be told apart from
   * "X is fogged out", "X is not drawn" or "that's the sky". Tonio suggested
   * it mid-hunt for a missing water underside, and it answered the question
   * in one screenshot. Also a toggle in the Perf Stats panel's Fog row, for
   * a headset. Empty = the real fog.
   */
  debugFogColor = ''
  private _fogDebugOff: (() => void) | null = null

  /**
   * Contribute a fog layer — underwater, inside a cloud, out in space. Return `null` (or
   * `weight: 0`) when you're not contributing. Returns an unregister function.
   *
   * `b3d-fog` sets the BASE (and the mode, once). Everyone else leans on it.
   */
  /**
   * THE MEDIA IN THIS SCENE — what things are moving through.
   *
   * A live list, not a snapshot: a water element registers itself here and
   * anything that cares (projectiles wanting drag, a vehicle wanting a regime,
   * a shader wanting depth) asks the scene rather than re-deriving the surface
   * from a mesh it had to go and find. Two subsystems deriving "am I under
   * water" separately is how they end up disagreeing at the boundary — the
   * failure that produced the fogged-sky/transparent-window conflict.
   *
   * See [[medium]] for the geometry (plane or sphere: a sea, or a planet's
   * ocean and atmosphere) and the queries.
   */
  readonly media: Medium[] = []

  /** Register a medium. Returns its unregister, like `addFogLayer`. */
  addMedium(m: Medium): () => void {
    this.media.push(m)
    return () => {
      const i = this.media.indexOf(m)
      if (i >= 0) this.media.splice(i, 1)
    }
  }

  /**
   * How much a MEDIUM is between you and everything, `0…1` — and therefore how
   * much of the SKY it should hide.
   *
   * Distance fog and a medium are not the same thing and must not be summed.
   * Haze thickens with distance, so it is right that it never erases the sky —
   * the sky IS the far distance and Babylon's fog already handles it. Being
   * INSIDE cloud, water or a dust storm is the other case: there is white a
   * metre from your face, and a blue sky above it is simply wrong. That was
   * the "the cloud whiteout is not whiting out the skybox" report, and the
   * reason it could not be fixed by turning fog on for the sky mesh is exactly
   * this distinction — the base fog would then eat the sky too.
   *
   * So this is the composited weight of the LAYERS only, never the base, and
   * the colour to pair it with is `scene.fogColor` (already composited and
   * smoothed by the same pass).
   */
  get fogVeil(): number {
    return this._fogVeil
  }

  addFogLayer(layer: FogContributor): () => void {
    this._fogLayers.push(layer)
    return () => {
      const i = this._fogLayers.indexOf(layer)
      if (i >= 0) this._fogLayers.splice(i, 1)
    }
  }

  /** The fog everything else blends FROM. `b3d-fog` owns this; without one we still keep a
   * whisper of fog on, so a layer can ramp up without ever switching the mode. */
  setFogBase(base: FogState | null): void {
    this._fogBase = base
    // Clearing the base (the <tosi-b3d-fog> was removed) must also drop the cached current fog,
    // or `_updateFog`'s "no base + live layers → auto-enable" branch can never re-fire and
    // underwater/cloud fog stays dead for the rest of the scene's life.
    if (base == null) {
      this._fogNow = null
      return
    }
    if (this._fogNow == null) {
      this._fogNow = {
        color: { ...base.color },
        density: base.density,
        start: base.start,
        end: base.end,
      }
    }
  }

  /**
   * Apply `clearColor` if the author gave one.
   *
   * Empty means "leave Babylon's default alone" rather than "black", because a
   * scene that never mentions the attribute should not change appearance for
   * having gained one.
   */
  private _applyClearColor(): void {
    const hex = (this as any).clearColor as string
    if (this.scene == null || !hex) return
    const c = BABYLON.Color3.FromHexString(hex)
    this.scene.clearColor = new BABYLON.Color4(c.r, c.g, c.b, 1)
  }

  private _updateFog(dt: number): void {
    const scene = this.scene
    if (scene == null) return
    // No <tosi-b3d-fog> in the scene? Fog is STILL on, at a whisper — because a layer
    // (underwater, cloud) must be able to ramp up without ever switching fogMode, which
    // would recompile every shader. "Always on to some extent" is the whole trick.
    if (this._fogBase == null && this._fogLayers.length > 0) {
      scene.fogMode = BABYLON.Scene.FOGMODE_EXP2
      this.setFogBase({
        color: {
          r: scene.fogColor.r,
          g: scene.fogColor.g,
          b: scene.fogColor.b,
        },
        density: 0.00001,
        start: scene.fogStart,
        end: scene.fogEnd,
      })
    }
    const base = this._fogBase
    if (base == null || this._fogNow == null) return
    const layers: FogLayer[] = []
    for (const fn of this._fogLayers) {
      const l = fn()
      if (l != null && l.weight > 0) layers.push(l)
    }
    /*
    OVER, not summed: each layer covers what is left of the sky rather than
    adding to it, so two half-weight media read as three-quarters hidden and
    nothing can push past total.
    */
    let veil = 0
    for (const l of layers) {
      const v = l.veil ?? l.weight
      veil += (1 - veil) * Math.min(1, Math.max(0, v))
    }
    const target = compositeFog(base, layers)
    // A SHORT time constant. This exists to stop a hard pop (and to absorb a layer whose
    // weight jumps — a cloud recycling behind you, a camera teleporting), NOT to make
    // transitions leisurely. Crossing the water's surface should read as instant-but-smooth:
    // a few frames, not a fade.
    this._fogNow = approachFog(this._fogNow, target, dt, 0.07)
    // Same time constant as the fog itself, so the sky and the air in front of
    // it cannot arrive at a transition a few frames apart.
    const k = 1 - Math.exp(-dt / 0.07)
    this._fogVeil += (veil - this._fogVeil) * k
    const f = this._fogNow
    if (this._fogDebugOff == null) {
      this._fogDebugOff = this.addDebugSource({
        name: 'Fog',
        lines: () => {
          const n = this._fogNow
          return n == null
            ? ['off']
            : [
                `density=${n.density.toFixed(3)} end=${n.end.toFixed(
                  0
                )}m veil=${this._fogVeil.toFixed(2)}`,
              ]
        },
        actions: [
          {
            label: () =>
              this.debugFogColor ? 'Debug colour ON' : 'Debug colour',
            handleClick: () => {
              this.debugFogColor = this.debugFogColor ? '' : '#ff00ff'
            },
          },
        ],
      })
    }
    if (this.debugFogColor) {
      try {
        scene.fogColor.copyFrom(
          BABYLON.Color3.FromHexString(this.debugFogColor)
        )
      } catch {
        scene.fogColor.set(1, 0, 1)
      }
    } else scene.fogColor.set(f.color.r, f.color.g, f.color.b)
    scene.fogDensity = f.density
    scene.fogStart = f.start
    scene.fogEnd = f.end
  }

  private _recenterXr: () => void = noop

  /**
   * Re-seat the head: take your CURRENT head yaw as "facing forward". The same thing the
   * headset's own recentre (holding the Meta button) asks for — we listen for that too, so
   * it now works; this is the manual door, e.g. a panel button.
   */
  recenterXr(): void {
    this._recenterXr()
  }

  /**
   * Capture the current view as a PNG **data URL**. Resolution-independent — it
   * renders through an offscreen render target at the requested size (default: the
   * canvas size), so you can grab a large still from a small canvas, and it never
   * depends on the drawing buffer being preserved. Works in the flat view,
   * including **in-scene 3D panels** — which is how you eyeball the SVG-texture UI
   * (the same raster path the VR panels use) without a headset.
   *
   * Returns a `data:image/png;base64,…` string (handy from the console or a dev
   * channel); for a Blob, `await (await fetch(url)).blob()`.
   */
  async snapshot(
    opts: { width?: number; height?: number } = {}
  ): Promise<string> {
    const cam = this.scene.activeCamera
    if (cam == null) throw new Error('snapshot: the scene has no active camera')
    const canvas = this.engine.getRenderingCanvas()
    const width = opts.width ?? canvas?.width ?? 1280
    const height = opts.height ?? canvas?.height ?? 720
    return BABYLON.Tools.CreateScreenshotUsingRenderTargetAsync(
      this.engine,
      cam,
      { width, height }
    )
  }

  /**
   * SAVE THE VIEW AS A PNG: what the canvas shows, `ui` true with the panels
   * and the system bar, false without them (the `ui` and `modal` layers and
   * world-layer panels are hidden for the frame and put back). Read straight
   * after a frame ends, so it needs no preserved drawing buffer. Downloads
   * `name` (default: the panel title, or "scene", and the time).
   */
  async saveScreenshot(
    opts: { ui?: boolean; name?: string } = {}
  ): Promise<Blob | null> {
    const canvas = this.engine?.getRenderingCanvas()
    if (canvas == null) return null
    const restore = opts.ui === false ? setPanelsHidden(this.scene) : noop
    let blob: Blob | null
    try {
      // A frame for the change to land, then read the next as it ends.
      const endOfFrame = () =>
        new Promise<void>((done) =>
          this.engine.onEndFrameObservable.addOnce(() => done())
        )
      await endOfFrame()
      blob = await new Promise<Blob | null>((done) =>
        this.engine.onEndFrameObservable.addOnce(() =>
          canvas.toBlob(done, 'image/png')
        )
      )
    } finally {
      restore()
    }
    if (blob == null) return null
    const stamp = wallDate().toISOString().replace(/[:.]/g, '-').slice(0, 19)
    const base =
      String((this as any).panelTitle || 'scene')
        .trim()
        .replace(/[^\w-]+/g, '-') || 'scene'
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = opts.name ?? `${base}-${stamp}.png`
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
    return blob
  }

  /** The panel's screenshot buttons: close their own popup first, so "with
   * panels" does not photograph the button you pressed. */
  private async _screenshotFromPanel(ui: boolean): Promise<void> {
    this._debugOpen.delete('Screenshot')
    this._repaintPanels()
    await this.saveScreenshot({ ui })
  }

  /** Repaint BOTH presentations of the panel. The flat one rebuilds; the XR one rewrites
   * its contents in place. Unified on purpose — see `_panelWidgets`. */
  private _repaintPanels(): void {
    // Flat and XR both (refreshScenePanel covers the headset's too).
    this.refreshScenePanel()
    /*
    AND RECONCILE THE POPUPS, here rather than at the call sites.

    Every caller that changes `_debugOpen` needs this, and the one that did it
    explicitly was the icon bar — so an action button inside a popup repainted,
    the popup was not reconciled, and the set and the screen disagreed: the
    popup was gone while `_debugOpen` still held its id, which is why bringing
    it back took two clicks (the first one only turned the flag off again).
    */
    this._syncDebugPopups()
  }

  /**
   * A buzz in a headset's controller (`hand`: 'right', 'left' or 'both'):
   * `strength` 0…1 for `ms`. Nothing in a flat view, or where the controller
   * has no haptics.
   */
  haptic(
    strength = 0.5,
    ms = 40,
    hand: 'left' | 'right' | 'both' = 'right'
  ): void {
    for (const c of this.xrHelper?.input?.controllers ?? []) {
      const side = c.inputSource.handedness
      if (hand !== 'both' && side !== hand) continue
      void c.motionController?.pulse(strength, ms).catch(() => false)
    }
  }

  addDebugSource(source: DebugPanelSource): () => void {
    this._debugSources.push(source)
    this.refreshScenePanel()
    return () => {
      const i = this._debugSources.indexOf(source)
      if (i >= 0) this._debugSources.splice(i, 1)
      this.refreshScenePanel()
    }
  }

  /*
  A tiny diagnostic ring buffer, for VR bugs you can only catch in a headset.

  There is no console in a headset and window.rAF is suspended, so the two
  normal readback paths are gone. This records structured events IN the page:
  read them back afterward at the desk with
  `document.querySelector('tosi-b3d').debugLog` (over haltija when the tab is
  reachable, or straight from DevTools). `frameNow` is a monotone counter, not a
  clock — Date.now is banned in the deterministic layer and useless for ordering
  frames anyway. Off by default; `b3d.debugCapture('origin')` arms a tag.
  Mirror-to-console is opt-in per tag because a VR frame budget can't afford a
  log line every reset.
  */
  /*
  THE CONSOLE THAT EXISTS IN A HEADSET.

  A VR-only crash is the worst shape of bug we have: there is no console, and
  the one person who can see it is wearing the thing. The guided-missile demo
  crashes on a hit in VR and NOT flat (Tonio), which is exactly the case where
  "read me the error" is impossible.

  So capture errors into the ring and surface the latest on the Perf panel. A
  PASSIVE listener — never `preventDefault()` — so behaviour is unchanged and
  this can ship on by default; an error that reaches `window` was already
  uncaught, and recording it costs nothing until one happens.

  Installed once per element, torn down with it.
  */
  private _errors: Array<{ f: number; msg: string; at: string }> = []
  private _errorCaptureOff: (() => void) | null = null
  /**
   * A node that is its own ancestor, if the scene has one: the names round the
   * loop. Babylon does not refuse such a parent, and the next `setEnabled` or
   * `dispose` anywhere on the loop recurses until the stack runs out (#3135).
   */
  private _parentCycle(): string[] | null {
    const scene = this.scene
    if (scene == null) return null
    const nodes: BABYLON.Node[] = [
      ...scene.meshes,
      ...scene.transformNodes,
      ...scene.cameras,
      ...scene.lights,
    ]
    const clear = new Set<BABYLON.Node>()
    for (const start of nodes) {
      const path: BABYLON.Node[] = []
      const seen = new Set<BABYLON.Node>()
      let n: BABYLON.Node | null = start
      while (n != null && !clear.has(n)) {
        if (seen.has(n)) {
          return path
            .slice(path.indexOf(n))
            .map((x) => `${x.name || '(unnamed)'} [${x.getClassName()}]`)
        }
        seen.add(n)
        path.push(n)
        n = n.parent
      }
      for (const x of path) clear.add(x)
    }
    return null
  }
  private _cycleReported = false
  private _reportParentCycle(when: string): void {
    if (this._cycleReported) return
    const cycle = this._parentCycle()
    if (cycle == null) return
    this._cycleReported = true
    console.warn('tosi-b3d: a node is its own ancestor:', cycle.join(' <- '))
    sendDebug('parent-cycle', { when, cycle })
  }
  private _installErrorCapture(): () => void {
    const onError = (e: ErrorEvent) => {
      const where = e.filename
        ? `${String(e.filename).split('/').pop()}:${e.lineno}`
        : 'unknown'
      this._errors.push({
        f: this._debugFrame,
        msg: String(e.message ?? e.error ?? 'error').slice(0, 120),
        at: where,
      })
      if (this._errors.length > 8) this._errors.shift()
      /*
      With the stack, top and bottom: a stack overflow's top is the function
      that recurses and its bottom is what started it, and the middle is the
      same frame a thousand times.
      */
      const stack = String((e.error as Error | undefined)?.stack ?? '')
      if (/call stack/i.test(String(e.message)))
        this._reportParentCycle('error')
      this.logDebug('error', {
        msg: String(e.message),
        at: `${where}:${e.colno}`,
        stack:
          stack.length > 5000
            ? `${stack.slice(0, 2500)}\n…\n${stack.slice(-2500)}`
            : stack,
      })
    }
    const onRejection = (e: PromiseRejectionEvent) => {
      this._errors.push({
        f: this._debugFrame,
        msg: `unhandled: ${String(e.reason)}`.slice(0, 120),
        at: 'promise',
      })
      if (this._errors.length > 8) this._errors.shift()
      this.logDebug('rejection', {
        msg: String(e.reason),
        stack: String((e.reason as Error | undefined)?.stack ?? '').slice(
          0,
          4000
        ),
      })
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }

  private _debugRing: Array<Record<string, unknown>> = []
  private _debugCapture = new Set<string>()
  private _debugFrame = 0
  /** Monotone frame counter for ordering diagnostic events (NOT a clock). */
  get debugFrame(): number {
    return this._debugFrame
  }
  /** The captured diagnostic events, oldest first. Read over haltija/DevTools. */
  get debugLog(): ReadonlyArray<Record<string, unknown>> {
    return this._debugRing
  }
  /** Arm (or, with on=false, disarm) diagnostic capture for a tag. */
  debugCapture(tag: string, on = true): void {
    if (on) this._debugCapture.add(tag)
    else this._debugCapture.delete(tag)
  }
  /** Record a diagnostic event if its tag is armed. Cheap no-op otherwise. */
  logDebug(tag: string, event: Record<string, unknown>): void {
    sendDebug(tag, event)
    if (!this._debugCapture.has(tag)) return
    this._debugRing.push({ f: this._debugFrame, tag, ...event })
    if (this._debugRing.length > 512) this._debugRing.shift()
  }

  // Rows contributed by registered debug sources. Kept generic on purpose: the core
  // knows nothing about terrain (or whatever else) — each source decides what's worth
  // three lines on a panel you're reading through a headset.
  //
  // The lines UPDATE IN PLACE (see `_startLiveDebug`): we keep their <text> nodes and
  // rewrite the content on a timer, rather than rebuilding the panel. Rebuilding would
  // fight with interaction (a slider drag torn out from under you), and — the bug that
  // prompted this — a readout that only refreshes when you REOPEN the panel is useless:
  // you switch a profiler on, the panel rebuilds instantly with the fresh (all-zero)
  // counters, and then you sit there watching frozen zeros while you fly.
  private _sourceRows(
    src: DebugPanelSource,
    bucket: LiveDebugRow[]
  ): Widget3d[] {
    let lines: string[]
    try {
      lines = src.lines()
    } catch (err) {
      lines = [`(threw: ${(err as Error)?.message ?? err})`]
    }
    // Name as a compact heading, then ONE wrapping block for the body — instead of a
    // 40px row per line that both wasted vertical space and clipped long lines.
    const block = textBlock3d({ lines, muted: true })
    const rows: Widget3d[] = [
      label3d({ text: src.name, bold: true, compact: true }),
      block,
    ]
    bucket.push({
      update: (next) => block.update(next),
      lines: () => src.lines(),
    })
    for (const action of src.actions ?? []) {
      rows.push(
        button3d({
          label:
            typeof action.label === 'function' ? action.label() : action.label,
          // A button's own label can change ('Profile tiles' → 'Profiling ON'), and a
          // button label isn't live text — so this one case does want a rebuild.
          handleClick: () => {
            handlerOf<() => void>(action, 'handleClick', 'onClick')?.()
            this._repaintPanels()
          },
        })
      )
    }
    return rows
  }

  // The debug tools the panel's icon bar offers: Perf Stats (when opted in) plus every
  // registered source, each with the icon its toggle shows. Perf Stats is core, not a
  // registered source, so it carries a fixed id + icon here; a source picks its own via
  // `DebugPanelSource.icon` (default `bug`). The icon bar toggles membership in
  // `_debugOpen`; only open tools render their rows below the bar (see `_panelWidgets`).
  /**
   * ICON-BAR GADGETS — items that FLIP something rather than expanding a
   * readout. Same bar as the debug tools (one row of small icons, one UI in
   * both presentations), different job.
   *
   * The glass gamepad's fade is production-correct and development-hostile: as
   * soon as a mouse or trackpad is present it goes away and doesn't come back,
   * so checking it on a laptop meant switching Chrome into responsive mode.
   * This is the one-tap way back, and it works in a headset too.
   */
  private _panelGadgets(): Array<{
    id: string
    name: string
    icon: string
    active: boolean
    handleClick: () => void
  }> {
    const out: Array<{
      id: string
      name: string
      icon: string
      active: boolean
      handleClick: () => void
    }> = []
    /*
    PAUSE WAS A ONE-WAY DOOR.

    The pause panel offers Continue, so pause was a state you could LEAVE and
    never ENTER: nothing in the standard panel paused a running scene (Tonio).
    `pause()`/`resume()` existed and were reachable only from JS or from
    `startPaused`, which is not a control, it is an initial condition.

    A gadget rather than a scenePanel row, so it lands in the flat panel AND the
    in-VR panel from one definition — and in a headset it is the only way to
    stop the world at all.

    `pauseWhenHidden` is deliberately untouched: that is an automatic policy,
    this is a manual act, and conflating them would make backgrounding the tab
    look like the user pressed something.
    */
    out.push({
      id: '__pause',
      name: this.paused ? 'Resume' : 'Pause',
      /*
      `play` when paused (what pressing it DOES), `pause` when running — a
      transport control shows its action, not its state.

      The BARE glyphs, not `playCircle`/`pauseCircle`: Tonio's call once both
      existed. (The solid-white-box bug was never about which variant — the
      names simply weren't in our generated icon set; see icon-names.test.ts.)
      */
      icon: this.paused ? 'play' : 'pause',
      active: this.paused,
      handleClick: () => {
        if (this.paused) this.resume()
        else this.pause('user')
        this._repaintPanels()
      },
    })
    /*
    SLOW MOTION, beside Pause: world time at a quarter speed and back. Input,
    the UI and the camera stay in real time; everything stepping by
    `sceneDelta` slows (physics, bipeds, vehicles, the sky).
    */
    const scale = Number((this as any).timeScale ?? 1)
    out.push({
      id: '__slow',
      name: scale < 1 ? 'Normal speed' : 'Slow motion',
      icon: 'clock',
      active: scale < 1,
      handleClick: () => {
        ;(this as any).timeScale = scale < 1 ? 1 : 0.25
        this._repaintPanels()
      },
    })
    const pad = this.querySelector('tosi-b3d-gamepad') as unknown as {
      hidden?: boolean
      setFade?: (on: boolean) => void
      fade?: string
    } | null
    if (pad?.setFade != null) {
      const forced = String(pad.fade) === 'off'
      out.push({
        id: '__gamepad',
        name: forced ? 'Gamepad: always shown' : 'Gamepad: auto-hides',
        icon: 'game',
        active: forced,
        handleClick: () => {
          pad.setFade?.(forced)
          this._repaintPanels()
        },
      })
    }
    return out
  }

  /** Popups currently open, by tool id — see `_syncDebugPopups`. */
  private _debugPopups = new Map<string, { close: () => void }>()
  private _rebuildingXrPanel = false
  /** The live rows each open popup registered, so closing can retire them. */
  private _popupLive = new Map<string, LiveDebugRow[]>()

  /**
   * The live flat panel's SVG, if the panel is open.
   *
   * Not held on a field, because the panel is REBUILT on every structural
   * change — a stored reference would be a handle to a detached element, which
   * is the same class of bug as the orphaned observers in `B3dChild`. Asking
   * the host each time cannot go stale.
   */
  /** The in-scene panel, while a session is running. See `_attachXrPanel`. */
  private _xrPanelEl: { popup?: PanelPopup } | null = null
  /** The XR rig node: the camera's parent, and what a vehicle carries. */
  private _xrRig: BABYLON.TransformNode | null = null

  private _livePanelEl(): { popup?: PanelPopup } | null {
    // In a session the in-scene panel IS the panel — the flat overlay is not
    // visible, so a popup opened against it would be opened into nothing.
    if (this.xrActive && this._xrPanelEl != null) return this._xrPanelEl
    if (this._flatScenePanel != null)
      return this._flatScenePanel.el as unknown as { popup?: PanelPopup }
    const host = this.parts?.scenePanelHost as HTMLElement | undefined
    if (host == null || host.hasAttribute('hidden')) return null
    /*
    `:scope > svg`, not `svg`. The panel host also contains the header BUTTONS,
    each of which is an svg icon, so a plain descendant query returns the first
    icon — an element with no `popup` on it and a 24-unit viewBox, which is
    exactly what it looked like: a panel that reported being 24 tall and
    silently refused to open anything.
    */
    return host.querySelector(':scope > svg') as unknown as {
      popup?: PanelPopup
    } | null
  }

  /**
   * Open and close debug popups so they match `_debugOpen`.
   *
   * Driven from the SET rather than from the click, so the icon's active state
   * and the popup cannot drift apart — including when a popup is dismissed from
   * outside, which clears the flag on its way out.
   *
   * There used to be an `afterRebuild` flag that dropped every handle on the
   * floor, because a repaint replaced the panel's SVG and took the popup layers
   * with it. It does not any more — `_openScenePanel` re-appends the holders —
   * so this is now purely a reconciliation and can run after any repaint.
   */
  private _syncDebugPopups(): void {
    for (const [id, handle] of [...this._debugPopups]) {
      if (this._debugOpen.has(id)) continue
      handle.close()
      this._retirePopup(id)
    }
    const panel = this._livePanelEl()
    if (panel?.popup == null) return
    for (const t of this._debugTools()) {
      if (!this._debugOpen.has(t.id) || this._debugPopups.has(t.id)) continue
      const bucket: LiveDebugRow[] = []
      let rows: Widget3d[] = []
      if (t.id === '__perf') rows = this._perfReadoutRows(bucket)
      else {
        const src = this._debugSources.find((x) => x.name === t.id)
        if (src) rows = this._sourceRows(src, bucket)
      }
      /*
      REGISTER THE LIVE BUCKET or the readout freezes the moment it opens —
      which is the failure the inline version's own comment already named: "a
      readout that only refreshes on reopen is useless — you'd switch a profiler
      on and then watch frozen zeros".
      */
      this._liveDebug.flat = [...this._liveDebug.flat, ...bucket]
      this._popupLive.set(t.id, bucket)
      this._startLiveDebug()
      this._debugPopups.set(
        t.id,
        panel.popup(
          {
            /*
            NO TITLE HERE. Both row builders already emit their own heading —
            `_sourceRows` opens with the source's name and `_perfReadoutRows`
            with its own labels — so passing one produced "Crowd" twice, one
            above the other, which is what it looked like.
            */
            handleClose: () => {
              // The panel being rebuilt under it is not a dismissal.
              if (this._rebuildingXrPanel) return
              // Dismissed from outside: clear the flag so the icon un-lights.
              this._debugOpen.delete(t.id)
              this._retirePopup(t.id)
              this._repaintPanels()
            },
          },
          ...rows
        )
      )
    }
  }

  /**
   * Forget a popup: its handle, and the live rows it registered.
   *
   * The rows matter — without this the ticker keeps rewriting text nodes that
   * are no longer on screen, and the list grows by a block every time the popup
   * is reopened.
   */
  private _retirePopup(id: string): void {
    this._debugPopups.delete(id)
    const gone = this._popupLive.get(id)
    if (gone == null) return
    this._liveDebug.flat = this._liveDebug.flat.filter((r) => !gone.includes(r))
    this._popupLive.delete(id)
  }

  private _debugTools(): Array<{
    id: string
    name: string
    icon: string
    quiet: boolean
  }> {
    type Tool = { id: string; name: string; icon: string; quiet: boolean }
    const tools: Tool[] = []
    if (perfDebugEnabled() || this.stats) {
      tools.push({
        id: '__perf',
        name: 'Perf Stats',
        icon: 'barChart2',
        quiet: false,
      })
    }
    for (const src of this._debugSources) {
      /*
      A source that THROWS while deciding whether it is quiet is not quiet:
      something is wrong and the icon should say so rather than going dark. Same
      reasoning as `_sourceRows`, which renders the throw as its content.
      */
      let quiet: boolean
      try {
        quiet = src.quiet?.() ?? false
      } catch {
        quiet = false
      }
      tools.push({
        id: src.name,
        name: src.name,
        icon: src.icon ?? 'bug',
        quiet,
      })
    }
    return tools
  }

  // Rewrite the debug lines' text in place. The flat panel is live DOM, so it just shows
  // the new text; the XR panel is rasterised by an SvgTexture that re-renders on its own
  // cadence (200ms), so it picks the change up for free. Cheap — a few string compares.
  private _startLiveDebug(): void {
    if (this._liveDebugTimer != null) return
    let last = 0
    this._liveDebugTimer = setInterval(() => {
      /*
      In a headset, once every two seconds. Each change redraws the popup's
      texture (SVG to image to canvas to GPU), and a Quest that hung within a
      minute or two with Perf Stats open ran indefinitely with it closed
      (#3135). The cause inside that path is not known; this only does it less.
      */
      const now = realNow()
      if (this.xrActive && now - last < 2000) return
      last = now
      // While a readout is up, so the loop is named when it forms and not
      // when something trips over it.
      this._reportParentCycle('tick')
      const rows = [...this._liveDebug.flat, ...this._liveDebug.xr]
      if (rows.length === 0) {
        clearInterval(this._liveDebugTimer as ReturnType<typeof setInterval>)
        this._liveDebugTimer = null
        return
      }
      for (const row of rows) {
        let lines: string[]
        try {
          lines = row.lines()
        } catch {
          continue
        }
        // The block re-wraps in place. A source that changes its LINE COUNT still needs a
        // rebuild to reflow the rows below it; the block itself stays live regardless.
        row.update(lines)
        // Sent before the redraw it causes: if that hangs, this is the last line.
        if (this.xrActive) sendDebug('live', { lines })
      }
    }, 400)
  }

  // Perf-stats readout for the scene panel (dual-presence: flat overlay AND the in-VR
  // panel), so it's reachable in a headset too — the reason it lives here and not in the
  // flat toolbar. Rendered only when its icon-bar toggle is on; the icon owns the
  // expand/collapse, so there's no header button here (there used to be, back when the
  // section was toggled by tapping its own header).
  //
  // IDENTICAL flat and in XR. `_refreshXrPanel` rewrites the XR panel in place, so a
  // control that exists in one presentation works in both — the panel is ONE ui with two
  // presentations.
  private _perfReadoutRows(bucket?: LiveDebugRow[]): Widget3d[] {
    const scaled = this._statsBaseScale != null
    /*
    LIVE, and with a frame TIME. These were three labels built once, so the fps
    shown was the fps at the instant the popup opened, and there was no time
    figure at all: reported from a headset as "no frame rate or rendering time
    numbers", which for a frozen number is fair.

    `cpu` is the time this thread spends in `scene.render()`, averaged over the
    last second. It is NOT the whole frame: the GPU's share is not measurable
    on a headset (no timer queries), so read it against `frame`, which is the
    interval between frames.
    */
    if (this._perfMeter == null || this._perfMeter.scene !== this.scene) {
      this._perfMeter?.dispose()
      this._perfMeter = new BABYLON.SceneInstrumentation(this.scene)
      this._perfMeter.captureFrameTime = true
    }
    const lines = (): string[] => {
      const d = this.debugState
      const meter = this._perfMeter
      const fps = this.engine?.getFps() ?? 0
      const frame = fps > 0 ? (1000 / fps).toFixed(1) : '?'
      const cpu = meter?.frameTimeCounter.lastSecAverage.toFixed(1) ?? '?'
      // `current`, read between frames, is the frame just finished. (The
      // per-second average stays 0: nothing feeds it for this counter.)
      const draws = meter?.drawCallsCounter.current ?? '?'
      return [
        `fps ${d.fps}  frame ${frame}ms  cpu ${cpu}ms${
          d.xrActive ? '  [XR]' : ''
        }`,
        this._sweepStep,
        `draws ${draws}  meshes ${this.scene.getActiveMeshes().length}`,
        `render ${d.renderWidth}×${d.renderHeight}  (css ${d.cssWidth}×${d.cssHeight})`,
        `dpr ${d.devicePixelRatio}  scale ${d.hardwareScaling?.toFixed(2)}  ${
          d.tier
        }  resizes ${d.resizeCount}`,
        this._svgCostLine(),
        this._heapLine(),
        ...(d.xrActive
          ? [
              `xr scale ${this.xrResolution.scale.toFixed(
                2
              )}  foveation ${this.xrResolution.foveation.toFixed(2)}`,
            ]
          : []),
      ]
    }
    const block = textBlock3d({ lines: lines(), muted: true })
    bucket?.push({ update: (next) => block.update(next), lines })
    return [
      label3d({ text: 'Perf Stats', bold: true, compact: true }),
      block,
      // Only once XR has been entered — meaningless before, and a row that says
      // nothing is a row that costs panel space in the place with least of it.
      ...(this._xrBaseline != null
        ? [
            label3d({
              text: (() => {
                const b = this._xrBaseline!
                const d = (now: number, was: number) => {
                  const n = now - was
                  return n === 0 ? '0' : n > 0 ? `+${n}` : String(n)
                }
                return `xr ${this._xrSessions}x  since 1st: mesh ${d(
                  this.scene.meshes.length,
                  b.mesh
                )} mat ${d(this.scene.materials.length, b.mat)} tex ${d(
                  this.scene.textures.length,
                  b.tex
                )}`
              })(),
              muted: true,
            }),
          ]
        : []),
      // Occlusion on or off, from inside any demo and any headset: what it
      // costs is a question about THIS scene on THIS device, and most demos
      // have no control for it. The label is what is RUNNING, not what was
      // asked for: `on` with the flat-only method does nothing in a headset,
      // and a button that said "on" there measured nothing.
      button3d({
        label: `Occlusion: ${
          this._ssaoRunning
            ? 'on'
            : isOff((this as any).ssao)
            ? 'off'
            : 'on, not running here'
        }`,
        handleClick: () => {
          ;(this as any).ssao = isOff((this as any).ssao) ? 'on' : 'off'
          this._applySsao()
          this._repaintPanels()
        },
      }),
      button3d({
        label: this._sweepNow ?? 'Cost sweep',
        handleClick: () => {
          if (this._sweepNow == null) void this.costSweep().catch(() => {})
        },
      }),
      button3d({
        label: this._sweepNow ?? 'Shader sweep',
        handleClick: () => {
          if (this._sweepNow == null)
            void this.costSweep(2, 'shaders').catch(() => {})
        },
      }),
      ...(this._sweepLines.length > 0
        ? [textBlock3d({ lines: this._sweepLines, muted: true })]
        : []),
      // One-tap discriminator: swap between the engine's real hardware scaling and
      // a coarse ×3 (≈1/9th the pixels). FPS recovers → fill/RTT is the bottleneck;
      // FPS unmoved → the resize machinery is. Fable's mobile-Safari test, in-panel.
      button3d({
        label: scaled ? 'Reset scale' : 'Force scale ×3',
        handleClick: () => {
          if (this.engine == null) return
          if (this._statsBaseScale == null) {
            this._statsBaseScale = this.engine.getHardwareScalingLevel()
            this.engine.setHardwareScalingLevel(3)
          } else {
            this.engine.setHardwareScalingLevel(this._statsBaseScale)
            this._statsBaseScale = null
          }
          this._repaintPanels()
        },
      }),
    ]
  }

  // The scene panel's widgets: a debug icon-bar (Perf Stats + each registered source,
  // one icon apiece), the expanded content of whichever debug tools are on, then the
  // author's `scenePanel` hook. Both the flat overlay and the XR panel build from THIS
  // one list — that's the "one UI, two presentations" contract, so the icon bar and its
  // expansion behave identically flat and in a headset. Toggling an icon mutates
  // `_debugOpen` and rebuilds both panels (a structural change — see `_repaintPanels`).
  //
  // The bar comes FIRST: a demo's own controls shouldn't be buried under diagnostics, but
  // the diagnostics must be one tap away (there's no console in VR). Collapsed by default,
  // so the panel opens clean; an expanded tool's status still sits ABOVE the author rows.
  /**
   * The icon-bar items — diagnostics first, then gadgets.
   *
   * ONE list, two presentations, which is the panel's standing contract. Flat,
   * these render as small round buttons in the panel's header beside the close
   * button; in XR they render as an `iconBar3d` row, because a headset has no
   * DOM header to put them in. Same items, same handlers, different layout —
   * that is presentation, not divergence. What must never happen is the LIST
   * differing between the two.
   *
   * They moved out of the flat panel BODY because a full-width row per toggle
   * is a lot of panel for a thing you press once: "the graph button in the
   * standard panel is a waste of space" (Tonio).
   */
  private _barItems(): Array<{
    icon: string
    title: string
    active: boolean
    /** Nothing to report — rendered dimmed in both presentations. */
    dim?: boolean
    handleClick: () => void
  }> {
    return [
      ...this._debugTools().map((t) => ({
        icon: t.icon,
        title: t.name,
        active: this._debugOpen.has(t.id),
        // Nothing to report → dimmed, so the bar answers before you press it.
        dim: t.quiet,
        handleClick: () => {
          if (this._debugOpen.has(t.id)) this._debugOpen.delete(t.id)
          else this._debugOpen.add(t.id)
          // No hand re-sync: `_repaintPanels` reconciles, and doing it here
          // as well is what let the two paths drift.
          this._repaintPanels()
        },
      })),
      // Gadgets last: diagnostics are the bar's main job, and a toggle moving
      // position as tools appear would be worse than a fixed tail.
      ...this._panelGadgets().map((g) => ({
        icon: g.icon,
        title: g.name,
        active: g.active,
        handleClick: g.handleClick,
      })),
    ]
  }

  /*
  DISPOSE THE SET WE ARE REPLACING.

  This method is re-invoked on every `_repaintPanels()` — pause/resume, any
  icon-bar toggle, every gear open — and each call builds a FRESH widget list
  from `scenePanel(this)`. The previous list becomes garbage, but a widget that
  registered itself somewhere does not: `spinner3d` joins a module-global
  ticker, so orphans kept animating forever and each held its whole SVG subtree
  alive through the tick closure. A leak, not merely wasted CPU.

  Reaping by `el.isConnected` looks tempting and is wrong: `SvgTexture`
  serialises the panel with `XMLSerializer` and never attaches it, so an
  IN-SCENE panel's SVG is legitimately detached for its whole life and would be
  culled while visible.

  The owner knows. It built the list, it is replacing the list, so it disposes
  the list — which is also why `dispose` is on `Widget3d` rather than on the
  one widget that currently needs it.
  */
  private _disposeWidgets(key: 'flat' | 'xr'): void {
    for (const w of this._builtWidgets[key]) {
      try {
        w.dispose?.()
      } catch {
        // A consumer's dispose must not take the repaint with it.
      }
    }
    this._builtWidgets[key] = []
  }

  private _builtWidgets: { flat: Widget3d[]; xr: Widget3d[] } = {
    flat: [],
    xr: [],
  }

  /*
  COLLAPSIBLE SECTIONS (board #2466): folded or tabbed by the exported
  `foldSections` (widgets3d), so a plain panel3d gets the same behaviour
  (board #2805). Here, in `_panelWidgets`, the one place both presentations
  get their rows, so flat and XR fold identically; the open set and the
  active tab persist per page.
  */
  private _foldSections(rows: Widget3d[]): Widget3d[] {
    return foldSections(rows, {
      key: `tosi-b3d:sections:${location.pathname}`,
      mode: this.panelSections,
      repaint: () => this._repaintPanels(),
    })
  }

  private _panelWidgets(xr = false): Widget3d[] {
    const key = xr ? 'xr' : 'flat'
    this._disposeWidgets(key)
    const all = this.scenePanel(this)
    const rows = this._foldSections(all)
    const items = this._barItems()
    if (items.length === 0) {
      // Nothing in the bar → nothing to stop, clear this presentation's live bucket.
      this._liveDebug[key] = []
      // ALL of them, folded ones included: they were built, so they dispose.
      this._builtWidgets[key] = all
      return rows
    }
    /*
    THE PANEL CARRIES THE AUTHOR'S ROWS AND NOTHING ELSE.

    Neither presentation gets its bar from here — flat renders the items as
    header buttons, XR builds an `iconBar3d` that also carries Exit VR and
    Re-seat — and as of now neither gets the DEBUG READOUTS from here either.

    DEBUG TOOLS ARE POPUPS, not rows.

    They used to be spliced in above the author's controls, so switching a
    diagnostic on shoved the whole panel down and changed its height — and the
    thing you were measuring moved while you measured it. Tonio: "Having one of
    these info panels push out the panel layout is a bad experience."

    The state still lives in `_debugOpen`; what changed is where the rows are
    rendered. `_syncDebugPopups` opens and closes them against that set, so the
    icon bar's active state and the popup cannot disagree.
    */
    this._builtWidgets[key] = all
    return rows
  }

  // window.requestAnimationFrame stops firing during an immersive XR session (the
  // session's own frame loop drives rendering instead). tosijs batches component
  // re-renders via rAF, so REACTIVE ATTRIBUTE BINDINGS — a skybox's `timeOfDay`
  // bound to a slider, say — silently stop updating in VR (an explicit `.observe()`
  // still fires, which is why some controls worked and others didn't). Intercept
  // rAF while in-session and flush its callbacks from the XR frame loop; restore on
  // exit. Babylon renders via the XR session's rAF (not window's), so its loop is
  // untouched. Returns a restore function.
  private _installXrRafPump(base: BABYLON.WebXRExperienceHelper): () => void {
    const realRaf = window.requestAnimationFrame.bind(window)
    const realCancel = window.cancelAnimationFrame.bind(window)
    let queue: Array<{ id: number; cb: FrameRequestCallback }> = []
    let nextId = 1
    window.requestAnimationFrame = (cb: FrameRequestCallback): number => {
      const id = nextId++
      queue.push({ id, cb })
      return id
    }
    window.cancelAnimationFrame = (id: number): void => {
      queue = queue.filter((q) => q.id !== id)
    }
    const pump = () => {
      if (queue.length === 0) return
      const due = queue
      queue = []
      const now = realNow()
      for (const { cb } of due) {
        try {
          cb(now)
        } catch (err) {
          console.warn('rAF callback failed during XR', err)
        }
      }
    }
    const obs = base.sessionManager.onXRFrameObservable.add(pump)
    return () => {
      base.sessionManager.onXRFrameObservable.remove(obs)
      window.requestAnimationFrame = realRaf
      window.cancelAnimationFrame = realCancel
      pump() // flush anything queued just before exit
    }
  }

  connectedCallback(): void {
    super.connectedCallback()
    this._blockPageZoom()
    /*
    A MOVE MUST NOT COST A SCENE.

    Re-parenting an element fires `disconnectedCallback` then
    `connectedCallback` in the SAME task, and this method builds an Engine and a
    Scene unconditionally — so a move built a second one. The first is then
    disposed, which disposes its materials, which deletes their shader programs,
    and the SURVIVING scene renders black while every uniform reads correct and
    `isReady()` returns true. `gl.isProgram()` is false and nothing says so.

    Reported by tosijs-3d-ensemble (#58) with the measurement that pins it: two
    Scene objects 43ms apart, a SkyMaterial rebuilt across the boundary, a
    `deleteProgram`, and NO add/remove recorded on the element itself — because
    the move was of an ancestor HOST, which disconnects everything in its shadow
    root. This is also the real cause of the intermittent black sky in #51.

    A move need not touch this element at all, and it is ordinary DOM: SPA
    routers, layout changes, re-parenting. So teardown is DEFERRED (see
    `disconnectedCallback`) and a reconnect in the same task simply cancels it —
    the scene never stopped existing, and there is nothing to rebuild.
    */
    if (this._teardownTimer != null) {
      clearTimeout(this._teardownTimer)
      this._teardownTimer = null
      return
    }
    // Reconnected after teardown already ran, or connected for the first time.
    // Input focus follows the pointer: hovering or pressing anywhere in this scene
    // (canvas OR the glass-gamepad / panel overlays, which are siblings of the canvas)
    // makes it the one shared keyboard/gamepad input drives — see hasInputFocus. Listen
    // on the host so overlay interaction counts; pointerdown bubbles from any child.
    this.addEventListener('pointerenter', () => this.takeInputFocus())
    // A system panel given new content (`el.svg = next`), #111.
    this.addEventListener('panelcontent', this._swapSystemContent)
    this.addEventListener('panelanchor', this._reanchorSystemPanel)
    this.addEventListener('pointerdown', () => this.takeInputFocus())
    window.addEventListener('pointerup', this._freeTouchSlot, true)
    window.addEventListener('pointercancel', this._freeTouchSlot, true)
    /*
    NEVER TWO ENGINES ON ONE CANVAS.

    Building a second WebGL context over a live one does not fail loudly: the
    first context's shader PROGRAMS become invalid while every uniform still
    reads back correctly, so the symptoms are meshes rendering white, a dark
    sky, or a half-loaded scene — four different-looking failures with one
    cause. tosijs-3d-ensemble measured exactly that (#56): correct sky uniforms
    alongside `gl.isProgram(program) === false`, `gl.getError() === 1282`, and
    two `webgl2` contexts created 207ms apart on a single load.

    `connectedCallback` should not reach here twice — a disconnect always
    schedules the teardown that a same-task reconnect cancels (#58) — but
    "should not" is what the old code relied on, and this failure is invisible
    until someone reads it off the GL context. Cheap to make impossible.
    */
    // A DISPOSED engine is what a genuine remove-then-re-add leaves behind —
    // the ordinary case, not this one, so it must not warn or dispose twice.
    if (this.engine != null && !this.engine.isDisposed) {
      console.warn(
        'b3d: an engine already exists on this element; disposing it before ' +
          'building another. A second WebGL context silently invalidates the ' +
          "first one's shader programs — white meshes, dark sky, or a " +
          'half-loaded scene.'
      )
      this._teardown()
    }
    /*
    A TORN-DOWN CANVAS IS SPENT. Teardown LOSES its context on purpose (see
    `loseContextOnDispose` below), and a lost context is what `getContext`
    hands back for that canvas forever after — so an element removed and later
    re-added would build its engine on a dead context and render nothing.
    Swap in a fresh canvas; `parts.canvas` re-queries once the old one is
    disconnected.
    */
    let cnv = this.parts.canvas as HTMLCanvasElement
    if (this._canvasSpent) {
      const fresh = document.createElement('canvas')
      for (const a of Array.from(cnv.attributes))
        fresh.setAttribute(a.name, a.value)
      cnv.replaceWith(fresh)
      cnv = fresh
      this._canvasSpent = false
    }
    cnv.addEventListener('wheel', (e) => e.preventDefault(), { passive: false })
    this.engine = new BABYLON.Engine(cnv, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      /*
      RELEASE THE CONTEXT, don't wait for GC (tosijs-3d#79). Babylon's
      `dispose()` keeps the WebGL context alive unless told otherwise, so every
      torn-down scene held one until its canvas was collected — and Chrome caps
      live contexts per page, so an SPA navigating back and forth stalled after
      about eight trips. Teardown only runs on a genuine removal (a move
      cancels it), so there is nothing here to keep.
      */
      loseContextOnDispose: true,
      // Babylon 8 makes the legacy audio engine opt-in (older versions
      // defaulted it on). Without this, `new BABYLON.Sound()` silently
      // no-ops — it never even fetches the file. b3d-sound depends on it.
      audioEngine: true,
    })
    /*
    SAY SO WHEN THE CONTEXT GOES.

    A lost WebGL context is the other way to get white meshes and a dark sky
    with every uniform still reading back correctly, and it is silent by
    default — the page simply renders wrong. Chrome caps contexts near 16 per
    page and force-loses the OLDEST, so an SPA that leaks engines takes out the
    scene you are currently looking at rather than the one that leaked.

    Naming it costs two listeners and turns an afternoon of bisecting into a
    line in the console and the debug ring — which is the only readout that
    exists inside a headset.
    */
    cnv.addEventListener('webglcontextlost', (e) => {
      e.preventDefault() // allows a restore; without this it is permanent
      console.warn(
        'b3d: WebGL context LOST. Meshes may render white or the sky dark. ' +
          'Usually too many live contexts on one page (browsers cap ~16 and ' +
          'drop the oldest) — check for scenes that were never disposed.'
      )
      this.logDebug('gl', { event: 'context-lost' })
    })
    cnv.addEventListener('webglcontextrestored', () => {
      console.warn('b3d: WebGL context restored.')
      this.logDebug('gl', { event: 'context-restored' })
    })

    this.scene = new BABYLON.Scene(this.engine)
    this._applyClearColor()
    this.scene.collisionsEnabled = true
    this.scene.gravity = new BABYLON.Vector3(0, -9.81 / 60, 0)
    // Nearest first, the sky last: a covered pixel is rejected before it is
    // shaded (see draw-order.ts). Babylon's default is creation order.
    const order = nearFirst(this.scene)
    this.scene.setRenderingOrder(0, order, order)

    // Seed device quality BEFORE any child component builds, so terrain/shadows/
    // reflections resolve their `auto` defaults against the right budget on frame 1.
    // Never let quality setup break the scene — fall back to the safe default.
    try {
      this._setupQuality()
    } catch (err) {
      console.warn('b3d quality setup failed; using default profile', err)
    }

    const init = async () => {
      if (this.sceneCreated !== noop) {
        await this.sceneCreated(this, BABYLON)
      }
      if (this.scene.activeCamera === undefined) {
        const DEG = Math.PI / 180
        const camera = new BABYLON.ArcRotateCamera(
          'default-camera',
          -Math.PI / 2, // alpha (facing -Z)
          60 * DEG, // beta (~30° elevation to start)
          8, // radius
          BABYLON.Vector3.Zero(),
          this.scene
        )
        // beta is measured from straight-up: elevation = 90° - beta. Clamp it so
        // the camera stays between min/maxElevation above the horizon (never
        // under the ground), and clamp radius so you can't zoom out to orbit or
        // in through the scene.
        camera.upperBetaLimit = (90 - this.minElevation) * DEG
        camera.lowerBetaLimit = (90 - this.maxElevation) * DEG
        camera.lowerRadiusLimit = this.minDistance
        camera.upperRadiusLimit = this.maxDistance
        camera.attachControl(cnv, false)
        // Shift-drag, a trackpad drag or two fingers pan; the wheel or a pinch zooms (#52).
        touchOrbit(camera)
        this.setActiveCamera(camera)
      }
      this.gui = new GUI.GUI3DManager(this.scene)
      this.engine.runRenderLoop(this._update)

      // Mount the glass gamepad (if requested) before releasing descendants, so
      // b3dInputFocus sees its source when it wires up input.
      this._setupGamepad()

      // Scene is ready. Release any B3dChild components that connected and asked
      // (whenReady) before the scene was up — they insert themselves now. Anything
      // connecting later self-registers and runs immediately.
      // Errors, on the one readout that exists in a headset — see
      // _installErrorCapture. Registered once, with the scene.
      this._errorCaptureOff = this._installErrorCapture()
      this.addDebugSource({
        name: 'errors',
        quiet: () => this._errors.length === 0,
        lines: () => {
          if (this._errors.length === 0) return ['none']
          const last = this._errors[this._errors.length - 1]
          return [
            `${this._errors.length} seen · last f${last.f} @${last.at}`,
            last.msg.slice(0, 46),
          ]
        },
        actions: [
          {
            label: 'Clear',
            handleClick: () => {
              this._errors = []
              this._repaintPanels()
            },
          },
        ],
      })
      // A picture of the view, from the panel, flat or in a headset.
      this.addDebugSource({
        name: 'Screenshot',
        icon: 'download',
        lines: () => ['Save the view as a PNG.'],
        actions: [
          {
            label: 'With panels',
            handleClick: () => void this._screenshotFromPanel(true),
          },
          {
            label: 'Scene only',
            handleClick: () => void this._screenshotFromPanel(false),
          },
        ],
      })
      this._sceneReady = true
      // Pause plumbing: watchers first, then the initial state — so a scene
      // that comes up paused already has its panel and its listeners.
      this._watchPause()
      if (this.startPaused) this.pause('start')
      const queued = this._readyQueue
      this._readyQueue = []
      for (const cb of queued) cb()

      // Offer WebXR (non-blocking — it must not delay the canvas reveal).
      void this._setupXR()

      // Mount the gear-toggled DOM-overlay settings panel (flat screens). The
      // in-scene XR copy is built on session entry.
      this._setupScenePanel()
      this._setupFullscreen()

      // NPC nameplates (above non-player bipeds), in flat AND XR.
      this._setupNameplates()

      // Fade in canvas once all pending file loads complete and shaders compile.
      // Falls back to revealing after assets load even if shaders are still
      // compiling, to avoid an indefinitely hidden canvas.
      const spinner = this.parts.spinner as HTMLElement
      let revealed = false
      const reveal = () => {
        if (revealed) return
        revealed = true
        mark('shown')
        // Frame times for the 20 s after showing, a second at a time.
        const frames: number[] = []
        let count = 0
        let second = realNow()
        const watch = this.engine.onEndFrameObservable.add(() => {
          count++
          const now = realNow()
          if (now - second < 1000) return
          frames.push(Math.round((now - second) / count))
          count = 0
          second = now
          if (frames.length >= 20) {
            this.engine.onEndFrameObservable.remove(watch)
            sendDebug('load', { ...timeline, frameMsAfter: frames.join(' ') })
          }
        })
        // Where those frames go, by callback, for two minutes (debug sink only).
        if (debugSink.url !== '')
          frameProfile(this.scene, {
            seconds: 120,
            every: 5,
            report: (s) => sendDebug('frame', { ...s }),
            extra: () => [
              [
                'xrFrame',
                this.xrHelper?.baseExperience?.sessionManager
                  ?.onXRFrameObservable,
              ],
            ],
          })
        cnv.classList.add('ready')
        // Not a spinner a page is holding up for its own loading.
        if (!this._loadingHeld) spinner.classList.add('hidden')
        // Enable the toolbar only now the scene is up — the gear + Enter VR
        // shouldn't be clickable while the scene is still loading.
        const gearBtn = this.parts.scenePanelGear as
          | HTMLButtonElement
          | undefined
        const vrBtn = this.parts.enterVrButton as HTMLButtonElement | undefined
        if (gearBtn) gearBtn.disabled = false
        if (vrBtn) vrBtn.disabled = false
      }
      // Holds (`holdLoading`) and a streaming terrain keep the scene hidden,
      // for at most this long: a hold never released must not hide it forever.
      const started = realNow()
      const giveUp = started + 20000
      // A load's timeline for the debug sink: when files, holds and terrain
      // were done, when it was shown, and the frame times after (a headset's
      // load cannot be watched any other way).
      const timeline: Record<string, number | string> = {}
      const mark = (what: string) => {
        if (!(what in timeline))
          timeline[what] = Math.round(realNow() - started)
      }
      let lastHolds = ''
      const checkReady = () => {
        const holds = [...this._loadingHolds]
          .map((h) => h.replace(/#\d+$/, ''))
          .join(', ')
        if (holds !== lastHolds && lastHolds !== '') mark(`built ${lastHolds}`)
        lastHolds = holds
        if (this.scene.getWaitingItemsCount() === 0) mark('files')
        if (this._loadingHolds.size === 0) mark('holds')
        if (!this._terrainBusy()) mark('terrain')
        const built = this._loadingHolds.size === 0 && !this._terrainBusy()
        if (!built && realNow() > giveUp) {
          timeline.gaveUp = holds || 'terrain'
          console.warn(
            `tosi-b3d: shown while still building (${holds || 'terrain'})`
          )
        }
        if (
          this.scene.getWaitingItemsCount() === 0 &&
          (built || realNow() > giveUp)
        ) {
          /*
          And every shader compiled: Babylon skips a mesh whose shader is not
          ready, so a scene shown early fills in piece by piece (a Quest took
          14 s after a reveal that waited 0.5 s for this). The fallback is for a
          shader that never finishes, not a slow device.
          */
          this.scene.executeWhenReady(() => {
            mark('shaders')
            reveal()
          })
          setTimeout(() => {
            if (!revealed) timeline.shadersGaveUp = 1
            reveal()
          }, 10000)
        } else {
          setTimeout(checkReady, 100)
        }
      }
      // Start checking after a brief delay to let child components begin loading
      setTimeout(checkReady, 100)
    }

    init()
  }

  // WebXR is on by default. The `no-xr` attribute opts out; a `setupXr` hook
  // overrides the whole flow. Otherwise, when an immersive-vr session is
  // supported, mount a floating Enter/Exit-VR button wired to a default XR
  // experience (its own UI suppressed so the button matches the host theme).
  /** Request (or stop requesting) the layers feature with multiview, for the next session. */
  private _applyXrMultiview(): void {
    const fm = this.xrHelper?.baseExperience?.featuresManager
    if (fm == null) return
    const name = BABYLON.WebXRFeatureName.LAYERS
    const has = fm.getEnabledFeature(name) != null
    if (!isOff(this.xrMultiview) && !has) {
      try {
        // Optional (not required): a browser without layers still enters VR.
        fm.enableFeature(
          name,
          'latest',
          { preferMultiviewOnInit: true },
          true,
          false
        )
      } catch (err) {
        console.warn('tosi-b3d: multiview unavailable', err)
        sendDebug('xr', { multiview: 'enable failed', error: String(err) })
      }
    } else if (isOff(this.xrMultiview) && has) fm.disableFeature(name)
  }

  /** What multiview is doing: off, asked for, or drawing. */
  private _multiviewState(inSession = this.xrActive): string {
    const f = this.xrHelper?.baseExperience?.featuresManager?.getEnabledFeature(
      BABYLON.WebXRFeatureName.LAYERS
    ) as any
    if (f == null)
      return isOff(this.xrMultiview)
        ? 'off'
        : 'not requested (failed to enable)'
    if (!inSession) return 'requested for the next session'
    return f._isMultiviewEnabled === true
      ? 'ON (both eyes in one pass)'
      : 'requested, not available here'
  }

  private async _setupXR(): Promise<void> {
    if (this.noXr) return
    if (this.setupXr !== noop) {
      await this.setupXr(this, BABYLON)
      return
    }
    if (navigator.xr == null) return
    let supported: boolean
    try {
      supported = await navigator.xr.isSessionSupported('immersive-vr')
    } catch {
      supported = false
    }
    if (!supported || this.xrHelper != null) return

    // Creating the default experience can steal scene.activeCamera (switching
    // to the XR camera on creation), which blanks the flat view before you ever
    // enter VR. Capture the flat camera and restore it; enterXRAsync swaps
    // cameras properly when you actually enter, and restores on exit.
    const flatCamera = this.scene.activeCamera
    const xr = await this.scene.createDefaultXRExperienceAsync({
      disableDefaultUI: true,
      /*
      THE RIGHT HAND POINTS FIRST. Babylon points with ONE controller, the
      first to connect (often the left), and the first trigger press on the
      other one only SWITCHES to it: the press itself is dropped. So a
      right-handed player's first press in a session did nothing (seen in the
      emulated headset as "the first press after entry is lost"). The left
      hand still takes over the first time it is used.
      */
      pointerSelectionOptions: { preferredHandedness: 'right' } as any,
    })
    this.xrHelper = xr
    this._applyXrMultiview()
    try {
      // An earlier build kept a choice here, and on a Quest 'on' is a grey view.
      localStorage.removeItem('tosi-b3d-xr-multiview')
    } catch {
      /* private mode */
    }
    /*
    THE POINTER DOES NOT PICK WHAT YOU ARE SITTING IN.

    In a cockpit the hull is all around you and nearer than any panel, so the
    controller's ray landed on the canopy: the laser dot sat on the cockpit,
    and on a Quest the panel behind it could be hovered but not pressed or
    dragged, with no way out of the session (Tonio, 0.9.0 run-through, board
    #3198). The panel re-picks past an occluder, which is why an emulated
    controller could still press it, but that is a second pick standing in for
    a first one that should never have hit the hull.

    So anything beneath the node the rig is parented to (the vehicle you are
    riding) is not a pointer target. The rig's own children (the panels) still
    are. On foot, or in a chase view, the rig is parented to nothing you could
    be inside, and nothing changes.
    */
    if (xr.pointerSelection != null) {
      xr.pointerSelection.raySelectionPredicate = (m) =>
        m.isEnabled() &&
        m.isVisible &&
        m.isPickable &&
        // Answered by a ray proxy (a city's buildings and roads): Babylon
        // walks every thin-instance copy for each controller's ray, every frame.
        m.metadata?.b3dRayProxy !== true &&
        !insideCarrier(m, this._xrRig)
    }
    if (xr.pointerSelection != null) pressWithEitherHand(xr, this.scene)
    this._unpickLayers?.()
    this._unpickLayers = pickLayersFirst(xr, this.scene)
    if (flatCamera != null && this.scene.activeCamera !== flatCamera) {
      this.scene.activeCamera = flatCamera
    }
    const base = xr.baseExperience
    if (base == null) return
    base.onStateChangedObservable.add((state) => {
      // A choice made in a session applies from the next one.
      if (state === BABYLON.WebXRState.NOT_IN_XR) this._applyXrMultiview()
      if (state === BABYLON.WebXRState.IN_XR)
        sendDebug('xr', { multiview: this._multiviewState(true) })
      if (state !== BABYLON.WebXRState.IN_XR) return
      // The flat settings panel rides the flat camera; the headset has its own.
      this._disposeFlatScenePanel()
      this._xrSessions += 1
      // Baseline from the FIRST entry only — the point is the trend across
      // sessions, so re-baselining each time would hide exactly what we want.
      this._xrBaseline ??= {
        mesh: this.scene.meshes.length,
        mat: this.scene.materials.length,
        tex: this.scene.textures.length,
      }
    })

    const vrButton = this.parts.enterVrButton as HTMLButtonElement
    vrButton.addEventListener('click', async () => {
      try {
        if (this.xrActive) {
          await base.exitXRAsync()
        } else {
          // Flush any pending tosijs renders WHILE flat rAF still works, so no
          // component's per-element `_renderQueued` flag is left stranded when the
          // immersive session suspends window.requestAnimationFrame. Otherwise a
          // component with a render already queued at entry (e.g. the skybox, whose
          // realtimeScale setInterval constantly queues one) never schedules
          // another render in-session and its reactive bindings freeze — the
          // "time-of-day slider dead on first XR entry" bug.
          await updates()
          await base.enterXRAsync('immersive-vr', 'local-floor')
        }
      } catch (err) {
        console.warn('XR session change failed', err)
      }
    })
    // A live map of XR controller component states (thumbsticks/buttons), built
    // once so we don't double-register listeners across sessions.
    const controllers: TosiXRControllerMap = xrControllers(xr)

    // Feed the XR controllers through the same VirtualGamepad spine as the
    // keyboard/glass gamepad: add an XrGamepadSource to the scene's input focus,
    // so the focused controllable (biped/aircraft/car) is driven by the
    // controllers in VR through its existing mapping. No per-entity XR code.
    const focus = this.querySelector('tosi-b3d-input-focus') as {
      inputMappedProvider?: { addSource(s: unknown): void }
      focused?: { mesh?: BABYLON.AbstractMesh }
    } | null
    const xrSource = new XrGamepadSource(controllers)
    focus?.inputMappedProvider?.addSource(xrSource)
    // Standalone <tosi-b3d-controller>s self-wire their own input, so feed them the XR
    // controllers too (same VirtualGamepad spine — the VR sticks/triggers drive them).
    for (const c of Array.from(this.querySelectorAll('tosi-b3d-controller'))) {
      ;(c as any).inputMappedProvider?.addSource(xrSource)
    }
    // The default experience enables teleportation; we drive locomotion
    // ourselves, so remove it to stop the thumbstick fighting our movement.
    try {
      xr.teleportation?.dispose()
    } catch {
      /* teleportation may not have been enabled */
    }

    // In a session the WebXR headset camera takes over (Babylon switches
    // scene.activeCamera to it); the flat-screen orbit camera is restored on
    // exit. On entry we stand the viewer on a walkable floor and wire stick
    // locomotion; on exit we tear it down.
    let xrSession: { dispose: () => void } | undefined
    let restoreRaf: (() => void) | undefined
    base.onStateChangedObservable.add((state) => {
      this.xrActive = state === BABYLON.WebXRState.IN_XR
      // SSAO must be gone BEFORE the headset camera becomes active, which
      // happens during ENTERING_XR, ahead of IN_XR.
      this._xrPresenting = state !== BABYLON.WebXRState.NOT_IN_XR
      this._applySsao()
      // Keep the xrColor icon as the button face; the title carries the state
      // (the flat button isn't visible in-session anyway). Setting textContent
      // here would wipe the icon.
      vrButton.title = this.xrActive ? 'Exit VR' : 'Enter VR'
      if (state === BABYLON.WebXRState.IN_XR) {
        /*
        ENTERING VR UNPAUSES. Putting the headset on IS the resume.

        A world you deliberately stepped INTO has no good reason to be frozen,
        and the state was actively broken: entering a paused scene showed no
        pause panel at all in-session (it exists, it is just never presented on
        a path where the pause pre-dates the session), so you arrived in a
        stopped world with no visible way out — the panel's toggle was the only
        escape, and only if you knew to look.

        This removes the state rather than teaching the panel to appear in it,
        which is the smaller and more honest fix (Tonio: "arguably entering VR
        should unpause a paused scene"). It is also symmetric with
        `enterXrOnResume`, which already couples resume → enter.

        Safe against re-entry: `xrActive` is set above, so `resume()`'s
        `enterXrOnResume && !xrActive` guard cannot fire a second entry. And you
        can still pause in VR — the panel's transport toggle does it.
        */
        if (this.paused) this.resume()
        // Snapshot the flat orbit camera so exit can restore it (see the field
        // note). Babylon only mutates the non-XR camera on the way OUT, so the
        // angles here are still the pre-entry ones.
        const fc = this.camera
        this._flatOrbitState =
          fc instanceof BABYLON.ArcRotateCamera
            ? {
                alpha: fc.alpha,
                beta: fc.beta,
                radius: fc.radius,
                target: fc.target.clone(),
              }
            : null
        // Stereo doubles fill — drop to the XR render-scaling budget on entry, and
        // back to the flat one on exit (the cheap lever that's safe to change live).
        this._applyHardwareScaling(true)
        this._applyXrResolution()
        this._applyGroundDetail(true)
        // Same reason: the XR tier is a smaller ambient pool, so re-divide it. A snowstorm
        // that was honest on a monitor may only afford to be nothing at all in a headset.
        this._reallocAmbient()
        // And re-arm the warm-up: XR entry is the single worst frame-rate moment there is
        // (stereo shaders compiling, the session spinning up). Judging the pool there and
        // shedding IRREVERSIBLY is how you lose the weather to a hitch that's already over.
        this._ambientWarmupMs = 10000
        this._ambientBadSamples = 0
        // Keep tosijs's rAF-batched reactive bindings flushing while in-session.
        restoreRaf ??= this._installXrRafPump(base)
        xrSession ??= this._startDefaultXrExperience(base, controllers)
        // A modal open on the monitor comes into the headset with you.
        this._representModals()
      } else if (state === BABYLON.WebXRState.NOT_IN_XR) {
        // Restore the orbit camera Babylon has just carried the (low) headset
        // pose into. Runs after Babylon's restore; setting target then angles
        // overrides the setPosition() it did, and ArcRotate recomputes position
        // from these next frame. Free cameras keep the carry-back (state is null).
        const fc = this.camera
        const s = this._flatOrbitState
        if (s != null && fc instanceof BABYLON.ArcRotateCamera) {
          fc.setTarget(s.target)
          fc.alpha = s.alpha
          fc.beta = s.beta
          fc.radius = s.radius
        }
        this._flatOrbitState = null
        // The pointer that pressed Enter VR never left the flat bar, so it
        // came back with that button still lit until the bar was hovered.
        ;(
          this._sceneBar?.svg as unknown as {
            handlePointer?: (k: 'leave', x: number, y: number) => void
          }
        )?.handlePointer?.('leave', 0, 0)
        this._applyHardwareScaling(false)
        this._applyGroundDetail(false)
        this._xrViewportOff?.()
        this._xrViewportOff = null
        this._reallocAmbient()
        xrSession?.dispose()
        xrSession = undefined
        restoreRaf?.()
        restoreRaf = undefined
        /*
        LEAVING VR PAUSES — for every scene, not just the ones that opted in.

        Taking the headset off is a departure, not a view change: the world
        should not run on without you while it sits on your desk.

        This used to require `enterXrOnResume`, on the reasoning that a flat
        scene which merely VISITED XR should not suddenly acquire a pause panel
        on the way out. Superseded by the symmetry, which is the stronger
        argument (Tonio: "just as entering VR should unpause, exiting VR should
        probably pause"): one gesture means resume, its inverse means stop, and
        a rule you have to opt into is not a pair. `enterXrOnResume` still owns
        the other direction — resume → enter — so the loop it describes is
        unchanged; it just no longer gates this half.

        Guarded on `paused` so exiting a scene you paused from INSIDE the headset
        does not re-enter the pause it is already in.
        */
        // …and one open in the headset comes back out to the monitor.
        this._representModals()
        if (!this.paused) this.pause('xr')
      }
    })
    // XR is available — reveal the Enter VR button (grouped next to the gear in the
    // scene toolbar, so it's obvious when VR is available and never clipped by the
    // panel's scroll).
    vrButton.hidden = false
  }

  // The built-in XR experience used when no `setupXr` hook is supplied: stand
  // the viewer on a grid floor near the scene, walk with the left stick
  // (relative to head facing), turn with the right stick, and change altitude
  // with the BUMPERS (left down / right up, analog — the same hand logic as
  // brake/accelerate on the triggers). The right stick's vertical still flies
  // when nothing is claiming it, but the bumpers are the reliable path: that
  // axis doubles as panel scroll. A rig
  // TransformNode is the movable anchor — live head tracking applies as a local
  // transform on top. Returns a disposer that tears everything down on exit.
  private _startDefaultXrExperience(
    base: BABYLON.WebXRExperienceHelper,
    controllers: TosiXRControllerMap
  ): { dispose: () => void } {
    const scene = this.scene
    const cam = base.camera

    const rig = new BABYLON.TransformNode('xr-rig', scene)
    this._xrRig = rig
    refuseOwnAncestor(rig)
    /*
    ARRIVE WHERE YOU WERE LOOKING FROM — height included.

    This used to take the flat camera's x/z and pin y to ZERO: "stand on the
    floor". That is right for a scene you walk around in and wrong for the ones
    this framework is mostly made of, where the flat camera ORBITS a subject from
    several metres up and looks down at it. Dropping to the floor puts you under
    the thing you came to look at. Tonio: "your world position is always quite
    low."

    So seed the height too, minus a nominal eye height, because `local-floor`
    adds the viewer's REAL head height on top — the aim is for your head to land
    where the flat camera was, not the rig's origin. Floored at 0 so a camera
    that dipped below ground does not bury you.

    (Orientation is deliberately NOT seeded. Where you look is your head's
    business — pinning it fights the headset — and the height was the part that
    actually broke the framing.)
    */
    const p = this.camera?.position
    const NOMINAL_EYE = 1.6
    rig.position.set(
      p?.x ?? 0,
      Math.max(0, (p?.y ?? NOMINAL_EYE) - NOMINAL_EYE),
      p?.z ?? -this.minDistance * 2
    )
    cam.parent = rig

    // Reference frames for spatial UI (body/neck/face follow the head; rig/world
    // are locomotion/play-space). Updated each XR frame below; UI parents to one.
    const frames = new XrFrames(scene, rig, cam)
    frames.attachInput(this.xrHelper?.input) // hand/wrist frames follow the grips
    this.xrFrames = frames

    // Body-anchored panels (inventory over the shoulders, quick-access at the
    // waist), pinned to their frame and revealed by looking toward them.
    // Whatever was open flat goes; it opens again from the headset's bar.
    // An app's chrome (exclusive="off") comes along.
    this._carrySystemPanels(true)
    const bodySpecs = this.bodyPanels(this).filter(
      (spec) => spec.anchor !== 'system-bar'
    )
    const bodyPanels = bodySpecs.map((spec) =>
      attachFramePanel(
        scene,
        cam,
        frames.get(spec.frame ?? 'body'),
        presentationSpec(spec, false)
      )
    )

    // NPC nameplates run in a GENERAL manager now (flat + XR) — see
    // _setupNameplates() — since a frame panel gaze-reveals off the active camera
    // and works on a monitor too, not just in a headset. Not created here.

    /*
    THE SETTINGS PANEL IS ASKED FOR, as it is flat. It used to hang 60° up
    for the whole session; now the scene's TOOLBAR sits there (below) and
    opens it, carries Exit VR, and brings the body panels (inventory,
    journal) front and centre. Tonio: "more consistent and intuitive", and
    the rig positions are kept free for other things.

    Rebuildable while open: a structural change (expanding the perf stats, a
    debug source adding a line) builds a NEW panel element into the same
    plane (`rebuild`), because the panel's pointer/scroll closures can't
    survive having their widgets swapped underneath them. Live numbers update
    the <text> nodes in place.
    */
    let panel: ReturnType<typeof this._attachXrPanel> | null = null
    const closeXrPopups = () => {
      /*
      AND THE POPUPS GO WITH IT. The plane stays now, but the rows a popup
      came from (and its live debug lines) are rebuilt, so it is closed and
      `_syncDebugPopups` re-opens whatever `_debugOpen` still asks for against
      the new panel. (A popup still blinks on a structural rebuild here.)

      CLOSED, not dropped. Dropping the handles relied on every popup being a
      child of the plane and dying with it. One that was not (torn off, or
      asked for and not yet mounted when the next rebuild came) stayed in the
      scene with nothing holding it: a second Perf Stats that never updated
      and could not be closed, seen on a Quest after a sweep. Closed BEFORE
      the plane goes, and quietly: this is not the user closing the tool.
      */
      this._rebuildingXrPanel = true
      try {
        for (const [id, handle] of [...this._debugPopups]) {
          handle.close()
          this._retirePopup(id)
        }
      } finally {
        this._rebuildingXrPanel = false
      }
    }
    const closeXrPanel = () => {
      if (panel == null) return
      closeXrPopups()
      panel.dispose()
      panel = null
      this._xrPanelEl = null
    }
    const toggleXrPanel = () => {
      if (panel != null) return closeXrPanel()
      // One at a time: an exclusive system panel (the inventory) goes.
      this._closeSystemPanels(true)
      panel = this._attachXrPanel(base, frames.eye)
    }
    this._closeXrSettings = closeXrPanel
    this._refreshXrPanel = () => {
      if (panel == null) return
      this._whenReleased(this._xrPanelEl as Element | null, () => {
        if (panel == null) return
        closeXrPopups()
        panel.rebuild()
      })
    }
    const xrBar = this._xrToolbar(scene, cam, frames, {
      settings: toggleXrPanel,
      exit: () => void this.xrHelper?.baseExperience?.exitXRAsync(),
    })

    // A subtle grid floor — something to stand on and judge motion against.
    // Show the grid when `xr-grid="on"`, or `"auto"` (default) UNLESS a player
    // entity is driving the rig — a focused controllable (biped/car/aircraft via
    // input-focus) is its own "non-default rig", so auto hides the grid there.
    // `"off"` always hides it. (A custom setupXr never reaches this code at all.)
    const focus = this.querySelector('tosi-b3d-input-focus') as {
      focused?: unknown
    } | null
    const playerDriven = focus?.focused != null
    const showGrid =
      this.xrGrid === 'on' || (this.xrGrid === 'auto' && !playerDriven)
    let ground: BABYLON.Mesh | undefined
    let grid: GridMaterial | undefined
    if (showGrid) {
      ground = BABYLON.MeshBuilder.CreateGround(
        'xr-ground',
        { width: 200, height: 200 },
        scene
      )
      ground.isPickable = false
      /*
      UNDER THE SUBJECT, not at the world origin — and not under the viewer.

      Pinned to (0,0) it was a mystery grid off in the distance for any scene
      whose subject isn't at the origin (VR-only, since it doesn't exist flat).
      Moving it under the RIG then made it "overlap but not centred": an orbit
      camera sits `radius` away from what it looks at, so a floor under your
      feet is a floor beside the volcano.

      The subject is the orbit camera's TARGET — the same point the flat view
      frames — so the grid lands under the thing the demo is about. Falls back
      to the rig for a non-orbit camera, where "under the viewer" IS the floor.
      Stays in WORLD space (not parented) so it remains a motion reference.
      */
      const flatCam = this.camera as BABYLON.ArcRotateCamera | null
      const subject = (flatCam as any)?.target as BABYLON.Vector3 | undefined
      ground.position.x = subject?.x ?? rig.position.x
      ground.position.z = subject?.z ?? rig.position.z
      // Drop a smidge BELOW y=0 so it doesn't z-fight ("z-chase") with a scene
      // ground / water / terrain at 0 — and so the real scene ground wins visually
      // (the grid only shows through where there's no ground, rather than covering
      // it). Imperceptible underfoot (you stand on the local-floor at 0).
      ground.position.y = -0.05
      grid = new GridMaterial('xr-ground-grid', scene)
      grid.majorUnitFrequency = 5
      grid.minorUnitVisibility = 0.4
      grid.gridRatio = 1
      grid.mainColor = new BABYLON.Color3(0.09, 0.11, 0.15)
      grid.lineColor = new BABYLON.Color3(0.25, 0.45, 0.7)
      grid.opacity = 0.7
      ground.material = grid
    }

    /*
    LOCOMOTION IS SCALE-BLIND — and that reads as "the left stick is broken".

    2.5 m/s is right for a room-sized demo and invisible in a landscape one. The
    volcano chunk is 128 * 4 = 512 units across and the rig sits hundreds of
    units out, so walking moved ~0.5% of the scene per second: indistinguishable
    from nothing. TURNING is angular (rad/s) and therefore scale-free, which is
    exactly why the right stick felt fine while the left felt dead — the
    symptom that sent us hunting a missing controller (Tonio, VR pass 2; the
    readout showed the left stick present and reporting 0.00).

    Scale to the orbit camera's radius, which is what the flat view already uses
    to frame the scene. The clamp floor is 1 so a small demo keeps TODAY's
    numbers exactly; the ceiling stops a galaxy-scale camera from making a step
    a light-year.
    */
    const flatRadius = (this.camera as BABYLON.ArcRotateCamera | null)?.radius
    const MOVE_SCALE = Math.min(20, Math.max(1, (flatRadius ?? 8) / 8))
    const HORIZ_SPEED = 2.5 * MOVE_SCALE // metres/sec
    const VERT_SPEED = 2.0 * MOVE_SCALE
    const TURN_SPEED = 2.0 // radians/sec at full deflection
    const DEAD = 0.15
    const CHASE_HEIGHT = 2.5 // chase-cam height above a piloted entity
    // The scene's input focus (if any): when it has a focused controllable, the
    // XR controllers drive THAT (via XrGamepadSource → its mapping) and the rig
    // chase-follows it instead of free walk/fly. Looked up once; .focused is live.
    const focusEl = this.querySelector('tosi-b3d-input-focus') as {
      focused?: {
        getCameraTarget?: () => BABYLON.Node | null
        cameraView?: string
        crashed?: boolean
        eyeHeight?: number
        cockpitForward?: number
        chaseMinHeight?: number
        chaseHeight?: number
        chaseDistance?: number
        getHeadPosition?: () => BABYLON.Vector3 | null
        /** Level position+heading node to PARENT the chase rig to — see
         * `B3dControllable.getChaseAnchor`. Absent/null ⇒ the eased
         * world-space follow below. */
        getChaseAnchor?: () => BABYLON.TransformNode | null
        stickAims?: boolean
        chaseView?: () => number
        lastInput?: {
          cameraZoom: number
          cameraPeek: number
          lookX?: number
          lookY?: number
        }
      }
    } | null
    let last = realNow()
    // Reused scratch vectors — never allocate inside the per-frame loop (in XR
    // that runs at 72-120fps, and the garbage was driving the perf creep).
    const fwd = new BABYLON.Vector3()
    const side = new BABYLON.Vector3()
    const head = new BABYLON.Vector3()
    const tmp = new BABYLON.Vector3()
    // Thumbstick-scroll: while a scrollable panel is gaze-visible, the RIGHT
    // stick's Y scrolls it and is withheld from vertical movement. One axis, one
    // hand — see the note at the call site for why it used to be everything.
    const SCROLL_SPEED = 1200 // panel viewBox units / sec at full stick (2× — VR thumbstick scroll felt sluggish vs the flat drag)
    // Chase-cam follow state (ported from the biped's XR camera): smoothly track
    // the piloted entity's position AND facing, with head-tracking compensation.
    const chasePos = new BABYLON.Vector3()
    const yawQuat = new BABYLON.Quaternion()
    const mtx = new BABYLON.Matrix()
    let chaseYaw = 0
    let chaseYawOffset = 0
    // Declared HERE, above `rearmYaw`, because re-seat has to re-arm this too —
    // see the note there. Kept out of the later block so the closure can never
    // reference it before initialisation.
    let chaseFirstFrame = true
    let cockpitYawOffset = 0 // head yaw captured when you take the seat
    // Deferred + re-armable, rather than captured once on entry. See the capture site.
    let yawCaptureNeeded = false
    // Same, for FREE locomotion (no piloted entity) — see the capture below.
    // Armed on entry so the first posed frame seats you facing the subject.
    let freeYawNeeded = true
    // The head yaw the free seed captured. The eye frame (which the scene panel
    // hangs off) takes its yaw from `eyeYawOffset`, NOT from the head — so it
    // must carry the same offset or the panel sits `headYaw` away from where
    // you are looking. Cockpit already does this with `cockpitYawOffset`.
    let freeYawOffset = 0

    const sm = base.sessionManager
    /** A real head pose this frame? Before one arrives the camera's rotation is stale. */
    const hasViewerPose = (): boolean => {
      const f = sm.currentFrame
      const rs = sm.referenceSpace
      if (f == null || rs == null) return false
      try {
        return f.getViewerPose(rs) != null
      } catch {
        return false
      }
    }

    /**
     * Re-seat the head. Holding the Meta button (or any system recentre) fires `reset` on
     * the XR reference space — the runtime moves the world under you and expects the app
     * to take the new pose as forward.
     *
     * We used to bake `cockpitYawOffset` once, on entry, and then keep applying it — so a
     * system recentre changed the reference space while we went on correcting by a yaw
     * measured against the OLD one. The recentre appeared to do nothing (we were undoing
     * it), which is why the only cure was to exit and re-enter. Re-arm the capture and the
     * next posed frame re-derives it.
     */
    const rearmYaw = (): void => {
      /*
      RE-SEAT MUST RE-ARM ALL THREE CAMERA PATHS — they capture yaw separately.

      There are three, and each has its own capture: COCKPIT bakes
      `cockpitYawOffset` (gated on `yawCaptureNeeded`), FREE locomotion seeds
      `rig.rotation.y` (gated on `freeYawNeeded`), and CHASE/FPV derives
      `chaseYawOffset` — gated on `chaseFirstFrame`, which re-seat used to leave
      alone. So re-seating in an aircraft's chase view did NOTHING, while the
      identical button worked in a cockpit and in an orbit demo.

      The tell was diagnostic: toggling cockpit↔chase "fixed" it, because the
      view-change path sets `chaseFirstFrame` — the repair was the toggle, not
      the button (Tonio, VR pass 2: "it did start working after I had toggled
      between cockpit and chase a bit").

      A capture added later must be re-armed here, or it inherits this bug.
      */
      yawCaptureNeeded = true
      // Orbit demos too, or the Meta button appears to do nothing in exactly
      // the scenes where you cannot fly out of a bad heading.
      freeYawNeeded = true
      // Chase/FPV. Also re-anchors `chasePos`, which is what a re-seat means
      // for a third-person camera.
      chaseFirstFrame = true
      if (debugSink.url !== '') sendDebug('xr', { seat: 're-armed' })
    }
    let resetSpace: XRReferenceSpace | null = null
    const bindReset = (): void => {
      resetSpace?.removeEventListener('reset', rearmYaw)
      resetSpace = sm.referenceSpace ?? null
      resetSpace?.addEventListener('reset', rearmYaw)
    }
    bindReset()
    // Babylon swaps the reference space on teleport/recentre — rebind, and re-seat.
    const refSpaceObs = sm.onXRReferenceSpaceChanged.add(() => {
      bindReset()
      rearmYaw()
    })
    /*
    RE-SEAT IS A CHICKEN-AND-EGG GESTURE — so confirm it with your head straight.

    Re-seating takes your CURRENT head yaw as forward. But to press the button
    you must LOOK AT the button, so if the panel has drifted off to the left you
    turn left to reach it and re-seat then points the rig at... where the panel
    was. The gesture defeats itself exactly when you need it (Tonio, VR pass 2).

    So the button no longer re-seats. It raises a prompt and waits for a trigger:
    "look comfortably ahead, then pull the trigger." Pull it and the yaw is
    captured from wherever you are looking THEN, which is the direction you
    actually meant.

    The prompt is pinned to the FACE frame — head-locked, `reveal: 'always'`.
    Face-locking normally fights the headset and we avoid it; here it is the
    whole point, because the instruction has to stay readable WHILE you turn your
    head to face forward. This is the one case that earns it.

    The SYSTEM recentre (holding the Meta button) still re-seats immediately —
    you were already looking where you meant, and adding a confirmation to the
    headset's own gesture would be impertinent.
    */
    let reseatPrompt: { dispose: () => void } | null = null
    let reseatArmed = false
    let triggerWasDown = true // ignore a trigger still held from pressing the button
    const clearReseatPrompt = (): void => {
      reseatPrompt?.dispose()
      reseatPrompt = null
      if (reseatArmed) {
        this.suppressInput(false)
        this.freeze(false)
      }
      reseatArmed = false
    }
    const promptReseat = (): void => {
      if (reseatArmed) {
        // Pressing it again is a cancel — never trap someone behind a prompt.
        clearReseatPrompt()
        return
      }
      reseatArmed = true
      triggerWasDown = true
      // Borrowing the trigger means the trigger must stop meaning "shoot".
      // The input gate is unconditional (and is the only half a networked world
      // could honour); the freeze is policy, default on for single player.
      this.suppressInput(true)
      if (!isOff(this.reseatFreeze)) this.freeze(true)
      const svg = panel3d(
        {
          width: 420,
          height: 210,
          // DELIBERATELY NOT `panelBg`. This prompt appears in front of the
          // scene panel you were already looking at, and two near-identical
          // translucent dark slabs read as one confusing surface. `info` is the
          // themeable status SURFACE for exactly this: opaque, so the panel
          // behind cannot show through the instruction, and distinct enough to
          // say "different thing, acting now".
          background: w3dTheme.info,
        },
        label3d({ text: 'Re-seat', bold: true }),
        label3d({ text: '1. Look comfortably ahead.' }),
        label3d({ text: '2. Pull right trigger to reseat.' }),
        label3d({ text: 'Or, pull left trigger to cancel.', muted: true })
      )
      reseatPrompt = attachFramePanel(scene, cam, frames.get('face'), {
        frame: 'face',
        // Straight ahead of the FACE frame, so it is dead centre whichever way
        // you turn — and `focus` is the frame origin, i.e. your own eyes.
        anchor: {
          azimuthDeg: 0,
          elevationDeg: 0,
          distance: 0.9,
          focus: [0, 0, 0],
        },
        // ~2x the 0.26 default: at 0.9m that is a comfortable read rather than
        // a postage stamp, and a modal should out-weigh the panel behind it.
        width: 0.55,
        reveal: 'always',
        svg,
      })
    }
    this._recenterXr = promptReseat
    /*
    XR INPUT + RIG READOUT — because a headset has no console.

    "I can rotate with the right stick but cannot move" means the free-fly
    branch IS running (turn lives in it) while `controllers['left']` is absent —
    but absent WHY is unguessable from outside the headset, and guessing is what
    this session has repeatedly paid for. So say it out loud, in the one readout
    that exists in VR: which hands are seen, whether their thumbsticks report
    axes, and where the rig ended up (including whether the entry yaw seed
    actually fired).
    */
    const squeezeDbg = (h: 'left' | 'right') =>
      ((controllers[h] as Record<string, any> | undefined)?.[
        'xr-standard-squeeze'
      ]?.value as number | undefined) ?? 0
    const xrInputDbgOff = this.addDebugSource({
      name: 'xr input',
      lines: () => {
        const fmt = (h: 'left' | 'right') => {
          const c = controllers[h] as Record<string, any> | undefined
          if (c == null) return `${h[0].toUpperCase()}:—`
          const stick = c['xr-standard-thumbstick']
          const ax = stick?.axes
          if (ax == null)
            return `${h[0].toUpperCase()}:no-stick[${Object.keys(c).join(',')}]`
          return `${h[0].toUpperCase()}:${ax.x.toFixed(2)},${ax.y.toFixed(2)}`
        }
        const deg = (r: number) => ((r * 180) / Math.PI).toFixed(0)
        return [
          `${fmt('left')}  ${fmt('right')}`,
          `lift L${squeezeDbg('left').toFixed(1)} R${squeezeDbg(
            'right'
          ).toFixed(1)}`,
          `rig ${rig.position.x.toFixed(1)},${rig.position.y.toFixed(
            1
          )},${rig.position.z.toFixed(1)} yaw ${deg(rig.rotation.y)}°`,
          `seed ${freeYawNeeded ? 'PENDING' : 'done'} · parent ${
            rig.parent ? 'piloted' : 'world'
          } · x${MOVE_SCALE.toFixed(0)}`,
        ]
      },
    })
    let lastView = '' // re-seat when the camera view toggles
    let chaseZoom = 0.5 // 0..1 chase distance (right stick Y while piloting)
    let lastPiloted: BABYLON.TransformNode | null = null
    const ZOOM_RATE = 0.8
    const MAX_PEEK = 0.8 // radians of temporary look (right stick X), ~46°
    const frame = base.sessionManager.onXRFrameObservable.add(() => {
      const now = realNow()
      // Re-seat confirmation: capture the yaw from where you are looking WHEN
      // YOU PULL, not from where you had to look to press the button. Either
      // hand. Edge-triggered, and `triggerWasDown` starts true so a trigger
      // still held from pressing the button doesn't fire it instantly.
      if (reseatArmed) {
        const t = (h: 'left' | 'right') =>
          ((controllers[h] as Record<string, any> | undefined)?.[
            'xr-standard-trigger'
          ]?.value ?? 0) > 0.6
        const rightDown = t('right')
        const leftDown = t('left')
        const down = rightDown || leftDown
        if (down && !triggerWasDown) {
          // RIGHT commits, LEFT cancels — two hands, no ambiguity, and no way
          // to be trapped behind the prompt.
          const commit = rightDown
          clearReseatPrompt()
          if (commit) rearmYaw()
        }
        triggerWasDown = down
      }
      const dt = Math.min((now - last) * 0.001, 0.1)
      last = now
      // Piloting a live controllable → the controllers fly it (via its mapping)
      // and the rig FOLLOWS it: positioned behind/at it AND rotated to face the
      // same way, so turning the entity turns your view (head tracking on top).
      // A crashed entity yields back to free walk/fly so you can leave the wreck.
      const entity = focusEl?.focused
      // First-person = fpv/cockpit; chase = third. Gates view-restricted panels.
      const viewCtx = {
        firstPerson: entity
          ? entity.cameraView === 'fpv' || entity.cameraView === 'cockpit'
          : true,
      }
      // Keep the body/neck/face frames tracking the head (after locomotion has
      // moved the rig last frame; before any UI reads them this frame).
      frames.update(dt)
      for (const p of bodyPanels) p.update(viewCtx)
      xrBar.update()
      for (const p of this._sysPanels.values()) p.panel.update()

      // getCameraTarget() (not .mesh) — the aircraft's node is `meshNode`, so
      // .mesh is undefined and it would never be chased.
      const piloted = entity?.crashed
        ? null
        : ((entity?.getCameraTarget?.() ??
            null) as BABYLON.TransformNode | null)
      if (piloted != null) {
        const view = entity?.cameraView ?? ''
        const eyeH = entity?.eyeHeight ?? 1.6
        const isFpv = view === 'fpv'
        const isCockpit = view === 'cockpit'
        const isChase = !isFpv && !isCockpit
        // Re-seat (recapture the recenter) on a view toggle as well as an entity
        // change, so cockpit↔chase doesn't snap.
        if (view !== lastView) {
          chaseFirstFrame = true
          lastView = view
        }
        /*
        Right stick (while piloting) zooms the chase and peeks left/right.
        From the camera axes if a mapping sets them, else from the LOOK axes,
        which is where the vehicle mappings (GTA's right stick) put the stick:
        only cameraPeek/cameraZoom were read here, nothing set them, and the
        right stick did nothing in VR in City Drive (Tonio). Stick forward
        (lookY > 0) zooms in: \`zoomIn\` > 0 brings the chase closer.
        */
        // A vehicle whose stick AIMS (a tank's gun) does not zoom with it, and
        // peeks only a little, toward where its turret is turning.
        const last = entity?.lastInput
        const aims = entity?.stickAims === true
        const zoomIn = last?.cameraZoom || (aims ? 0 : last?.lookY ?? 0)
        const peekIn = last?.cameraPeek || (last?.lookX ?? 0) * (aims ? 0.4 : 1)
        /*
        A vehicle that says which way its view faces (a car's orbit, a tank's
        turret) is FOLLOWED, and the rig ORBITS round it by that much (Tonio:
        "in VR the camera still pivots from behind the vehicle it doesn't
        orbit and it doesn't track the tank's turret heading"). Otherwise the
        stick peeks, turning the view in place.
        */
        const viewed = entity?.chaseView?.()
        const orbits = viewed != null
        const peekYaw = orbits ? viewed : peekIn * MAX_PEEK

        // COCKPIT: LITERALLY parent the rig to the hull — Babylon composes the
        // transform, so the camera inherits the airframe's full orientation with
        // zero hand-rolled quaternions. Identity local rotation = ride the hull's
        // orientation exactly. Scale is neutralized (rig world scale → 1) so head
        // tracking and the seat offset stay 1:1. The seat offset is head-comp'd
        // so your eye lands at (0, eyeH, cockpitForward) in the hull frame.
        if (isCockpit) {
          if (rig.parent !== piloted) {
            rig.parent = piloted
            refuseOwnAncestors(rig)
            yawCaptureNeeded = true
          }
          // Capture the head's entry yaw so we can recenter the CAMERA to look out the
          // nose without moving the (correctly-placed) panels.
          //
          // NOT on the frame we take the seat: for the first frames of a session the
          // viewer pose can still be null, and the camera then reports a stale/identity
          // rotation — so we'd bake a garbage yaw and you'd be seated facing the wrong
          // way. (The tell: exiting, holding your head still, and re-entering "fixed" it —
          // that just bought a clean capture.) Wait for a real pose.
          if (yawCaptureNeeded && hasViewerPose()) {
            cockpitYawOffset = cam.rotationQuaternion
              ? cam.rotationQuaternion.toEulerAngles().y
              : 0
            yawCaptureNeeded = false
          }
          // Neutralize the hull's scale so head tracking & the seat offset stay
          // 1:1 (no-op once the hull is canonical, but the model isn't always).
          const s = piloted.scaling.x || 1
          rig.scaling.set(1 / s, 1 / s, 1 / s)
          // Rig local rotation = RotationY(−entryYaw): swings the head to forward.
          BABYLON.Quaternion.RotationYawPitchRollToRef(
            -cockpitYawOffset,
            0,
            0,
            yawQuat
          )
          rig.rotationQuaternion = yawQuat
          // Head-comp THROUGH that rotation so the eye still lands at the seat.
          BABYLON.Matrix.FromQuaternionToRef(yawQuat, mtx)
          BABYLON.Vector3.TransformCoordinatesToRef(cam.position, mtx, tmp)
          rig.position.set(
            -tmp.x / s,
            (eyeH - tmp.y) / s,
            ((entity?.cockpitForward ?? 0.5) - tmp.z) / s
          )
          // Counter-rotate the eye frame by +entryYaw so the panels DON'T move
          // (they were already correct) while the camera recenters.
          frames.eyeYawOffset = cockpitYawOffset
          chaseFirstFrame = true // re-seat the chase on toggle-out
          return
        }

        // Non-cockpit: ensure the rig is back in world space — unless it is
        // riding a chase ANCHOR, which is the good case (see below).
        const chaseAnchor = isFpv ? null : entity?.getChaseAnchor?.() ?? null
        if (rig.parent != null && rig.parent !== chaseAnchor) {
          // setParent(null), NOT `parent = null` — see the note in the
          // free-walk branch. Harmless here (the pose is recomputed below), but
          // spelled the same way so neither site can drift into the other.
          rig.setParent(null)
          rig.scaling.set(1, 1, 1)
          chaseFirstFrame = true
        }

        if (isChase) {
          chaseZoom = Math.max(
            0,
            Math.min(1, chaseZoom - zoomIn * dt * ZOOM_RATE)
          )
        }
        const loH = entity?.chaseMinHeight ?? eyeH
        const chaseH = entity?.chaseHeight ?? CHASE_HEIGHT
        const chaseD = entity?.chaseDistance ?? 5
        /*
        fpv: at the head (back 0). chase: behind + above (zoomable), and past
        0.6 of the way out the boom swings up to almost overhead (Tonio: "could
        pulling back bring you to almost overhead at its extreme").
        */
        const over = Math.max(0, (chaseZoom - 0.6) / 0.4) ** 2
        const back = isFpv
          ? 0
          : chaseD * (0.8 + chaseZoom * 1.1) * (1 - 0.8 * over)
        const up = isFpv
          ? eyeH
          : loH +
            (chaseH - loH) * chaseZoom +
            over * Math.max(0, chaseD * 3 - chaseH)
        // Up there it looks DOWN at the vehicle (Tonio: "the camera goes up
        // but doesn't point down"): the rig pitches toward a point a little
        // above it, all the way at the top of the arc.
        const lookDown = isFpv
          ? 0
          : over * Math.atan2(up - eyeH, Math.max(back, 0.5))
        /*
        The rig's turn: facing \`yaw\`, pitched DOWN by \`pitch\` about the axis
        across where the HEAD looks. The head faces the rig's forward turned by
        \`offset\` (captured when the chase began), so pitching about the rig's
        own side axis rolled the view instead, by as much as the head was
        turned: sideways overhead (Tonio). Checked: yaw and pitch land as asked,
        the head's side axis stays level.
        */
        function chaseTurn(
          yaw: number,
          pitch: number,
          offset: number,
          out: BABYLON.Quaternion
        ) {
          BABYLON.Quaternion.RotationAxisToRef(BABYLON.Axis.Y, yaw, out)
          out.multiplyInPlace(
            BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, pitch)
          )
          out.multiplyInPlace(
            BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Y, -offset)
          )
        }
        piloted.getDirectionToRef(XR_FORWARD, fwd) // world forward
        const targetYaw = Math.atan2(fwd.x, fwd.z)
        // fpv: anchor to the actual head bone if the entity exposes it.
        const headPos = isFpv ? entity?.getHeadPosition?.() ?? null : null
        // The node ORIGIN — the vehicle convention's centred, grounded stance
        // point. NOT `getAbsolutePivotPoint()`: that returns the CoG, which is a
        // MASS centre authored off the centreline (the scout's is 0.4 units to
        // one side), so anchoring there shifts the chase sideways. Tried, and it
        // regressed the flat view.
        const targetX = headPos ? headPos.x : piloted.position.x - fwd.x * back
        const targetY = headPos ? headPos.y : piloted.position.y + up
        const targetZ = headPos ? headPos.z : piloted.position.z - fwd.z * back
        // Only from a real head pose, as the cockpit and free captures wait:
        // a session's first frames can carry a stale rotation, which left the
        // bar and panels off to one side until a re-seat.
        if ((chaseFirstFrame || lastPiloted !== piloted) && hasViewerPose()) {
          // Align to where the headset is currently looking so it doesn't snap.
          chaseFirstFrame = false
          lastPiloted = piloted
          chaseYawOffset = cam.rotationQuaternion
            ? cam.rotationQuaternion.toEulerAngles().y
            : 0
          chaseYaw = targetYaw - chaseYawOffset
          chasePos.set(targetX, targetY, targetZ)
          // A headset's seat, for the debug sink: where the head was when the
          // chase took its forward (#3726, re-seating on entry).
          if (debugSink.url !== '') {
            const deg = (r: number) => Math.round((r * 180) / Math.PI)
            sendDebug('xr', {
              seat: 'chase',
              headYaw: deg(chaseYawOffset),
              head: [cam.position.x, cam.position.y, cam.position.z].map(
                (n) => +n.toFixed(2)
              ),
              vehicleYaw: deg(targetYaw),
              frame: scene.getEngine().frameId,
            })
          }
        }

        /*
        PARENTED CHASE — the rigid path, when the entity offers an anchor.

        The rig becomes a CHILD of a level position+heading node the entity
        updates in the same tick it moves. That fixes three separate faults at
        once, all measured 2026-08-26 (see TODO → "THE CHASE RIG"):

          1. ORDER. This observer is `onXRFrameObservable`, which fires BEFORE
             `scene.render()`; the entity moves in `registerBeforeRender`, which
             fires inside it. So the world-space path below positions the rig
             from LAST frame's aircraft position, every frame — and a variable
             frame time turns that fixed lag into jitter. A child's world matrix
             is resolved at render, after the move.
          2. LAG. `chasePos += (target - chasePos) * k*dt` is a first-order
             tracker, so it sits `v/k` behind — about 6.8 m at 61 m/s, and
             PROPORTIONAL TO SPEED. Tonio, in a headset: "throttle up the
             aircraft gets further away. Throttle down it gets closer." A child
             at a fixed local offset cannot drift.
          3. FRAME-RATE DEPENDENCE. `k*dt` as a lerp factor is the wrong form
             (`1 - exp(-k*dt)` is the right one), so the easing itself changed
             behaviour with frame rate.

        Level and yaw-only, exactly as the un-parented version was: parenting to
        the airframe would hand the view its attitude, and a rolling horizon in
        a headset is why this was not simply parented in the first place.

        Head compensation stays. It is not decoration — `cam.position` is the
        tracked head offset within the rig, and in a floor-level reference space
        that is your whole standing height. It mirrors the cockpit branch above,
        which has always worked this way. The residual staleness is a frame of
        HEAD motion (millimetres), not a frame of aircraft motion (metres).
        */
        if (chaseAnchor != null) {
          if (rig.parent !== chaseAnchor) {
            rig.parent = chaseAnchor
            refuseOwnAncestors(rig)
            rig.scaling.set(1, 1, 1)
          }
          chaseTurn(peekYaw, lookDown, chaseYawOffset, yawQuat)
          rig.rotationQuaternion = yawQuat
          BABYLON.Matrix.FromQuaternionToRef(yawQuat, mtx)
          BABYLON.Vector3.TransformCoordinatesToRef(cam.position, mtx, tmp)
          // Anchor-local: +Z is the entity's forward, so behind is −Z; an
          // orbit swings "behind" round to behind where the view faces.
          const ox = orbits ? -back * Math.sin(peekYaw) : 0
          const oz = orbits ? -back * Math.cos(peekYaw) : -back
          rig.position.set(ox - tmp.x, up - tmp.y, oz - tmp.z)
          frames.eyeYawOffset = chaseYawOffset - peekYaw
          return
        }
        // Chase eases horizontally (turning doesn't snap); vertical always tracks
        // tightly so it doesn't sink below on a climb. fpv tracks tight all round.
        // `1 - exp(-k*dt)`, NOT `k*dt`: the latter is not frame-rate
        // independent, so the follow behaved differently at 72 and 90 Hz. Only
        // entities with no chase anchor still come through here (a biped, a
        // car) — an aircraft is parented above and does not ease at all.
        const posT = 1 - Math.exp(-(isChase ? 9 : 16) * dt)
        const posTy = 1 - Math.exp(-16 * dt)
        const yawT = 1 - Math.exp(-6 * dt)
        chasePos.x += (targetX - chasePos.x) * posT
        chasePos.y += (targetY - chasePos.y) * posTy
        chasePos.z += (targetZ - chasePos.z) * posT
        let yawDiff = targetYaw - chaseYawOffset - chaseYaw
        while (yawDiff > Math.PI) yawDiff -= Math.PI * 2
        while (yawDiff < -Math.PI) yawDiff += Math.PI * 2
        chaseYaw += yawDiff * yawT
        // Compensate for the head's local (tracked) offset so the HEAD lands at
        // chasePos: rig = chasePos − (headLocal rotated into the rig's yaw).
        chaseTurn(
          chaseYaw + peekYaw + chaseYawOffset,
          lookDown,
          chaseYawOffset,
          yawQuat
        )
        BABYLON.Matrix.FromQuaternionToRef(yawQuat, mtx)
        BABYLON.Vector3.TransformCoordinatesToRef(cam.position, mtx, tmp)
        rig.position.set(
          chasePos.x - tmp.x,
          chasePos.y - tmp.y,
          chasePos.z - tmp.z
        )
        rig.rotationQuaternion = yawQuat
        // Eye-frame panels align with the LOGICAL forward (entity facing), not
        // the rig's recentered yaw: cancel the recenter (and the live peek).
        frames.eyeYawOffset = chaseYawOffset - peekYaw
        return
      }

      // Free walk/fly. If we were just piloting, hand the rig's yaw back to euler
      // (a set rotationQuaternion overrides the euler the stick-turn below uses).
      chaseFirstFrame = true
      lastPiloted = null
      lastView = ''
      // Match the seed: the eye frame's yaw is rig-local, so without this the
      // panel hangs `headYaw` away from your gaze — "the panel is behind me",
      // reproducibly, whenever you happened to enter facing off-axis.
      frames.eyeYawOffset = freeYawOffset
      if (rig.parent != null) {
        /*
        `setParent(null)`, NEVER `parent = null`.

        Assigning `parent` KEEPS THE LOCAL POSE and reinterprets it as world, so
        a rig sitting at local (0, 2, −5) behind its parent teleports to (0, 2,
        −5) in the WORLD — next to the origin. `setParent` preserves the world
        transform, which is what "back to world space" was always supposed to
        mean.

        This is the path a death takes: `releaseFocus()` nulls the focused
        entity, so the next XR frame finds nothing piloted and falls through to
        here. Tonio, VR: "I collided with wreckage high up and respawned at the
        origin or starting point with the wrecked plane hanging in mid-air off
        in the distance." He was not moved away from the wreck — he was moved to
        the ORIGIN, and the wreck stayed where he died (measured: a corpse
        drifts 0.05 m in 10 s).

        The comment here used to say "came from the cockpit", and that was true:
        only the cockpit branch parented the rig, which is why TODO has carried
        "COCKPIT DEATH: … the aircraft was moved way away from me" as a
        cockpit-only oddity since 0.7.0. Parenting the CHASE rig (the jitter
        fix) made the same latent bug reachable from the view people actually
        fly in, which is how a five-month-old note finally got diagnosed.
        */
        rig.setParent(null)
        rig.scaling.set(1, 1, 1)
      }
      if (rig.rotationQuaternion != null) {
        rig.rotation.y = rig.rotationQuaternion.toEulerAngles().y
        rig.rotationQuaternion = null
      }

      /*
      FACE WHAT YOU WERE LOOKING AT — the free-locomotion case.

      Both PILOTED paths seed yaw against the head (cockpit bakes
      `cockpitYawOffset`, chase does `chaseYaw = targetYaw - chaseYawOffset`).
      Free locomotion never did, so an ORBIT demo — one with no piloted entity,
      which is most of them — dropped you in facing whichever way your head
      physically pointed, with the scene wherever the unrotated rig left it.
      Reported as the panel being "behind and left" and the subject "nowhere in
      sight" (Tonio, VR pass 2, carved-landform/volcano).

      Same shape as the cockpit capture, including the reason it waits: for the
      first frames of a session the viewer pose can be null and the camera
      reports a stale rotation, so capturing early bakes a garbage yaw. That is
      also why TOGGLING VIEWS "fixed" it — the toggle re-armed a capture on a
      frame that had a real pose.

      Forward is the flat camera's view direction (an ArcRotateCamera looks at
      its target), so you arrive looking at what you were looking at.
      */
      if (freeYawNeeded && hasViewerPose()) {
        const flat = this.camera as BABYLON.ArcRotateCamera | null
        if (flat != null) {
          const tgt = (flat as any).target as BABYLON.Vector3 | undefined
          const dx = (tgt?.x ?? 0) - flat.position.x
          const dz = (tgt?.z ?? 0) - flat.position.z
          if (Math.hypot(dx, dz) > 1e-4) {
            const headYaw = cam.rotationQuaternion
              ? cam.rotationQuaternion.toEulerAngles().y
              : 0
            rig.rotation.y = Math.atan2(dx, dz) - headYaw
            freeYawOffset = headYaw
          }
        }
        freeYawNeeded = false
      }

      const left = controllers['left']?.['xr-standard-thumbstick']?.axes
      const right = controllers['right']?.['xr-standard-thumbstick']?.axes

      /*
      PANEL SCROLL — the RIGHT stick's Y, and nothing else.

      This used to withhold BOTH sticks from locomotion whenever a scrollable
      panel was visible, and it set the withhold flags for every connected
      controller UNCONDITIONALLY — not when a stick had actually scrolled
      anything. So a scene with a scene-panel long enough to scroll had NO
      locomotion at all: left stick dead, turn dead, vertical dead. Reported as
      "I'm stuck here… it's still not allowing me to move around at all in VR",
      on demo after demo, while every control was implemented and correct.

      Scroll stays gated on GAZE rather than on the controller ray (a pick that
      breaks must not take the only escape hatch with it — that lesson holds).
      What changed is the COST: it claims one axis on one hand, so walking and
      turning are never withheld, and the most you lose while reading a panel is
      vertical movement — which you are not using while reading a panel.
      */
      let rightScroll = false
      if (panel != null && panel.scrollable && panel.plane.visibility > 0.5) {
        const axes = right
        if (axes != null && Math.abs(axes.y) > DEAD) {
          panel.scrollBy(axes.y * SCROLL_SPEED * dt)
          rightScroll = true
        }
      }

      if (
        left != null &&
        (Math.abs(left.x) > DEAD || Math.abs(left.y) > DEAD)
      ) {
        // Walk relative to where the head currently faces (flattened to floor).
        cam.getDirectionToRef(XR_FORWARD, fwd)
        fwd.y = 0
        fwd.normalize()
        cam.getDirectionToRef(XR_RIGHT, side)
        side.y = 0
        side.normalize()
        const step = HORIZ_SPEED * dt
        fwd.scaleToRef(-left.y * step, tmp)
        rig.position.addInPlace(tmp)
        side.scaleToRef(left.x * step, tmp)
        rig.position.addInPlace(tmp)
      }
      /*
      ALTITUDE ON THE BUMPERS: left = down, right = up.

      Tonio's mapping, and the same hand logic as the triggers (left brakes,
      right accelerates) so the pair is learned once. It is ANALOG — squeeze
      value, not a boolean — so you can ease onto a height rather than bang
      between rates.

      It exists because the right stick's vertical was not a reliable way down.
      That axis is ALSO the panel scroll, so in any scene with a scrollable
      panel open — which is most of the demos, they set `scenePanelOpen` — the
      stick scrolls and the rig does not move, and the control appears simply
      missing ("there's still no way to change your altitude"). Two dedicated
      buttons cannot be stolen by a panel.

      The stick keeps working when nothing is claiming it, so nobody who
      already knows it loses it.
      */
      const squeeze = (h: 'left' | 'right') =>
        ((controllers[h] as Record<string, any> | undefined)?.[
          'xr-standard-squeeze'
        ]?.value as number | undefined) ?? 0
      const lift = squeeze('right') - squeeze('left')
      if (Math.abs(lift) > DEAD) rig.position.y += lift * VERT_SPEED * dt
      if (right != null && !rightScroll && Math.abs(right.y) > DEAD) {
        rig.position.y += -right.y * VERT_SPEED * dt // push up to ascend
      }
      if (right != null && Math.abs(right.x) > DEAD) {
        // Smooth-turn around the head (not the rig origin) so you spin in place
        // rather than orbiting when you've stepped off-centre. Rotate, then nudge
        // the rig so the head's world XZ is unchanged.
        head.copyFrom(cam.globalPosition)
        rig.rotation.y += right.x * TURN_SPEED * dt // push right → turn right
        rig.computeWorldMatrix(true)
        cam.computeWorldMatrix()
        rig.position.x += head.x - cam.globalPosition.x
        rig.position.z += head.z - cam.globalPosition.z
      }
    })

    return {
      dispose: () => {
        base.sessionManager.onXRFrameObservable.remove(frame)
        clearReseatPrompt()
        xrInputDbgOff()
        sm.onXRReferenceSpaceChanged.remove(refSpaceObs)
        resetSpace?.removeEventListener('reset', rearmYaw)
        this._recenterXr = noop
        this._refreshXrPanel = noopRefresh
        this._xrPanelEl = null
        panel?.dispose()
        this._closeXrSettings = noop
        this._carrySystemPanels(false)
        xrBar.dispose()
        for (const p of bodyPanels) p.dispose()
        frames.dispose()
        this.xrFrames = null
        cam.parent = null
        ground?.dispose()
        grid?.dispose()
        rig.dispose()
      },
    }
  }

  // Build a panel SVG from a row list. Each surface (overlay, in-scene) builds
  // its own with independent widget instances bound to the same reactive
  // values, so they stay in sync.
  /*
  SIZE TO THE CONTENT, capped — and PIN the bar.

  The height used to be guessed as `46 + rows * 48`, which assumes a row is
  about 48px tall. Most are. `lightEditor3d` is ONE row that lays out over
  1200px, so a panel holding it was built ~190px tall and you scrolled a
  postage stamp: Tonio, from the headset, "crammed into a tiny tiny view so you
  have to scroll it constantly… I didn't try messing with curves because they
  were too far down."

  `height: 'fit'` measures the widgets and takes their real total; `maxHeight`
  stops it becoming a wall. Past the cap it scrolls, as before — but now only
  when it genuinely does not fit.

  `header` keeps the icon bar out of that scroll. It carries Exit VR, and a
  control you need in order to LEAVE must not be the one that scrolls away.
  */
  /*
  IN THE SCENE it has a title bar: move, `panelTitle`, and (flat) close, as a
  popup has, in the band that used to be empty headroom. The DOM panel keeps
  its own HTML head over that band instead.
  */
  private _makePanel(
    rows: Widget3d[],
    header: Widget3d[] = [],
    grip = false,
    maxHeight = 620,
    handleClose?: () => void
  ): SVGSVGElement {
    return panel3d(
      {
        width: 320,
        height: 'fit',
        maxHeight,
        // Only the in-scene panel is draggable — the flat one is a DOM overlay
        // the page already positions, and a grab bar there would do nothing.
        grip,
        title: grip ? (this as any).panelTitle || undefined : undefined,
        handleClose,
        // The DOM panel: room for the HTML head's buttons (top-right).
        paddingTop: grip ? undefined : 34,
        header,
      },
      ...rows
    )
  }

  // Flat-screen surface: the owl on the system bar toggles the settings panel
  // (in the scene, or a DOM overlay with flat-ui="dom"). Only revealed when the
  // scenePanel hook returns widgets. The panel is REBUILT each time it opens (not once at setup), so a hook whose
  // contents depend on async state — e.g. a library mesh-picker list that only
  // exists after the GLB loads — is always current when you open it. (The in-XR
  // panel likewise re-invokes the hook when it's built on VR entry.)
  // NPC nameplates in ALL contexts (flat + XR): a gaze-revealed label above each
  // non-player biped. A frame panel already reveals off `scene.activeCamera`, so
  // the same code works on a monitor and in a headset — no XR-specific wiring.
  // Created lazily as bipeds' GLBs load (and disposed when they leave), updated
  // every rendered frame (onBeforeRenderObservable fires in both flat and XR).
  private _setupNameplates(): void {
    const scene = this.scene
    // A `Nameplates` debug source used to hang here, reporting gaze-reveal internals
    // (xr/cam/updates/cos) to diagnose plates staying visible inside a session. That's
    // resolved, so it's gone — scaffolding kept past its bug is just a row competing for
    // panel space with whatever you're debugging NEXT. The instrumentation it read
    // (`panel.debug` in frame-panel.ts) is deliberately still there and still live, so
    // restoring the readout is one `addDebugSource` block if the reveal ever regresses.
    scene.onBeforeRenderObservable.add(() => {
      const cam = scene.activeCamera as BABYLON.TargetCamera | null
      if (cam == null) return
      // Add newly-ready bipeds / drop departed ones only occasionally (querySelector
      // + set churn shouldn't run every frame).
      if (this._nameplateScan-- <= 0) {
        this._nameplateScan = 30
        this._scanNameplates(cam)
      }
      for (let i = 0; i < this._nameplateList.length; i++) {
        this._nameplateList[i].ef.update(cam)
        this._nameplateList[i].panel.update()
      }
    })
  }

  private _scanNameplates(cam: BABYLON.TargetCamera): void {
    const scene = this.scene
    const seen = new Set<Element>()
    for (const el of Array.from(this.querySelectorAll('tosi-b3d-biped'))) {
      const b = el as any
      if (b.player || b.mesh == null) continue
      seen.add(el)
      if (this._nameplates.has(el)) continue
      const ef = new EntityFrame(scene, b.mesh, {
        offset: [0, (b.eyeHeight ?? 1.7) + 0.25, 0],
      })
      const panel = attachFramePanel(scene, cam, ef.node, {
        // Aim the cone at the NPC, not at the plate floating above their head. The wide
        // 70°/40° cone that used to be here was a WORKAROUND for measuring against the plate
        // (looking at someone didn't reveal their name), and 70° is most of your field of
        // view — which is why the plates never went away. With the cone pointed at the
        // subject, a tight one behaves: look at someone, get their name; look away, lose it.
        gazeTarget: b.mesh,
        // Aim at the CHEST. A biped's node origin is at their FEET — with a tight cone aimed
        // there, standing near someone in VR puts their feet ~40° below your gaze, so looking
        // them in the eye revealed nothing at all (the plates read 0 forever). Flat hid this
        // completely: a chase camera looks DOWN at the scene, so the feet sit near the middle
        // of the screen and it all seemed fine.
        gazeOffset: [0, (b.eyeHeight ?? 1.7) * 0.6, 0],
        anchor: {
          position: [0, 0, 0],
          focus: [0, 0, 1], // faces +Z = toward the viewer (frame turns to face you)
          // Not too tight: in a headset you aim with your head, not a mouse.
          revealStartDeg: 32,
          revealFullDeg: 14,
        },
        // Fade with distance rather than cliff-edging. A hard `maxDistance: 8` was tried and
        // removed because it snapped plates out of existence and the monitor-tuned cutoff was
        // wrong in a headset. Fade, don't switch — same lesson as the fog and the bubbles.
        // Pulled in per Tonio: plates should declutter at much closer range than the first pass.
        fadeFrom: 4,
        fadeTo: 9,
        // Compact card (280×116 vs the default 320×200) — ~50% less padding
        // around the label so the plaque hugs the name and doesn't hang low.
        svg: placeholderPanelSvg((b.id as string) || '$6M biped', 280, 116),
        width: 0.6, // 2× — readable at a glance
        // No distance gate: an earlier `maxDistance: 8` hid nameplates in VR
        // (the head is easily >8m from NPCs), while flat worked. The 26° gaze
        // cone already declutters — you only see the ones you look at.
      })
      this._nameplates.set(el, { ef, panel })
    }
    // Drop nameplates whose biped is gone (removed from the DOM / disposed mesh).
    for (const [el, n] of this._nameplates) {
      if (!seen.has(el)) {
        n.panel.dispose()
        n.ef.dispose()
        this._nameplates.delete(el)
      }
    }
    this._nameplateList = [...this._nameplates.values()]
  }

  /*
  FULL SCREEN, from manta's own: the standard API with the webkit fallback.
  The SCENE goes full screen rather than the page, so a demo on a doc page
  fills the screen without the page around it. Where neither API exists
  (iPhone Safari lets only a video go full screen), the button MAXIMISES the
  scene instead: fixed over the page, filling what is visible, which is what
  a page like manta's play page does by its own layout.
  */
  private _fullscreenWired = false
  private _setupFullscreen(): void {
    const btn = this.parts.fullscreenButton as HTMLButtonElement | undefined
    if (btn == null || this._fullscreenWired) return
    this._fullscreenWired = true
    const el = this as any
    const doc = document as any
    const can =
      !!(el.requestFullscreen || el.webkitRequestFullscreen) &&
      (doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? true)
    btn.hidden = false
    const isFull = () =>
      can
        ? (doc.fullscreenElement ?? doc.webkitFullscreenElement) === this
        : this.hasAttribute('data-maximized')
    const sync = (): void => {
      const full = isFull()
      btn.title = full ? 'Leave full screen' : 'Full screen'
      btn.replaceChildren(full ? svgIcons.minimize() : svgIcons.maximize())
      // The canvas changed size with the element.
      this.engine?.resize()
    }
    /*
    Into the TOP LAYER as a manual popover, not just `position: fixed`: an
    ancestor with containment or a transform (a doc page's example card) makes
    a fixed element fill the CARD, not the screen. Never moved in the DOM,
    since a moved scene is disconnected and reconnected.
    */
    const pop = this as unknown as {
      showPopover?: () => void
      hidePopover?: () => void
    }
    const maximize = (on: boolean) => {
      if (on) {
        resetPageZoom()
        this.setAttribute('data-maximized', '')
        if (pop.showPopover != null) {
          this.setAttribute('popover', 'manual')
          try {
            pop.showPopover()
          } catch {
            // Already open, or refused: the fixed layout still applies.
          }
        }
      } else {
        if (this.hasAttribute('popover')) {
          try {
            pop.hidePopover?.()
          } catch {
            // Not open.
          }
          this.removeAttribute('popover')
        }
        this.removeAttribute('data-maximized')
      }
      sync()
    }
    const toggle = () => {
      if (!can) {
        maximize(!this.hasAttribute('data-maximized'))
        return
      }
      // A refusal (no user gesture, a policy) is not worth an uncaught
      // rejection: the button simply did nothing.
      const r = isFull()
        ? (doc.exitFullscreen || doc.webkitExitFullscreen).call(doc)
        : (el.requestFullscreen || el.webkitRequestFullscreen).call(el)
      ;(r as Promise<void> | undefined)?.catch?.(() => {})
    }
    btn.addEventListener('click', toggle)
    document.addEventListener('fullscreenchange', sync)
    document.addEventListener('webkitfullscreenchange', sync)
    // Removed with the rest: teardown clears `_fullscreenWired`, and the
    // button outlives it, so a reconnect would otherwise add a second toggle.
    this._fullscreenOff = () => {
      // Not through `maximize`: its resize would reach an engine on its way out.
      if (this.hasAttribute('popover')) {
        try {
          pop.hidePopover?.()
        } catch {
          // Not open.
        }
        this.removeAttribute('popover')
      }
      this.removeAttribute('data-maximized')
      btn.removeEventListener('click', toggle)
      document.removeEventListener('fullscreenchange', sync)
      document.removeEventListener('webkitfullscreenchange', sync)
    }
  }
  private _fullscreenOff: (() => void) | null = null

  /*
  PAGE ZOOM, on iPhone Safari. Its pinch arrives as its own `gesture*` events,
  which `touch-action` does not stop, so they are cancelled here; and a page
  that got zoomed anyway (before the scene loaded, from outside it) is reset
  whenever the scene goes full screen or the zoom changes while it is, since
  a scene that covers the screen leaves nothing to pinch out on.
  */
  private _zoomBlocked = false
  private _blockPageZoom(): void {
    if (this._zoomBlocked) return
    this._zoomBlocked = true
    const stop = (e: Event) => e.preventDefault()
    const active = { passive: false } as AddEventListenerOptions
    this.addEventListener('gesturestart', stop, active)
    this.addEventListener('gesturechange', stop, active)
    // Safari's gesture events need not leave the shadow DOM; touch events do.
    // A two-finger move here is never a page pinch, and a quick second tap is
    // never a double-tap zoom.
    this.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length > 1) e.preventDefault()
      },
      active
    )
    let lastTap = 0
    this.addEventListener(
      'touchend',
      (e) => {
        const now = e.timeStamp
        if (now - lastTap < 350) e.preventDefault()
        lastTap = now
      },
      active
    )
    // A page zoomed anyway is put back the moment you touch the scene: a scene
    // that fills the screen leaves nowhere else to pinch out.
    this.addEventListener('touchstart', () => resetPageZoom(), {
      passive: true,
    })
    const vv = (window as { visualViewport?: VisualViewport }).visualViewport
    vv?.addEventListener('resize', () => {
      if (this.hasAttribute('data-maximized') && vv.scale > 1.01)
        resetPageZoom()
    })
  }

  private _setupScenePanel(): void {
    const gear = this.parts.scenePanelGear as HTMLButtonElement
    const host = this.parts.scenePanelHost as HTMLElement
    if (!this._scenePanelWired) {
      this._scenePanelWired = true
      gear.addEventListener('click', () => {
        if (this._scenePanelOpen()) this._closeScenePanel()
        else this._openScenePanel()
      })
      // Held on the system bar: back where it started, and open.
      gear.addEventListener('systemhold', () => {
        this._flatPanelSpot = null
        this._closeScenePanel()
        this._openScenePanel()
      })
      /*
      A PRESS ON THE BACKGROUND CLOSES IT.

      The flat panel is a transient overlay over the scene, and every other
      transient overlay in this library already dismisses that way — the
      surface's popups do it on PRESS, and for the reason stated there: a press
      that starts outside was never meant for the panel, and waiting for the
      release leaves it open under a pointer that has already moved on.

      Three things must NOT close it, and each is a bug if it does:
      - a press inside the panel (you are using it)
      - a press on the GEAR, which toggles — closing here first would make the
        gear's own handler reopen it, so it would look inert
      - a drag that merely ENDS outside, which is an ordinary slider gesture
        leaving the track

      `pointerdown` on the host element rather than the document: the scene owns
      its own overlay, and a page with several scenes should not have one
      dismiss another's panel.
      */
      this.addEventListener('pointerdown', (e) => {
        if (!this._scenePanelOpen()) return
        // A press a panel in the scene took (the settings panel itself, the
        // corner toolbar) is theirs, not the background's.
        if (pointerClaimedByPanel(e)) return
        /*
        `composedPath()`, NOT `e.target`.

        The panel lives behind a shadow boundary, so a listener out here sees
        the target RETARGETED to the host element — `host.contains(e.target)`
        is then false for a press on the panel's own controls, and the panel
        closes under the finger that was using it. Measured: pressing a slider
        inside the panel dismissed it.

        The composed path is the real route the event took, boundaries and all,
        so asking whether the panel or the gear is on it answers the question
        that was actually being asked.
        */
        const path = e.composedPath()
        if (path.includes(host) || path.includes(gear)) return
        this._closeScenePanel()
      })
    }
    // Reveal the gear when the panel has anything in it: the scenePanel hook's
    // rows, or its icon bar (Pause is always there). Rows alone hid the owl on a
    // page with no settings of its own, which the headset still showed.
    // (Enter VR is a SEPARATE button grouped next to the gear — see the toolbar —
    // so XR availability no longer gates the gear.)
    if (this._panelWidgets().length > 0 || this._barItems().length > 0) {
      gear.hidden = false
      if ((this as any).scenePanelOpen) this._openScenePanel()
    }
  }

  /**
   * Open the flat scene panel: a header row of small round buttons pinned
   * top-right — the icon-bar items, then close — over the panel body.
   *
   * The toggles used to be a full-width `iconBar3d` row inside the body, which
   * spent a whole row of a small panel on things you press once. They are the
   * same items either way (`_barItems`); only the flat LAYOUT changed.
   */
  /*
  THE SETTINGS PANEL, IN THE SCENE (flatUi 'scene'; #3307). The same panel the
  headset gets (`_makePanel` with an icon bar), as a `ui`-layer frame panel
  pinned under the corner toolbar, a little nearer than the loadout so two ui
  panels never share a depth. Its popups are made in the same layer and hang
  from its drawn mesh (`attachSceneLayer`). Rebuilt on refresh like the DOM one.
  */
  private _flatScenePanel: {
    el: SVGSVGElement
    panel: {
      update: () => void
      dispose: () => void
      drawn: BABYLON.Mesh
      setSvg: (next: SVGSVGElement) => void
    }
    /** The plane's size as built (it scales as the content changes). */
    planeW: number
    planeH: number
    frames: XrFrames
    obs: BABYLON.Nullable<BABYLON.Observer<BABYLON.Camera>>
  } | null = null
  private _flatPanelSpot: ScreenAnchor | null = null
  private _scenePanelOpen(): boolean {
    if (this._flatScenePanel != null) return true
    const host = this.parts?.scenePanelHost as HTMLElement | undefined
    return host != null && !host.hasAttribute('hidden')
  }
  private _disposeFlatScenePanel(): void {
    const f = this._flatScenePanel
    if (f == null) return
    this.scene?.onBeforeCameraRenderObservable.remove(f.obs)
    f.panel.dispose()
    f.frames.dispose()
    this._flatScenePanel = null
    this._syncGamepadCover()
  }
  /*
  THE GLASS GAMEPAD STANDS ASIDE while in-scene UI is up. It is HTML over the
  canvas (it stays DOM, #3307), so it covers in-scene panels and takes their
  presses where they overlap: a press on the settings panel under its bumper
  closed the panel. While the settings panel or a pause is up you are not
  playing, so it hides, and comes back after. (The DOM panel sat above it.)

  A SYSTEM PANEL ONLY IF THE PERSON OPENED IT (#3436). Opened with its button,
  they are looking at it, not playing, so the pad gets out of the way. Opened
  by code (an objectives list shown for a while, an app's pinned chrome) it is
  up while they play, and on a phone hiding the pad took the controls away:
  place one clear of the pad's clusters (its `screenAnchor`) instead. Tonio:
  "if the user discloses a panel, the pad should get out of the way".
  */
  private _syncGamepadCover(): void {
    if (this._holdPanelEvents) return
    const covered =
      this._flatScenePanel != null ||
      this._modals.size > 0 ||
      [...this._sysPanels.values()].some((p) => p.byUser)
    this._suppressGamepad('in-scene-ui', covered)
  }
  private _holdPanelEvents = false
  private _openFlatScenePanel(): boolean {
    const scene = this.scene
    const cam = scene?.activeCamera
    if (scene == null || cam == null) return false
    // Close is in the title bar now, as a popup's is.
    const items = this._barItems()
    const el = this._makePanel(
      this._panelWidgets(),
      items.length > 0 ? [iconBar3d({ items })] : [],
      true,
      this._flatPanelMax() - 64,
      () => this._closeScenePanel()
    )
    const vb = el.viewBox.baseVal
    /*
    REBUILT IN PLACE. Every icon press and refresh rebuilds this panel, and a
    new plane is blank until its texture is drawn: on a headset-speed CPU
    that showed the scene through the panel for two or three frames on every
    rebuild (measured at 4x CPU throttle). The same plane takes the new
    content; its texture keeps the old picture until the new one is ready.
    */
    const live = this._flatScenePanel
    if (live != null) {
      carryScroll(live.el, el)
      live.panel.setSvg(el)
      live.el = el
      if (this._flatPanelSpot != null) this._flatPanelSpot.height = vb.height
      this._attachFlatLayer(el, live.panel.drawn, live.planeW, live.planeH)
      return true
    }
    const W = 0.3
    const H = W * ((vb.height || 1) / (vb.width || 1))
    const frames = XrFrames.flat(scene, cam)
    // ONE anchor object, kept: a drag by the title bar writes its spot into
    // it, so a rebuild (any icon press) leaves the panel where you put it.
    const spot = (this._flatPanelSpot ??= {
      left: 8,
      top: UNDER_BAR,
      distance: 0.9,
    })
    // One viewBox unit per CSS pixel, as the DOM panel drew it.
    spot.height = vb.height
    const panel = attachFramePanel(
      scene,
      cam as BABYLON.TargetCamera,
      frames.get('face'),
      {
        frame: 'face',
        anchor: { position: [0, 0, 1] },
        presence: 'both',
        reveal: 'always',
        layer: 'ui',
        svg: el,
        width: W,
        resolution: 1024,
        screenAnchor: spot,
      }
    )
    const obs = scene.onBeforeCameraRenderObservable.add((rendering) => {
      if (rendering !== cam) return
      frames.update(0)
      panel.update()
    })
    this._attachFlatLayer(el, panel.drawn, W, H)
    this._flatScenePanel = { el, panel, planeW: W, planeH: H, frames, obs }
    this._syncGamepadCover()
    return true
  }
  /** The flat settings panel's popups go on their own surface in front of it. */
  private _attachFlatLayer(
    el: SVGSVGElement,
    drawn: BABYLON.Mesh,
    planeW: number,
    planeH: number
  ): void {
    attachSceneLayer({
      svg: el,
      owner: this as unknown as PopupOwner,
      openerMesh: drawn,
      planeW,
      planeH,
      // One viewBox unit per CSS pixel, so the screen is the canvas, offset
      // by where the panel is pinned (or dragged to).
      screenRoom: () => {
        const h = this.parts.canvas?.clientHeight ?? 0
        const top = Number(this._flatPanelSpot?.top) || 0
        return h > 0 ? { top: -top, bottom: h - top } : null
      },
    })
  }

  private _openScenePanel(): void {
    // One at a time: an exclusive system panel (the inventory) goes.
    this._closeSystemPanels(true)
    if (!this._flatDom() && !this.xrActive && this._openFlatScenePanel()) return
    const host = this.parts.scenePanelHost as HTMLElement
    const mk = (
      title: string,
      icon: Element | string,
      onClick: () => void,
      active = false,
      dim = false
    ): HTMLButtonElement => {
      const b = button(
        {
          class: active ? 'scene-panel-btn active' : 'scene-panel-btn',
          type: 'button',
          title,
          // Dimmed, not DISABLED: an empty error log is still worth opening to
          // confirm it is empty, and a button you cannot press cannot tell you
          // that.
          style: dim ? { opacity: '0.45' } : {},
        },
        icon
      ) as HTMLButtonElement
      b.addEventListener('click', (e) => {
        e.stopPropagation()
        onClick()
      })
      return b
    }
    const buttons = this._barItems().map((it) =>
      mk(
        it.title,
        (svgIcons as Record<string, () => Element>)[it.icon]?.() ??
          svgIcons.bug(),
        it.handleClick,
        it.active,
        it.dim
      )
    )
    buttons.push(
      mk(
        'Close',
        // In a session the flat overlay isn't visible anyway, but keep it
        // playful: a bug-eyed face for VR, the close icon on flat screens.
        this.xrActive ? '😳' : svgIcons.close(),
        () => this._closeScenePanel()
      )
    )
    /*
    KEEP THE POPUPS. They are SIBLINGS of the panel, not children of it — which
    is the whole point of a DOM layer, since a popup inside the panel's `<svg>`
    is cropped by its viewBox — so a plain `replaceChildren` deletes every open
    popup along with the panel it is rebuilding.

    That is what "clicking reset worst closed the panel" was: every action
    button repaints, every repaint rebuilt this host, and the popup the button
    was IN went with it. Re-appending the holders keeps everything about them
    that a rebuild has no business touching — where they were dragged to, how
    far they were scrolled, and the live rows already ticking inside them.
    */
    const fresh = this._makePanel(
      this._panelWidgets(),
      [],
      false,
      this._flatPanelMax()
    )
    carryScroll(host.querySelector(':scope > svg[data-w3d="panel"]'), fresh)
    replaceKeepingLayers(
      host,
      div(
        { class: 'scene-panel-head' },
        span({ class: 'scene-panel-title' }, (this as any).panelTitle ?? ''),
        ...buttons
      ),
      fresh
    )
    host.removeAttribute('hidden')
    this._panelHeightKey = this.clientHeight
  }

  /*
  THE FLAT PANEL FITS THE SPACE IT HAS (board #2466). It draws one viewBox
  unit per CSS pixel, and was capped at a fixed 620: on a short window or a
  phone a long panel (Land and Sky) ran off the bottom. Capped at the scene's
  height less the overlay's 12 px inset top and bottom, its own scrolling
  takes over. Re-measured when the scene resizes (see handleResize). The XR
  panel keeps 620: in a headset there is no window to fit.
  */
  private _panelHeightKey = 0
  private _panelResizeTimer: ReturnType<typeof setTimeout> | null = null
  private _flatPanelMax(): number {
    const h = this.clientHeight
    if (!(h > 0)) return 620
    return Math.max(160, Math.min(620, h - 24))
  }

  private _closeScenePanel(): void {
    this._disposeFlatScenePanel()
    ;(this.parts.scenePanelHost as HTMLElement).setAttribute('hidden', '')
    // Debug tools collapse again on next open — kept out of the way by default.
    this._debugOpen.clear()
    // …and their popups go with the panel that opened them, live rows included.
    for (const [id, h] of [...this._debugPopups]) {
      h.close()
      this._retirePopup(id)
    }
  }

  /** Rebuild the flat scene panel from the current rows, if it's open.
   * Call after async state the panel reflects has changed (e.g. a library loaded,
   * or XR availability / session state) so an already-open panel updates. */
  refreshScenePanel(): void {
    /*
    BOTH PRESENTATIONS. In a headset the settings panel is the XR one, and
    this only rebuilt the flat panel, so a tool that refreshes after a change
    (the rig tool's animate toggle following its view) drew the old state in
    VR (Tonio: the toggle stayed on while the clip had stopped).
    */
    this._refreshXrPanel()
    if (!this._scenePanelOpen()) return
    this._whenReleased(this._livePanel(), () => {
      if (this._scenePanelOpen()) this._openScenePanel()
    })
  }
  /** The settings panel element showing now, in whichever presentation. */
  private _livePanel(): Element | null {
    if (this._flatScenePanel != null) return this._flatScenePanel.el
    const host = this.parts?.scenePanelHost as HTMLElement | undefined
    return host?.querySelector(':scope > svg[data-w3d="panel"]') ?? null
  }
  /*
  NOT MID-PRESS. A rebuild that lands while a slider is being dragged (a
  debug line arriving, a library finishing its load) replaced the slider
  under the finger and ended the drag. Held until the release, then run once:
  later requests while it waits are the same rebuild.
  */
  private _held = new WeakMap<Element, () => void>()
  private _whenReleased(el: Element | null, run: () => void): void {
    const pressing = () => !!(el as { pressing?: boolean } | null)?.pressing
    const scene = this.scene
    if (el == null || scene == null || !pressing()) return run()
    const waiting = this._held.has(el)
    this._held.set(el, run)
    if (waiting) return
    const obs = scene.onBeforeRenderObservable.add(() => {
      if (pressing()) return
      scene.onBeforeRenderObservable.remove(obs)
      const latest = this._held.get(el)
      this._held.delete(el)
      latest?.()
    })
  }

  // Mount the split touch "glass" gamepad when the `gamepad` attribute is
  // present, as a light-DOM child (projected over the canvas via the slot, and
  // findable by b3dInputFocus, which adds its poll() to the input provider).
  // The attribute value selects/positions controls.
  private _setupGamepad(): void {
    const attr = this.getAttribute('gamepad')
    const prop = (this as any).gamepad
    if (attr == null && (prop === false || prop == null)) return
    const spec = typeof prop === 'string' && prop !== '' ? prop : attr ?? ''
    this.appendChild(
      b3dGamepad({
        controls: spec,
        scale: (this as any).gamepadScale ?? 1,
        // Reachable opt-out: the pad fades once a real input device appears,
        // and a scene that WANTS it permanent (kiosk, screenshot, a demo about
        // the pad) had no way to say so.
        fade: (this as any).gamepadFade ?? 'on',
      })
    )
    // A scene that starts paused got there before the pad existed.
    if (this._paused) this._suppressGamepad('pause', true)
  }

  // In-scene surface: render the panel onto a plane positioned each frame in
  // WORLD space relative to the HEAD (not the rig / flat camera), floating
  // overhead in the direction you face and fading in only as you tilt your head
  // up — so it never obstructs the forward view. Picks route via the scene
  // pointer observable (mouse and XR controllers alike) into the panel's own
  // viewBox coords. Returns a disposer.
  /*
  THE SYSTEM BAR IN A HEADSET, where the settings panel used to hang for the
  whole session (#3307): the same `systemBar` as the flat corner one. The
  owl opens and closes the settings panel just under it; then a button per
  system panel (a panel with an `icon`, the inventory), which opens under it
  too; then Exit VR. Enter VR and full screen mean nothing in a session.

  TWICE the flat bar's size: at this distance the flat one's proportions
  came out "teeny" (Tonio, in a headset).
  */
  private _xrToolbar(
    scene: BABYLON.Scene,
    cam: BABYLON.TargetCamera,
    frames: XrFrames,
    act: { settings: () => void; exit: () => void }
  ): { update: () => void; dispose: () => void } {
    const mk = (icon: Element, title: string, click: () => void) => {
      const b = button({ type: 'button', title }, icon) as HTMLButtonElement
      b.addEventListener('click', () => {
        sendDebug('xr-bar', { click: title })
        click()
      })
      return b
    }
    // The owl, as on the flat bar: the gear button's own icon.
    const gear = this.parts.scenePanelGear as HTMLElement | undefined
    const owl =
      (gear?.querySelector('svg')?.cloneNode(true) as Element | undefined) ??
      svgIcons.settings()
    /*
    VIEW AND MENU, IF THE SCENE ASKED FOR THEM. In a headset the stick clicks
    are stick presses, so the pad's view and menu buttons have no controller
    button; the bar carries them, but only when the glass pad's controls name
    them outright (`gamepad="…,view,menu"`), so a scene that never uses them
    does not get two dead buttons.
    */
    const pads = this._gamepads()
    const padButtons = (
      [
        ['view', svgIcons.copy(), 'View'],
        ['menu', svgIcons.menu(), 'Menu'],
      ] as const
    )
      .filter(([part]) => pads.some((p) => p.requested?.(part)))
      .map(([part, icon, title]) =>
        mk(icon, title, () => pads.forEach((p) => p.pulse?.(part)))
      )
    const buttons = [
      mk(owl, 'Settings', act.settings),
      ...this._systemButtons(),
      ...padButtons,
      mk(svgIcons.logOut(), 'Exit VR', act.exit),
    ]
    const bar = systemBar(buttons)
    this._xrBarSvg = bar.svg
    /*
    WHAT A PRESS ON THE BAR DID, to the debug sink: in the aircraft a headset
    lit a button and the press never landed, while the emulated headset's
    presses clicked. Presses and releases are rare, so this is cheap.
    */
    const barSvg = bar.svg as unknown as {
      handlePointer: (k: string, x: number, y: number) => void
    }
    const handle = barSvg.handlePointer
    barSvg.handlePointer = (k, x, y) => {
      if (k === 'down' || k === 'up')
        sendDebug('xr-bar', { kind: k, x: Math.round(x), y: Math.round(y) })
      handle(k, x, y)
    }
    const vb = bar.svg.viewBox.baseVal
    const width = 0.0022 * (vb.width || 100)
    const panel = attachFramePanel(scene, cam, frames.eye, {
      frame: 'eye',
      anchor: {
        azimuthDeg: 0,
        elevationDeg: XR_BAR_ELEVATION,
        distance: XR_BAR_DISTANCE,
      },
      reveal: 'always',
      layer: 'ui',
      svg: bar.svg,
      width,
      resolution: 512,
    })
    return {
      update: () => panel.update(),
      dispose: () => {
        if (this._xrBarSvg === bar.svg) this._xrBarSvg = null
        panel.dispose()
        bar.dispose()
      },
    }
  }
  /** The VR toolbar's SVG while a session has one (for tests and tools). */
  private _xrBarSvg: SVGSVGElement | null = null

  private _attachXrPanel(
    base: BABYLON.WebXRExperienceHelper,
    anchorFrame: BABYLON.TransformNode
  ): {
    dispose: () => void
    plane: BABYLON.AbstractMesh
    scrollBy: (dy: number) => void
    scrollable: boolean
    /** Rebuild the rows into the same plane (see `_refreshXrPanel`). */
    rebuild: () => void
  } {
    const scene = this.scene
    // In-scene panel always carries an Exit-VR button (you can't reach a DOM
    // button inside a headset), plus any scenePanel widgets.
    // Exit VR and Re-seat ride in the ICON BAR rather than taking a full-width
    // row each. Two labelled buttons cost a third of a small panel for two
    // things you press once a session — and the panel's job in a headset is the
    // author's controls, not its own chrome. They are appended at the MOUNT
    // SITE (never branched into the shared widget list) because they are
    // genuinely XR-only: Re-seat is meaningless flat, and flat already has an
    // Exit VR in the toolbar lozenge.
    //
    // It is the PINNED header, not row 0: Exit VR scrolling out of reach is the
    // one failure this panel cannot recover from inside a headset.
    // Exit VR is on the system bar above it (Tonio: "doesn't need an exit
    // vr button").
    type PanelEl = SVGSVGElement & {
      handlePointer?: (kind: string, x: number, y: number) => void
      scrollBy?: (dy: number) => void
      scrollable?: boolean
      gripHit?: (x: number, y: number) => boolean
    }
    const build = (): PanelEl => {
      const barRow = iconBar3d({
        items: [
          {
            icon: 'compass',
            title: 'Re-seat (look forward first)',
            active: false,
            handleClick: () => this.recenterXr(),
          },
          ...this._barItems(),
        ],
      })
      const rows: Widget3d[] = [...this._panelWidgets(true)]
      // A ×, as the flat panel has: one UI (it closes to the system bar).
      return this._makePanel(rows, [barRow], true, 620, () =>
        this._closeXrSettings()
      ) as PanelEl
    }
    let panelEl = build()
    /*
    THE PANEL A POPUP SHOULD OPEN ON, while a session is running.

    `_livePanelEl` only ever looked at the FLAT overlay's host, which is not
    visible in a headset — so `_syncDebugPopups` found no panel, returned early,
    and opened nothing, while `_debugOpen` kept the tool's id and lit its icon.
    Tonio, from a Quest 3: "In VR the popup panels don't appear. There's a flash
    and nothing. The indicator suggests it thinks the popup is open." It did
    think so; there was simply nowhere for it to put one.

    (The flash was the repaint underneath: toggling the icon rebuilds this panel,
    which in XR means dispose and re-attach.)
    */
    this._xrPanelEl = panelEl as unknown as { popup?: PanelPopup }
    // LIVE numbers in the headset. The XR panel is built once at entry, so a debug
    // readout would otherwise freeze at whatever it said when you put the headset on —
    // useless for watching a worst-frame spike as you fly. The SvgTexture re-renders
    // ⚠️ DO NOT "refresh" this panel by swapping its children.
    //
    // `panel3d` hangs `handlePointer`, `scrollBy` and `scrollable` off the SVG element as
    // CLOSURES over the widget objects it built, and the element's viewBox is sized for
    // that row count. `replaceChildren()` therefore leaves the pointer router aiming at
    // detached widgets laid out for a different height — the ray still reports a hit on
    // the panel and a plausible uv, but it maps to the WRONG control (a few px off, or
    // wildly, depending on how far the layout drifted), and scrolling dies outright
    // because `scrollBy` still drives nodes that are no longer in the tree.
    //
    // That is exactly what happened: a 500ms child-swap made the XR panel untargetable,
    // and only in scenes with a registered debug source — which is why the terrain demo
    // broke and the otherwise-identical combat demo didn't.
    //
    // Structural changes REBUILD the panel (a new element into the same plane: see `_refreshXrPanel`,
    // set where the panel is created). Live NUMBERS don't need any of that — `_startLiveDebug`
    // rewrites the <text> nodes in place, which changes no structure and no closure.
    let vb = panelEl.viewBox.baseVal

    // Anchored to the eye frame (origin = your head), 60° up the sight-line —
    // and that 60° is also the CEILING a drag may reach, pinned equal to
    // `ORBIT_MAX_ELEVATION` by a test. The position is derived through
    // `panel-orbit` rather than written out here, so the seat you start at and
    // the seats you drag to cannot disagree.
    const PLANE_W = 1.0 // metres wide (height follows the panel's aspect)
    const D = 1.4 // distance (matches the other eye-frame panels)
    const ELEV = (60 * Math.PI) / 180

    // Height in metres, kept because the orbit needs to know how tall the panel
    // LOOKS from the anchor in order to seat it by an edge rather than a centre.
    // As built; a rebuild into a different height scales the plane (`sy`).
    const planeH = PLANE_W * (vb.height / vb.width)
    let sy = 1
    const plane = BABYLON.MeshBuilder.CreatePlane(
      'xr-panel',
      {
        width: PLANE_W,
        height: planeH,
        sideOrientation: BABYLON.Mesh.DOUBLESIDE,
      },
      scene
    )
    // Pointer-pickable, collision-invisible — this panel hangs in front of your
    // face, which in cockpit view is inside the aircraft's impact sweep.
    markUiMesh(plane)

    // The panel is near-static, so re-rasterising the SVG at high res every
    // 30ms (the SvgTexture default) was the main XR perf regression — throttle
    // hard and drop the resolution. A settings panel doesn't need 33fps.
    const tex = new SvgTexture({
      scene,
      element: panelEl,
      resolution: 512,
      updateInterval: 200,
    })
    const mat = new BABYLON.StandardMaterial('xr-panel-mat', scene)
    mat.backFaceCulling = false
    mat.emissiveTexture = tex.texture
    mat.opacityTexture = tex.texture
    mat.diffuseColor = BABYLON.Color3.Black()
    mat.disableLighting = true
    plane.material = mat
    plane.visibility = 0
    // The XR rig is the camera's parent. Make the panel a SECOND child of that
    // rig — the SAME coordinate space as the camera — at a FIXED local pose: the
    // head (camera) rotates within the rig to look at it, while the panel itself
    // never moves or rotates. So it's rock-steady relative to you and stays put
    // to be pointed at. No billboard (that would re-rotate it every frame). (#1)
    plane.parent = anchorFrame
    plane.billboardMode = BABYLON.Mesh.BILLBOARDMODE_NONE

    /*
    POPUPS ARE POPUPS, not things cropped by the panel that opened them.

    Without a layer host this panel is not in the DOM (it is rasterised to a
    texture), so `showLayer` finds nowhere to mount and degrades to a popup
    bounded by the panel's own viewBox. In a headset that came out as a `spin`
    menu squeezed into the room left below its row — Tonio: "the pop up for
    speed is tiny… and it's clipped to the panel. We need pop ups to be pop ups
    and not constrained by the thing that pops them."

    `panelScene` has had this since the keyboard needed it; the settings panel
    never did, so every popup opened from it in VR took the cropped path. It is
    the same call now rather than a second copy of the placement maths.
    */
    attachSceneLayer({
      svg: panelEl,
      owner: this as unknown as PopupOwner,
      openerMesh: plane,
      planeW: PLANE_W,
      planeH,
    })

    /** Put the panel at a seat — the one place that writes its pose. */
    const seatPanel = (seat: Orbit): void => {
      const centre = orbitCentre(seat, angularHeight(planeH * sy, D))
      const p = orbitPosition(centre)
      plane.position.set(p.x, p.y, p.z)
      const aim = faceViewer(p, { x: 0, y: 0, z: 0 })
      plane.rotationQuaternion = BABYLON.Quaternion.RotationYawPitchRoll(
        aim.yaw,
        aim.pitch,
        0
      )
    }

    /** The seat the drag is currently asking for, BEFORE band or clamp. */
    /*
    THE SEAT SURVIVES A REBUILD, so pressing Pause does not move the panel.

    A gadget press rebuilds the panel (dispose + re-attach — structural changes
    have to, see `_refreshXrPanel`), and this used to be re-initialised to the
    default each time. So the panel you had just dragged somewhere comfortable
    jumped back to 60° up the moment you used it. Tonio: "If I hit the pause in
    the in-vr panel (after dragging it to a new location) it snaps back to
    default position (pause works, but I didn't want the panel to move away)."

    It lives on the ELEMENT, not in this closure, because the closure is exactly
    what a rebuild throws away.
    */
    this._xrPanelSeat ??= {
      azimuthDeg: 0,
      elevationDeg: (ELEV / Math.PI) * 180,
      radius: D,
    }
    let wanted: Orbit = { ...this._xrPanelSeat }

    const orbitTo = (ray: BABYLON.Ray): void => {
      const inv = new BABYLON.Matrix()
      anchorFrame.getWorldMatrix().invertToRef(inv)
      /*
      AIM AT A POINT ON THE RAY, not along its DIRECTION.

      The direction is the controller's, and the sphere is centred on your HEAD.
      Applying one to the other treats the controller as if it were at your eyes
      — so the panel sat where the maths said rather than where you pointed, and
      every wobble of your hand swung it by the whole offset between the two.
      Tonio: "jittery while dragging and doesn't seem to track the cursor
      properly (the cursor points towards it not away)."

      Taking a point `D` along the ray and asking which way THAT lies from the
      anchor is the actual question — "where on my sphere am I pointing" — and
      it is stable, because head and hand move together rather than differencing.

      `TransformCoordinates`, not `TransformNormal`: a point carries the frame's
      translation and a direction does not, and using the wrong one here is
      precisely the error being fixed.
      */
      // The ORIGIN as a point and the DIRECTION as a vector — a point carries
      // the frame's translation and a vector does not, and using one transform
      // for both is the same class of error `orbitFromAim` exists to prevent.
      const o = BABYLON.Vector3.TransformCoordinates(ray.origin, inv)
      const d = BABYLON.Vector3.TransformNormal(ray.direction, inv)
      wanted = orbitFromAim(
        { x: o.x, y: o.y, z: o.z },
        { x: d.x, y: d.y, z: d.z },
        // The RADIUS is the sphere's, not the centre's — `orbitCentre` shifts
        // the centre off the seat's stated elevation, so reading it back would
        // shrink the sphere a little on every frame of a drag.
        { ...wanted, radius: D }
      )
      /*
      `wanted` STAYS RAW — not clamped here.

      The band is what shows a limit (`bandOrbit` below) and the clamp is what
      the release springs back to. Clamping at this point would flatten the raw
      overshoot the band is computed from, so the panel would simply stop at the
      limit again and the rubber band would never engage.
      */
      this._xrPanelSeat = { ...wanted }
      // BANDED while the hand is down: past a limit the panel keeps following
      // you, stiffening, so a limit reads as a limit rather than as a dropped
      // drag. `releaseOrbit` is the other half.
      seatPanel(bandOrbit(wanted))
    }

    /*
    SPRING BACK on release, to the CLAMPED seat.

    This is what makes the stretch a band rather than a bigger box — the panel
    may pass a limit but must never come to rest past one, because out there its
    own Exit VR and Re-seat buttons are hard to reach.

    Eased over a fixed short time rather than a spring with a stiffness: a
    critically-damped spring is nicer to tune and worse to be inside, since an
    overshoot on the way back is another moment where the panel is somewhere you
    did not put it. This just goes there, quickly, and stops.
    */
    let springOff: (() => void) | null = null
    const releaseOrbit = (): void => {
      springOff?.()
      const from = { ...bandOrbit(wanted) }
      const to = clampOrbit(wanted)
      wanted = to
      this._xrPanelSeat = { ...to }
      if (
        Math.abs(from.azimuthDeg - to.azimuthDeg) < 0.01 &&
        Math.abs(from.elevationDeg - to.elevationDeg) < 0.01
      ) {
        seatPanel(to)
        return
      }
      const SPRING_MS = 180
      let t = 0
      const obs = scene.onBeforeRenderObservable.add(() => {
        t += scene.getEngine().getDeltaTime()
        const k = Math.min(1, t / SPRING_MS)
        // Ease out: fastest at the start, so it reads as the band letting go.
        const e = 1 - (1 - k) * (1 - k)
        seatPanel({
          azimuthDeg: from.azimuthDeg + (to.azimuthDeg - from.azimuthDeg) * e,
          elevationDeg:
            from.elevationDeg + (to.elevationDeg - from.elevationDeg) * e,
          radius: to.radius,
        })
        if (k >= 1) springOff?.()
      })
      springOff = () => {
        scene.onBeforeRenderObservable.remove(obs)
        springOff = null
      }
    }

    let placed = false
    const frame = base.sessionManager.onXRFrameObservable.add(() => {
      if (placed) return
      placed = true
      // Seat ONCE 60° up in the eye frame (origin = head), facing back down at
      // it. `faceViewer` aims the plane's VISIBLE face (local -Z) at your head;
      // this used to aim +Z and cancel the resulting mirror twice over — once on
      // the texture and once on every pick. See dialog-placement.faceViewer.
      // Through the ORBIT, so the seat you start at and the seats you drag to
      // are computed the same way — otherwise the first drag jumps by however
      // much the two disagreed. `ELEV` is the ceiling, and `panel-orbit` pins
      // that equal to `ORBIT_MAX_ELEVATION`.
      seatPanel(wanted)
      plane.visibility = 1
    })

    const T = BABYLON.PointerEventTypes
    let vx = 0
    let vy = 0
    let dragging = false

    /*
    SEAT THE PANEL WHERE YOU ARE POINTING, on its own sphere.

    The ray is world-space and the panel lives in the anchor's space, so the
    direction is taken into local space first — otherwise the panel orbits the
    world origin, which is only the same place by accident.

    A SEAT names the panel's far edge, not its centre (`orbitCentre`): pinning
    the edge furthest from the equator means a tall panel's extra height always
    lands somewhere more comfortable rather than trailing into the floor or
    pushing past the ceiling it was clamped to.
    */
    // XR pointer diagnostics, shown ON THE PANEL — the only way to debug a panel you
    // can't press. (The panel renders fine and its lines are live, so you can READ it in
    // the headset even when picking is broken.) Off unless something registered.
    const dbg = {
      kind: '-',
      events: 0,
      hit: '-',
      repick: '-',
      vis: 0,
      uv: '-',
    }
    const offDbg = this.addDebugSource({
      name: 'xr pointer',
      lines: () => [
        `events ${dbg.events}  last ${dbg.kind}  vis ${dbg.vis.toFixed(2)}`,
        `pick ${dbg.hit}`,
        `repick ${dbg.repick}  uv ${dbg.uv}`,
      ],
    })
    const obs = scene.onPointerObservable.add((pi) => {
      const kind =
        pi.type === T.POINTERDOWN
          ? 'down'
          : pi.type === T.POINTERUP
          ? 'up'
          : pi.type === T.POINTERMOVE
          ? 'move'
          : ''
      if (kind) {
        dbg.events++
        dbg.kind = kind
        dbg.vis = plane.visibility
        dbg.hit = pi.pickInfo?.pickedMesh
          ? `${pi.pickInfo.pickedMesh.name}${
              pi.pickInfo.pickedMesh === plane ? ' (PANEL)' : ''
            }`
          : pi.pickInfo?.ray
          ? 'nothing (ray ok)'
          : 'NO RAY'
      }
      // Only interactive while it's actually visible (you're looking at it).
      if (
        !kind ||
        plane.visibility < 0.5 ||
        typeof panelEl.handlePointer !== 'function'
      )
        return
      const pick = pi.pickInfo
      let uv =
        pick?.hit && pick.pickedMesh === plane
          ? pick.getTextureCoordinates()
          : null
      /*
      The panel may be occluded (e.g. the aircraft cockpit hull blocks the
      controller ray) — re-pick against ONLY the panel so it is still pointable.

      ⚠️ BUT NOT PAST ANOTHER PANEL. The re-pick ignored what the ray actually
      hit, so once this panel opened a popup on its own plane — in FRONT of it,
      which is the whole point of a layer — every press aimed at that popup was
      re-picked onto the panel behind and routed there. The popup's own close
      and move glyphs never saw a thing, and it could not be dismissed. That is
      a regression I introduced by routing menus through the scene layer, and
      the guard is: an occluder that is itself UI wins, because it is something
      the person is deliberately pointing at.
      */
      const blockedByUi =
        pick?.hit === true &&
        pick.pickedMesh != null &&
        pick.pickedMesh !== plane &&
        isNoCollide(pick.pickedMesh)
      if (!uv && !blockedByUi && pick?.ray) {
        // A full-length copy: the XR pointer's ray is cut to the distance of
        // the occluder it hit, so re-casting it can never reach a panel behind.
        const p2 = scene.pickWithRay(
          new BABYLON.Ray(pick.ray.origin, pick.ray.direction, 1e4),
          (m) => m === plane
        )
        if (p2?.hit) uv = p2.getTextureCoordinates()
        if (kind) dbg.repick = p2?.hit ? 'HIT' : 'miss'
      } else if (kind) {
        dbg.repick = uv ? 'n/a (direct)' : 'no ray'
      }
      if (kind) dbg.uv = uv ? `${uv.x.toFixed(2)},${uv.y.toFixed(2)}` : 'none'
      if (uv) {
        // Straight through, matching `b3d-svg-plane`: u across, v flipped
        // (texture space is bottom-up). This used to need `1 - uv.x` to undo a
        // U-flipped texture on a back-facing plane — two compensations for one
        // avoidable cause, and if either had gone missing every right-aligned
        // control (slider track, toggle switch, select arrows) would have mapped
        // to the dead label zone and felt unresponsive.
        vx = uv.x * vb.width
        vy = (1 - uv.y) * vb.height
      }
      /*
      THE GRIP GOES TO THE ORBIT, not to the panel.

      A press in the top strip drags the panel around its rig anchor rather than
      pressing anything — see `panel-orbit`. It is intercepted HERE rather than
      inside `panel3d` because the gesture needs the controller RAY, and the
      panel only ever sees viewBox coordinates.

      The panel is told 'leave' on the way into a drag, so a row that was hovered
      when you grabbed does not stay lit for the whole drag.
      */
      if (kind === 'down' && uv && panelEl.gripHit?.(vx, vy)) {
        dragging = true
        panelEl.handlePointer('leave', 0, 0)
        return
      }
      if (dragging) {
        if (kind === 'up') {
          dragging = false
          releaseOrbit()
          return
        }
        if (kind === 'move') {
          const ray = pi.pickInfo?.ray
          if (ray != null) orbitTo(ray)
          return
        }
      }
      // Route every event; the panel manages press-capture and hover itself.
      if (kind === 'move' && !uv) panelEl.handlePointer('leave', 0, 0)
      else if (kind === 'down' && !uv) return
      else panelEl.handlePointer(kind, vx, vy)
    })

    return {
      plane,
      get scrollable() {
        return !!panelEl.scrollable
      },
      scrollBy: (dy: number) => panelEl.scrollBy?.(dy),
      /*
      INTO THE SAME PLANE, so it does not blink. A new plane is blank until
      its texture is drawn, which at headset speed is two or three frames of
      scene showing through the panel on every icon press. The texture keeps
      the old picture until the new one is ready; the seat stays.
      */
      rebuild: () => {
        panelEl.handlePointer?.('leave', 0, 0)
        const was = panelEl
        panelEl = build()
        carryScroll(was, panelEl)
        vb = panelEl.viewBox.baseVal
        this._xrPanelEl = panelEl as unknown as { popup?: PanelPopup }
        sy = (PLANE_W * (vb.height / vb.width)) / planeH
        plane.scaling.y = sy
        tex.setElement(panelEl)
        attachSceneLayer({
          svg: panelEl,
          owner: this as unknown as PopupOwner,
          openerMesh: plane,
          planeW: PLANE_W,
          planeH: planeH * sy,
        })
        seatPanel(this._xrPanelSeat ?? wanted)
      },
      dispose: () => {
        // A spring mid-flight would keep writing to a disposed plane, once per
        // frame, forever — the leak class this file already carries scars about.
        springOff?.()
        base.sessionManager.onXRFrameObservable.remove(frame)
        scene.onPointerObservable.remove(obs)
        offDbg()
        this._liveDebug.xr = [] // its <text> nodes die with the panel
        tex.dispose()
        mat.dispose()
        plane.dispose()
      },
    }
  }

  disconnectedCallback(): void {
    /*
    DEFER, so a move can cancel it. See `connectedCallback`.

    `super.disconnectedCallback()` still runs immediately — tosijs owns its own
    bookkeeping and a move is normal to it; only OUR teardown (engine, timers,
    XR, the ready flag) waits to find out whether this was a removal or a move.
    */
    super.disconnectedCallback()
    if (this._teardownTimer != null) return
    this._teardownTimer = setTimeout(() => {
      this._teardownTimer = null
      this._teardown()
    }, 0) as unknown as number
  }

  private _teardownTimer: number | null = null
  /** The canvas's context was lost by teardown — see `connectedCallback`. */
  private _canvasSpent = false

  private _teardown(): void {
    // Both presentations' widgets, not just the visible one — an XR panel's
    // rows outlive the session that built them otherwise.
    this._disposeWidgets('flat')
    this._disposeWidgets('xr')
    this._errorCaptureOff?.()
    this._errorCaptureOff = null
    window.removeEventListener('pointerup', this._freeTouchSlot, true)
    window.removeEventListener('pointercancel', this._freeTouchSlot, true)
    this._pauseWatch?.()
    this._pauseWatch = null
    if (B3d._active === this) B3d._active = null
    if (this._qualityOff) {
      this._qualityOff()
      this._qualityOff = null
    }
    this._fullscreenOff?.()
    this._fullscreenOff = null
    this._closeSystemPanels()
    this._sceneBar?.dispose()
    this._sceneBar = null
    // The buttons outlive the scene (they are the shadow root's), so a
    // re-added element would find them "the same" and keep no bar at all.
    this._sceneBarButtons = []
    this._disposeFlatScenePanel()
    this._fullscreenWired = false
    if (this.xrHelper) {
      this._unpickLayers?.()
      this._unpickLayers = null
      this.xrHelper.dispose()
      this.xrHelper = undefined
    }
    // Kill the live-debug timer explicitly. Its self-clear only fires when BOTH row buckets are
    // empty, but `_liveDebug.flat` never empties on its own — so without this a removed scene
    // leaves a 400ms interval calling each debug source's `lines()` closure forever, pinning the
    // whole (disposed) scene from GC. A multi-demo docs page would leak one per visit. See the
    // pre-release review; this is exactly the leak class this project guards against.
    if (this._liveDebugTimer != null) {
      clearInterval(this._liveDebugTimer)
      this._liveDebugTimer = null
    }
    this._liveDebug = { flat: [], xr: [] }
    this._debugSources = []
    this._disposeFlatPanels()
    this._unwatchPixelRatio()
    // Descendant B3dChild components self-dispose via their own
    // disconnectedCallback when this subtree is removed — b3d doesn't dispose them.
    this._sceneReady = false

    /*
    SUBSCRIPTIONS ARE DURABLE; SCENE STATE IS NOT. That is the whole rule.

    `_readyQueue` is deliberately NOT cleared: a callback waiting on a scene
    that got torn down before it was ready is still waiting, and dropping it
    silently is the exact bug shape this file keeps producing. It fires against
    the next scene, or never (garbage with the element), and either way nobody
    is left holding a callback that will not run.

    `pastAdditions` IS cleared, because it is scene state — meshes and lights
    belonging to the scene we just disposed. Replaying those to the next
    `addSceneListener` hands a new listener dead nodes from a dead scene.

    Handlers run BEFORE disposal so they can still read what they are releasing,
    and each is isolated: one consumer throwing must not strand the rest, nor
    abort the engine disposal below it.
    */
    for (const cb of [...this._disposeHandlers]) {
      try {
        cb()
      } catch (err) {
        console.warn('b3d whenDisposed handler failed', err)
      }
    }
    this.pastAdditions = []

    /*
    OBLITERATE THE ENGINE. A WebGL context is not memory — Chrome caps them at
    about sixteen per page and force-loses the OLDEST when you exceed it, so a
    leaked engine doesn't degrade an SPA gradually: it works for a dozen route
    changes and then a scene that was fine goes black. That is #51's symptom
    arriving by a second road, and until now this method released the XR helper,
    the quality subscription and the debug timers but never the engine or the
    scene it holds.

    Safe to be absolute here because this only runs on a GENUINE removal: a
    re-parent (an ancestor moving, which disconnects every descendant as
    collateral) cancels this in `connectedCallback` before it fires. So there is
    one rule — disconnected means gone — and no pooling, no reuse, no engine
    kept warm on the chance somebody comes back.

    Order matters: stop the loop first, or the next frame renders a scene that is
    being disposed underneath it.
    */
    this._ssao?.dispose()
    this._ssao = undefined
    this._projectedAo?.dispose()
    this._projectedAo = undefined
    this._perfMeter?.dispose()
    this._perfMeter = null
    this._ssaoCamObs = undefined
    this.glowLayer = undefined
    this.gui = undefined
    try {
      this.engine?.stopRenderLoop()
      this.scene?.dispose()
      this.engine?.dispose()
      if (this.engine != null) this._canvasSpent = true
    } catch (err) {
      // A half-built scene (disconnected mid-init) can throw on the way down.
      // Never let teardown propagate — the element is going away regardless,
      // and a throw here would strand whatever the caller was doing.
      console.warn('b3d teardown', err)
    }
  }

  /**
   * Bring the SSAO pipeline in line with the `ssao*` attributes, the device
   * tier, the XR state and the active camera. Cheap when nothing changed, so it
   * is called from every place one of those can change.
   */
  private _applySsao(): void {
    if (this.scene == null || this.scene.isDisposed) return
    const a = this as any
    const budgets = qualityBudgets({ xr: this.xrActive })
    if (a.ssao === 'always' && !B3d._warnedSsaoAlways) {
      B3d._warnedSsaoAlways = true
      console.warn('tosi-b3d: ssao="always" is deprecated and means "on".')
    }
    const projected = a.ssaoMethod !== 'screen'
    const active = ssaoActive(a.ssao, {
      xr: this._xrPresenting || this.xrActive,
      budgetAllows: budgets.ssao,
      xrCapable: projected,
    })
    this._ssaoRunning = active
    const params = {
      strength: Number(a.ssaoStrength) || 0,
      radius: Number(a.ssaoRadius) || 2,
      samples: budgets.ssaoSamples,
      ratio: budgets.ssaoRatio,
    }
    // One method at a time: switching tears the other down.
    if (active && projected) {
      this._projectedAo ??= new ProjectedAoController(this.scene)
      this._projectedAo.update({
        ...params,
        active: true,
        rate: Math.max(0, Number(a.ssaoRate) || 0),
      })
    } else {
      this._projectedAo?.dispose()
    }
    const screen = active && !projected
    if (!screen && this._ssao == null) return
    if (this._ssao == null) {
      this._ssao = new SsaoController(this.scene)
      // A pipeline is attached to a CAMERA, and scenes swap cameras (a vehicle's
      // follow camera, a cutscene, XR entry).
      this._ssaoCamObs = this.scene.onActiveCameraChanged.add(() =>
        this._applySsao()
      )
    }
    this._ssao.update({ ...params, active: screen })
  }

  render(): void {
    super.render()
    // A render can be queued on rAF and land AFTER teardown disposed the engine.
    // Building a GlowLayer on a disposed scene is how that would surface — as a
    // GL error from a component that looks fine, which is the failure mode this
    // whole area has been generating.
    if (this.scene == null || this.scene.isDisposed) return
    // `pixelRatio` is live: an author changing it re-scales the render.
    const pr = (this as any).pixelRatio
    if (pr !== this._appliedPixelRatio) {
      this._appliedPixelRatio = pr
      this._applyHardwareScaling(this.xrActive)
    }
    const intensity = (this as any).glowLayerIntensity
    if (intensity > 0) {
      if (!this.glowLayer) {
        this.glowLayer = new BABYLON.GlowLayer('glow', this.scene)
        // Bloom only as much as a mesh is visible. Babylon's glow ignores opacity, so a
        // fading full-screen curtain whited out the whole frame (#2742).
        opacityWeightedGlow(this.glowLayer)
        // UI plaques and leaves are emissive but not light sources: keep them out of
        // the bloom. Panels built AFTER this exclude themselves on creation; these are
        // the ones that got here first.
        for (const m of this.scene.meshes) {
          // Neither UI plaques nor leaves are light sources — keep them out of the bloom.
          if (m.name === 'frame-panel' || m.name === 'ambient-leaves')
            this.glowLayer.addExcludedMesh(m as BABYLON.Mesh)
        }
      }
      this.glowLayer.intensity = intensity
    } else if (this.glowLayer) {
      this.glowLayer.dispose()
      this.glowLayer = undefined
    }
    this._applySsao()
  }
}

export const b3d = B3d.elementCreator()

/**
 * Put a pinch-zoomed page back to 1: briefly cap the viewport's scale, which
 * Safari obeys by zooming out, then lift the cap so people can still zoom the
 * page outside the scene.
 */
function resetPageZoom(): void {
  const vv = (window as { visualViewport?: VisualViewport }).visualViewport
  if (vv == null || vv.scale <= 1.01) return
  const meta = document.querySelector(
    'meta[name="viewport"]'
  ) as HTMLMetaElement | null
  if (meta == null) return
  const was = meta.content
  meta.content = `${was.replace(
    /,?\s*maximum-scale=[^,]*/g,
    ''
  )}, maximum-scale=1`
  setTimeout(() => (meta.content = was), 400)
}
